import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabase';
import { patchProfileClientsColumn, patchProfileColumns } from '../supabaseProfileClientsRestWrite';
import { patchQuoteInvoicePaymentStatus, patchQuoteColumns } from '../supabaseQuotesRestWrite';
import {
  runReliableAsync,
  refreshSupabaseSessionOnce,
  runSupabaseAuthedOperation,
  toUserMessage,
  devReliabilityLog,
  withTimeout,
  fetchWithReliability,
  DEVELLO_API_ORIGIN,
  safeRemoveDetachedNode,
} from '../utils/asyncReliability';
import { useResumeLoadingReset } from '../hooks/useResumeLoadingReset';
import { useAppResumeAuth } from '../hooks/useAppResumeAuth';
import { useNavigate, useParams, useLocation, Link, Navigate } from 'react-router-dom';
import Scheduling from './Scheduling';
import Projects from './Projects';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import QuoteFormModal from './QuoteFormModal';
import EmailModal from '../components/EmailModal';
import TrialCountdown from '../components/TrialCountdown';
import OverviewSection from '../components/dashboard/OverviewSection';
import BillingPlanModal from '../components/billing/BillingPlanModal';
import QuotesSection from '../components/dashboard/QuotesSection';
import ClientsSection from '../components/dashboard/ClientsSection';
import TeamsSection from '../components/dashboard/TeamsSection';
import AnalyticsSection from '../components/dashboard/AnalyticsSection';
import {
  DashboardPageHeader,
  dashboardCardClass,
  dashboardCardHeaderClass,
  dashboardModalHeaderSurfaceClass,
  invoiceBuilderModalShell,
  invoiceBuilderLeftPanel,
  invoiceBuilderRightPanel,
  dashboardInputClass,
  dashboardBtnPrimary,
  dashboardBtnModalCancel,
  dashboardModalBackdrop,
  dashboardModalWrap,
  dashboardLabelClass,
  dashboardModalPanelMd,
  dashboardModalPanel,
  dashboardModalBody,
  dashboardModalFooter,
  dashboardModalCloseBtn,
  dashboardBtnRowEdit,
  dashboardBtnRowDelete,
} from '../components/dashboard/DashboardChrome';
import ProductTour from '../components/ProductTour';
import ThemeToggle from '../components/ThemeToggle';
import MaterialSearchShoppingModal from '../components/MaterialSearchShoppingModal';
import {
  effectivePlanTier,
  accessSource,
  canViewProjects,
  canMutateProjects,
  isDowngradedReadOnlyProfile,
} from '../utils/entitlements';
import { getPlanDetails, PRO_MONTHLY_USD } from '../config/plans';
import { calculateQuoteTotals } from '../utils/quoteTotals';
import { fetchBillingSummary, syncCheckoutSession } from '../services/billingClient';
import { normalizeQuoteFromDb } from '../utils/quoteShape';
import { getDisplayInvoiceAmount, getInvoiceDueCountdownBadgeInfo } from '../utils/invoiceCardUtils';
import { clearSignupPlanIntent } from '../utils/signupPlanIntent';
import { devClickDebug } from '../devClickDebug';
import { devLog, devWarn, devError } from '../utils/logger';
import {
  COUNTRY_OPTIONS,
  US_STATE_OPTIONS,
  CA_PROVINCE_OPTIONS,
  inferCountryFromProvince,
} from '../utils/addressRegions';

/** When true, skips automatic `fetchBillingSummary` (Stripe return still uses `loadPaymentStatus({ force: true })`). */
const DISABLE_AUTO_BILLING_SUMMARY_FETCH = false;

/** Matches `public.quotes` columns — strips extras so insert/update does not fail on unknown keys. */
const QUOTE_DB_COLUMNS = new Set([
  'user_id',
  'client_email',
  'client_id',
  'quote_number',
  'status',
  'total',
  'agreed_price',
  'agreed_price_set_at',
  'tax_rate',
  'markup',
  'markup_type',
  'overhead',
  'quote_sent',
  'invoice_sent',
  'materials',
  'notes',
  'custom_quote_data',
  'labour',
  'pdf_data',
  'invoice_data',
  'client_feedback',
  'status_history',
  'pdf_url',
  'pdf_generated_at',
  'pdf_reference',
  'invoice_pdf_url',
  'invoice_pdf_generated_at',
  'invoice_generated_at',
  'invoice_amount',
  'edited_on',
  'created_at',
  'updated_at',
]);

function pickQuoteDbColumns(row) {
  const o = {};
  for (const [k, v] of Object.entries(row)) {
    if (QUOTE_DB_COLUMNS.has(k) && v !== undefined) o[k] = v;
  }
  return o;
}

/** Empty strings and bad strings break `numeric` columns — Postgres rejects the whole row. */
const QUOTE_NUMERIC_COLUMNS = ['total', 'agreed_price', 'tax_rate', 'markup', 'overhead', 'invoice_amount'];
const QUOTE_SAVE_HARD_RESET_MS = 90000;

function normalizeQuoteNumericFields(row) {
  const o = { ...row };
  for (const key of QUOTE_NUMERIC_COLUMNS) {
    const v = o[key];
    if (v === '' || v === null || v === undefined) {
      delete o[key];
      continue;
    }
    if (typeof v === 'string') {
      const n = parseFloat(v);
      if (Number.isNaN(n)) delete o[key];
      else o[key] = n;
    }
  }
  return o;
}

// ── Inline email-change widget (contractor settings) ─────────
function EmailChangeField({ currentEmail }) {
  const [email, setEmail]     = useState(currentEmail || '');
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');
  const [isErr, setIsErr]     = useState(false);

  const handleSave = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) { setIsErr(true); setMsg('Enter a valid email.'); return; }
    if (trimmed === (currentEmail || '').toLowerCase()) { setMsg('That is already your email.'); setIsErr(false); return; }
    setSaving(true); setMsg(''); setIsErr(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const resp = await fetch('/api/change-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Failed to update email.');
      setMsg('Email updated successfully.');
      setIsErr(false);
    } catch (e) { setMsg(e.message); setIsErr(true); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          placeholder="your@email.com"
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="shrink-0 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Update'}
        </button>
      </div>
      {msg && <p className={`text-xs ${isErr ? 'text-red-600' : 'text-emerald-700'}`}>{msg}</p>}
    </div>
  );
}

const Dashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [planBillingModalOpen, setPlanBillingModalOpen] = useState(false);
  const [billingModalHighlightCheckout, setBillingModalHighlightCheckout] = useState(false);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { section } = useParams();

  useAppResumeAuth(supabase);

  const closePlanBillingModal = useCallback(() => {
    setPlanBillingModalOpen(false);
    setBillingModalHighlightCheckout(false);
  }, []);

  const openPlanBillingModal = useCallback((highlightCheckout = false) => {
    setBillingModalHighlightCheckout(!!highlightCheckout);
    setPlanBillingModalOpen(true);
  }, []);

  useEffect(() => {
    if (section === 'billing') {
      navigate({ pathname: '/dashboard/settings', search: routerLocation.search || '' }, { replace: true });
      return;
    }
    const q = new URLSearchParams(routerLocation.search);
    if (q.get('plans') === '1' && section !== 'settings') {
      navigate({ pathname: '/dashboard/settings', search: routerLocation.search || '' }, { replace: true });
      return;
    }
    let strip = false;
    if (q.get('plans') === '1') {
      setPlanBillingModalOpen(true);
      strip = true;
    }
    if (q.get('welcome') === 'pro') {
      setBillingModalHighlightCheckout(true);
      setPlanBillingModalOpen(true);
      strip = true;
    }
    if (strip) {
      q.delete('plans');
      q.delete('welcome');
      const next = q.toString();
      navigate({ pathname: routerLocation.pathname, search: next ? `?${next}` : '' }, { replace: true });
    }
  }, [section, routerLocation.search, routerLocation.pathname, navigate]);
  

  useResumeLoadingReset(() => {
    devClickDebug('useResumeLoadingReset: clearing stuck loading flags', {});
    setMaterialSearchLoading(false);
    setSaving(false);
    clearQuoteSaveLoadingState();
    setQuoteRowMutationBusyId(null);
  });

  const runAuthedWrite = useCallback(
    async (name, fn, timeoutMs = 50000) => {
      devClickDebug('runAuthedWrite:handler start', { name, timeoutMs });
      try {
        const result = await runSupabaseAuthedOperation({
          name,
          supabaseClient: supabase,
          timeoutMs,
          retries: 1,
          fn,
        });
        devClickDebug('runAuthedWrite:after runSupabaseAuthedOperation', { name, ok: result.ok });
        if (!result.ok) {
          devClickDebug('runAuthedWrite:validation/result !ok — throwing', {
            name,
            userMessage: result.userMessage,
          });
          throw result.error || new Error(result.userMessage || 'Could not save changes.');
        }
        devClickDebug('runAuthedWrite:success', { name });
        return result.data;
      } catch (err) {
        devClickDebug('runAuthedWrite:catch', { name, err: String(err) });
        throw err;
      } finally {
        devClickDebug('runAuthedWrite:finally', { name });
      }
    },
    []
  );

  useEffect(() => {
    if (section === 'subcontractors') {
      navigate('/dashboard/teams?tab=subcontractors', { replace: true });
    }
  }, [section, navigate]);
  const settingsLogoInputRef = useRef(null);
  /** Supabase Realtime channel — remove before resubscribe (auth fires more than once). */
  const quotesChannelRef = useRef(null);
  /** Realtime channel for watching project changes written by team members. */
  const projectsChannelRef = useRef(null);

  const refreshProfilePlan = useCallback(async () => {
    if (!supabase) return;
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth?.user?.id;
    if (!uid) return;
    const { data: planData } = await supabase.from('profiles').select('*').eq('id', uid).single();
    if (!planData) return;
    setUserData({ ...planData, email: auth.user.email || planData.email });
    setUserPlan(effectivePlanTier(planData));
    setIsTrial(!!(planData.is_trial ?? planData.isTrial));
    setTrialEndDate(
      planData.trial_end_date || planData.trialEndDate
        ? new Date(planData.trial_end_date || planData.trialEndDate)
        : null
    );
  }, []);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('devello-sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('devello-sidebar-collapsed', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  // Settings state
  const [settings, setSettings] = useState({
    name: '',
    emailNotifications: true,
    companyName: '',
    companyLogo: null,
    streetAddress: '',
    country: 'CA',
    city: '',
    province: '',
    postalCode: '',
    address: '',
    websiteUrl: '',
    businessHours: '',
    companyEmail: '',
    companyPhone: '',
    hstNumber: '',
    logoSize: 60 // default size in px
  });

  // Client management state
  const [clients, setClients] = useState([]);
  const [selectedClientForQuotes, setSelectedClientForQuotes] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showClientQuotes, setShowClientQuotes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientForm, setClientForm] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    province: '',
    postalCode: '',
    status: 'active'
  });

  // Quote management state
  const [showCreateQuote, setShowCreateQuote] = useState(false);
  const [showReviewQuote, setShowReviewQuote] = useState(false);
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showEditQuote, setShowEditQuote] = useState(false);
  const [quoteSearchTerm, setQuoteSearchTerm] = useState('');
  const [quoteStatusFilter, setQuoteStatusFilter] = useState('all');
  const [quoteForm, setQuoteForm] = useState({
    clientId: '',
    materials: [],
    markupType: 'percentage',
    overheadType: 'amount',
    taxEnabled: false,
    finalTaxEnabled: false,
    taxRate: '',
    labour: {
      entries: [],
      total: 0
    },
    description: '',
    editedOn: new Date().toISOString(),
    quoteNumber: ''
  });

  /** Prevents double-submit and stranded "saving" UX on quote write flows */
  const saveQuoteInFlightRef = useRef(false);
  const saveQuoteWatchdogRef = useRef(null);
  const [isSavingQuote, setIsSavingQuote] = useState(false);
  /** Blocks duplicate invoice status / payment taps while a Supabase write is in flight */
  const [quoteRowMutationBusyId, setQuoteRowMutationBusyId] = useState(null);

  const clearQuoteSaveLoadingState = useCallback(() => {
    setIsSavingQuote(false);
    saveQuoteInFlightRef.current = false;
    if (saveQuoteWatchdogRef.current) {
      clearTimeout(saveQuoteWatchdogRef.current);
      saveQuoteWatchdogRef.current = null;
    }
  }, []);

  useEffect(() => {
    devClickDebug('state: quoteRowMutationBusyId changed', { quoteRowMutationBusyId });
  }, [quoteRowMutationBusyId]);

  useEffect(() => {
    devClickDebug('state: isSavingQuote changed', { isSavingQuote });
  }, [isSavingQuote]);

  useEffect(
    () => () => {
      if (saveQuoteWatchdogRef.current) {
        clearTimeout(saveQuoteWatchdogRef.current);
        saveQuoteWatchdogRef.current = null;
      }
    },
    []
  );

  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailQuoteData, setEmailQuoteData] = useState(null);

  // User plan state
  const [userPlan, setUserPlan] = useState('free');
  const canUseMaterialSearch = userPlan === 'pro';
  const [, setIsTrial] = useState(false);
  const [, setTrialEndDate] = useState(null);
  const [userData, setUserData] = useState(null);

  // Material search state
  const [materialSearch, setMaterialSearch] = useState('');
  const [materialResults, setMaterialResults] = useState([]);
  const [materialQty, setMaterialQty] = useState(1);
  const [materialPrice, setMaterialPrice] = useState('');
  const [materialSearchLoading, setMaterialSearchLoading] = useState(false);
  /** Prevents double-trigger on custom template PDF (same pattern as saveQuoteInFlightRef) */
  const customPdfGenInFlightRef = useRef(false);
  const [materialHistory, setMaterialHistory] = useState([]);
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Material search location state (structured for multi-country support)
  const [locationCountry, setLocationCountry] = useState('CA');
  const [locationRegion, setLocationRegion] = useState('');
  const [locationCity, setLocationCity] = useState('');

  const regionsByCountry = {
    CA: [
      { code: 'ON', name: 'Ontario' },
      { code: 'QC', name: 'Quebec' },
      { code: 'BC', name: 'British Columbia' },
      { code: 'AB', name: 'Alberta' },
      { code: 'MB', name: 'Manitoba' },
      { code: 'SK', name: 'Saskatchewan' },
      { code: 'NS', name: 'Nova Scotia' },
      { code: 'NB', name: 'New Brunswick' },
      { code: 'NL', name: 'Newfoundland and Labrador' },
      { code: 'PE', name: 'Prince Edward Island' },
    ],
    US: [
      { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
      { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' }, { code: 'CT', name: 'Connecticut' },
      { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
      { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
      { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
      { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
      { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
      { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
      { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
      { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
      { code: 'UT', name: 'Utah' },
      { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
      { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
    ],
  };
  const locationRegions = regionsByCountry[locationCountry] || [];

  const [saveToHistory, setSaveToHistory] = useState(true);
  const [showCustomMaterialModal, setShowCustomMaterialModal] = useState(false);
  /** Isolated from quick-add row so closing the modal does not wipe typed description/qty/price. */
  const [customMaterialModalDraft, setCustomMaterialModalDraft] = useState({
    name: '',
    qty: 1,
    price: '',
  });
  const [showCustomMaterialsList, setShowCustomMaterialsList] = useState(false);
  const [customMaterialSearch, setCustomMaterialSearch] = useState('');
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [showEditMaterialModal, setShowEditMaterialModal] = useState(false);
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [materialSaveMessage, setMaterialSaveMessage] = useState('');
  const [showMaterialSearchModal, setShowMaterialSearchModal] = useState(false);

  // Helper function to calculate quote totals with labour

  
  // Custom Quote Template State
  const [customQuoteData, setCustomQuoteData] = useState({
    // Company Information
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyWebsite: '',
    companyLogo: null,
    logoSize: 60, // default size in px
    
    // Quote Details
    quoteNumber: '',
    quoteDate: new Date().toLocaleDateString(),
    clientName: '',
    clientAddress: '',
    clientPhone: '',
    clientEmail: '',
    
          // Design & Styling
      templateStyle: 'clean', // clean black design
      primaryColor: '#000000', // black
      secondaryColor: '#000000', // black
      fontFamily: 'Arial',
      showLogo: true,
      showBorder: false,
      showShadows: false,
    
    // Content
    scopeOfWork: [
      {
        id: Date.now(),
        heading: 'To complete the following:',
        points: [
          { id: Date.now() + 1, text: '' }
        ]
      }
    ],
    notes: '',
    
    
    // Additional Fields
    validUntil: '',
    termsAndConditions: '',
    signature: '',
    showMaterialsBreakdown: true,
    showLabourBreakdown: true,
    showTaxBreakdown: true,
    currency: 'CAD',
    taxRate: '',
    taxLabel: 'Tax'
  });

  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState('');
  
  // Custom Quote Template Modal State
  const [showCustomQuoteModal, setShowCustomQuoteModal] = useState(false);
  const [selectedQuoteForCustomTemplate, setSelectedQuoteForCustomTemplate] = useState(null);
  
  // Email modal state



  // Request changes modal state
  const [showRequestChangesModal, setShowRequestChangesModal] = useState(false);
  const [selectedQuoteForChanges, setSelectedQuoteForChanges] = useState(null);

  // Quote highlighting state for navigation
  const [highlightedQuoteId, setHighlightedQuoteId] = useState(null);

  // Project and team data for financial analytics
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [allDayAssignments, setAllDayAssignments] = useState({});

  // Teams management state
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [teamMemberForm, setTeamMemberForm] = useState({
    name: '',
    role: '',
    phone: '',
    email: '',
    hourlyRate: ''
  });
  const [editingTeamMember, setEditingTeamMember] = useState(null);

  const [subcontractors, setSubcontractors] = useState([]);
  const [showAddSubcontractor, setShowAddSubcontractor] = useState(false);
  const [subcontractorForm, setSubcontractorForm] = useState({
    name: '',
    company: '',
    trade: '',
    phone: '',
    email: '',
    notes: '',
  });
  const [editingSubcontractor, setEditingSubcontractor] = useState(null);

  // Labour entry modal state
  const [showAddLabourModal, setShowAddLabourModal] = useState(false);
  const [labourEntryForm, setLabourEntryForm] = useState({
    teamMemberId: '',
    hours: ''
  });

  // Expenses management state
  const [expenses, setExpenses] = useState([]);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showViewExpenses, setShowViewExpenses] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [currentProjectFilter, setCurrentProjectFilter] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    title: '',
    amount: '',
    category: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    projectId: '',
    receipts: [],
    laborOrigin: 'internal'
  });
  const [expenseSearchTerm, setExpenseSearchTerm] = useState('');
  const [expenseCategoryFilter, setExpenseCategoryFilter] = useState('all');
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [selectedReceiptIndex, setSelectedReceiptIndex] = useState(0);
  const [currentReceiptsArray, setCurrentReceiptsArray] = useState([]);
  const [showReceiptLimitModal, setShowReceiptLimitModal] = useState(false);
  const [receiptLimitMessage, setReceiptLimitMessage] = useState('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const getNormalizedCity = () => locationCity.trim().replace(/\s+/g, ' ');
  const getLocationValidationErrors = () => {
    const errs = [];
    if (!getNormalizedCity()) errs.push('Enter a city');
    if (!locationRegion) errs.push('Select a state/province');
    return errs;
  };
  const buildSearchLocation = () => {
    const cityValue = getNormalizedCity();
    if (!cityValue || !locationRegion || !locationCountry) return '';
    return locationCountry === 'US'
      ? `${cityValue}, ${locationRegion}, USA`
      : `${cityValue}, ${locationRegion}, Canada`;
  };
  const handleLocationCountryChange = (countryCode) => {
    setLocationCountry(countryCode);
    setLocationRegion('');
    setLocationValidation(null);
    setLocationValidationMessage('');
  };
  const handleLocationRegionChange = (regionCode) => {
    setLocationRegion(regionCode);
    if (locationValidation !== 'testing') {
      setLocationValidation(null);
      setLocationValidationMessage('');
    }
  };
  const handleLocationCityChange = (cityValue) => {
    setLocationCity(cityValue);
    if (locationValidation !== 'testing') {
      setLocationValidation(null);
      setLocationValidationMessage('');
    }
  };

  // hasSearched state removed - no longer needed

  // Add state for location validation
  const [locationValidation, setLocationValidation] = useState(null); // null, 'valid', 'invalid', 'testing'
  const [locationValidationMessage, setLocationValidationMessage] = useState('');
  
  // Client search state for searchable dropdown
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // State to track if we're in review mode to prevent accidental form resets
  const [, setIsInReviewMode] = useState(false);

  // When clientId changes (e.g. open edit / pick from list), show that client's name. Do NOT depend on
  // clientSearch — otherwise any typing to search for a different client gets overwritten immediately.
  // When clientId is empty we do not clear clientSearch here (avoids wiping typed text if `clients` refreshes).
  useEffect(() => {
    if (!quoteForm.clientId) return;
    const selectedClient = clients.find((c) => c.id === quoteForm.clientId);
    if (selectedClient) {
      setClientSearch(selectedClient.name);
    }
  }, [quoteForm.clientId, clients]);

  // Handle clicking outside material dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMaterialDropdown && !event.target.closest('.material-search-container')) {
        setShowMaterialDropdown(false);
        setMaterialResults([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMaterialDropdown]);

  // Fast location test function
  const testLocation = async () => {
    const validationErrors = getLocationValidationErrors();
    if (validationErrors.length > 0) {
      setLocationValidation('invalid');
      setLocationValidationMessage(validationErrors.join('. '));
      return;
    }
    const locationString = buildSearchLocation();

    setLocationValidation('testing');
    setLocationValidationMessage('Testing location...');

    try {
      devReliabilityLog('testLocation:start', { location: locationString.slice(0, 80) });
      const res = await fetchWithReliability(`${DEVELLO_API_ORIGIN}/api/test-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location: locationString }),
        name: 'testLocation',
        timeoutMs: 35000,
        retries: 1,
      });
      if (!res.ok) {
        setLocationValidation('invalid');
        setLocationValidationMessage('Could not verify location. Check your connection and try again.');
        return;
      }
      const data = /** @type {{ valid?: boolean; error?: string }} */ (res.data);
      if (data.valid) {
        setLocationValidation('valid');
        setLocationValidationMessage('Valid location — ready for search.');
      } else {
        setLocationValidation('invalid');
        setLocationValidationMessage(
          data.error ? `Invalid location: ${data.error}` : 'Invalid location. Please check city/state/country.'
        );
      }
    } catch (error) {
      devReliabilityLog('testLocation:catch', { err: String(error) });
      setLocationValidation('invalid');
      setLocationValidationMessage(toUserMessage(error, 'Could not verify location. Please try again.'));
    }
  };

  // Search materials using SerpAPI
  const searchMaterials = async (query) => {
    if (!canUseMaterialSearch) {
      setMaterialSearchLoading(false);
      setShowMaterialSearchModal(false);
      return;
    }
    if (!query || query.length < 2) {
      setMaterialResults([]);
      setHasSearched(false);
      return;
    }

    const validationErrors = getLocationValidationErrors();
    if (validationErrors.length > 0) {
      setLocationValidation('invalid');
      setLocationValidationMessage(validationErrors.join('. '));
      return;
    }
    const locationString = buildSearchLocation();

    setMaterialSearchLoading(true);
    setHasSearched(true);
    if (query && query.length >= 2) {
      setShowMaterialSearchModal(true);
    }
    try {
      // First, filter material history for matching items (exact and partial matches)
      const historyResults = materialHistory
        .filter(m => {
          const searchLower = query.toLowerCase();
          const nameLower = m.name.toLowerCase();
          return nameLower.includes(searchLower) || searchLower.includes(nameLower);
        })
        .map(m => ({ 
          ...m, 
          source: '📚 Your History', 
          isHistory: true,
          matchScore: m.name.toLowerCase() === query.toLowerCase() ? 2 : 1 // Exact matches get higher priority
        }))
        .sort((a, b) => b.matchScore - a.matchScore); // Sort by match score

      const requestBody = { 
        query,
        location: locationString
      };
      
      devReliabilityLog('searchMaterials:request', { query: query.slice(0, 60), hasLocation: !!locationString });

      const res = await fetchWithReliability(`${DEVELLO_API_ORIGIN}/api/search-materials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        name: 'searchMaterials',
        timeoutMs: 55000,
        retries: 1,
      });

      let apiResults = [];
      if (res.ok) {
        const data = /** @type {{ results?: unknown[] }} */ (res.data);
        apiResults = Array.isArray(data.results) ? data.results : [];
      } else {
        devReliabilityLog('searchMaterials:apiFail', { userMessage: res.userMessage });
      }
      
      const combinedResults = [...historyResults, ...apiResults];
      setMaterialResults(combinedResults);
      
    } catch (error) {
      devReliabilityLog('searchMaterials:catch', { err: String(error) });
      const historyResults = materialHistory
        .filter(m => {
          const searchLower = query.toLowerCase();
          const nameLower = m.name.toLowerCase();
          return nameLower.includes(searchLower) || searchLower.includes(nameLower);
        })
        .map(m => ({ 
          ...m, 
          source: '📚 Your History', 
          isHistory: true,
          matchScore: m.name.toLowerCase() === query.toLowerCase() ? 2 : 1
        }))
        .sort((a, b) => b.matchScore - a.matchScore);
      
      setMaterialResults(historyResults);
    } finally {
      setMaterialSearchLoading(false);
    }
  };

  const searchMaterialsRef = useRef(searchMaterials);
  searchMaterialsRef.current = searchMaterials;

  const materialSearchDebounceRef = useRef(null);
  const debouncedSearch = useCallback((query) => {
    if (materialSearchDebounceRef.current) {
      clearTimeout(materialSearchDebounceRef.current);
    }
    materialSearchDebounceRef.current = setTimeout(() => {
      materialSearchDebounceRef.current = null;
      void searchMaterialsRef.current(query);
    }, 500);
  }, []);

  const handleMaterialShoppingPick = ({ name, price }) => {
    setMaterialSearch(name);
    setMaterialPrice(String(price));
    setMaterialQty(1);
    setMaterialResults([]);
    setShowMaterialSearchModal(false);
    setShowMaterialDropdown(false);
    setHasSearched(false);
  };

  // Save material to history
  const saveMaterialToHistory = async (material) => {
    devLog('saveMaterialToHistory called with:', material);
    if (!user) {
      devLog('No user found, returning early');
      return;
    }
    
    try {
      const newMaterial = {
        id: Date.now().toString(),
        name: material.name,
        price: material.price,
        addedAt: new Date().toISOString(),
        isCustom: true
      };
      
      devLog('New material object:', newMaterial);
      
      const updatedHistory = [newMaterial, ...materialHistory.filter(m => m.name !== material.name)];
      devLog('Updated history:', updatedHistory);
      
      setMaterialHistory(updatedHistory);

      const materialHistoryForDb = JSON.parse(JSON.stringify(updatedHistory));
      await patchProfileColumns(user.id, { material_history: materialHistoryForDb });
      devLog('Material saved to Supabase successfully');
      
      // Show success message
      setMaterialSaveMessage(`"${material.name}" saved!`);
      setTimeout(() => setMaterialSaveMessage(''), 2000); // Clear after 2 seconds
    } catch (error) {
      devError('Error saving material to history:', error);
    }
  };

  // Calculate totals using the new calculation function
  const totals = calculateQuoteTotals(quoteForm);
  
  // Legacy variable aliases for backward compatibility
  const materialsSubtotal = totals.materialsSubtotal;
  const materialsTax = totals.materialsTax;
  const materialsWithTax = totals.materialsWithTax;
  const markupAmount = totals.markupAmount;
  const total = totals.total;

  useEffect(() => {
    let cancelled = false;
    let initialSessionHandled = false;
    const SAFETY_RELEASE_MS = 20000;
    let safetyReleaseId = null;

    const applySession = async (event, session) => {
      if (cancelled) return;
      devClickDebug('onAuthStateChange', { event, hasSession: !!session });
      const user = session?.user ?? null;
      setUser(user);
      // Auto-refresh / resume must not re-enter heavy Supabase + Realtime setup from this callback;
      // doing so can deadlock the client so REST calls never complete until a full reload.
      if (event === 'TOKEN_REFRESHED') {
        devClickDebug('onAuthStateChange: early exit (TOKEN_REFRESHED — heavy setup skipped)', {});
        return;
      }
      if (event === 'USER_UPDATED') {
        setUserData((prev) => (prev && user ? { ...prev, email: user.email || prev.email } : prev));
        return;
      }
      if (!user) {
        setLoading(false);
        // Logout should land on marketing home; only bounce to /account when opening dashboard without a session.
        if (event === 'SIGNED_OUT') {
          navigate('/', { replace: true });
        } else {
          navigate('/account', { replace: true });
        }
      } else if (user.app_metadata?.user_type === 'team_member') {
        setLoading(false);
        await supabase.auth.signOut();
        navigate('/account?mode=team', { replace: true });
      } else if (!user.email_confirmed_at) {
        setLoading(false);
        navigate('/verify-email');
      } else {
        // Load user profile from Supabase (single fetch for all profile data)
        try {
          const { data: profileData } = await supabase.from('profiles').select('*').eq('id', user.id).single();
          if (profileData) {
            if (profileData.settings) {
              const loadedSettings = {
                name: '',
                emailNotifications: true,
                companyName: '',
                companyLogo: null,
                streetAddress: '',
                country: 'CA',
                city: '',
                province: '',
                postalCode: '',
                websiteUrl: '',
                businessHours: '',
                companyEmail: '',
                companyPhone: '',
                hstNumber: '',
                logoSize: 60,
                ...profileData.settings
              };
              if (loadedSettings.country !== 'US' && loadedSettings.country !== 'CA') {
                loadedSettings.country = inferCountryFromProvince(loadedSettings.province);
              }
              setSettings(loadedSettings);
              setHasUnsavedChanges(false);
            } else {
              setHasUnsavedChanges(false);
            }
            if (profileData.clients) setClients(profileData.clients);
            if (profileData.material_history) setMaterialHistory(profileData.material_history);
            if (profileData.projects) {
              const projectSortTime = (p) => {
                const timeCandidate = p?.createdAt || p?.startDate || p?.updatedAt;
                const dateValue = timeCandidate ? new Date(timeCandidate).getTime() : NaN;
                if (!Number.isNaN(dateValue)) return dateValue;
                const idValue = typeof p?.id === 'string' || typeof p?.id === 'number' ? Number(p.id) : NaN;
                return Number.isNaN(idValue) ? 0 : idValue;
              };
              const sortedProjects = [...profileData.projects].sort(
                (a, b) => projectSortTime(b) - projectSortTime(a)
              );
              setProjects(sortedProjects);
            }
            if (profileData.teams) setTeams(profileData.teams);
            if (profileData.subcontractors) setSubcontractors(profileData.subcontractors);
            if (profileData.appointments) setAppointments(profileData.appointments);
            if (profileData.projects?.length || profileData.expenses) {
              const topLevel = profileData.expenses || [];
              const inline = (profileData.projects || []).flatMap((p) =>
                (p.expenses || []).map((e) => ({
                  ...e,
                  projectId: e.projectId != null ? String(e.projectId) : String(p.id),
                }))
              );
              const seen = new Set();
              const merged = [];
              for (const e of [...topLevel, ...inline]) {
                const id = e?.id != null ? String(e.id) : '';
                if (!id || seen.has(id)) continue;
                seen.add(id);
                merged.push(e);
              }
              setExpenses(merged);
            }
            if (profileData.day_assignments) setAllDayAssignments(profileData.day_assignments);
          } else {
            setHasUnsavedChanges(false);
          }
        } catch (error) {}
        // Load quotes from Supabase with real-time subscription
        try {
          // Initial fetch of quotes
          const { data: initialQuotes } = await supabase
            .from('quotes').select('*').eq('user_id', user.id).order('created_at', { ascending: false });

          const processQuotes = (quotesData) => {
            if (!quotesData) return;
            const cleanedQuotesData = quotesData.map(quote => {
              if (quote.agreed_price && quote.agreed_price < 10) {
                devLog('[Quote Cleanup] Found quote with suspiciously low agreed price:', quote.id, quote.agreed_price);
                return { ...quote, agreed_price: null, agreed_price_set_at: null };
              }
              return quote;
            });
            
            devLog('📊 Loaded quotes from Supabase:', cleanedQuotesData.map(q => ({ 
              id: q.id, 
              status: q.status,
              quote_sent: q.quote_sent,
              invoice_sent: q.invoice_sent,
              hasInvoiceData: !!q.invoice_data 
            })));
            
            const sortedQuotes = cleanedQuotesData
              .map((q) => normalizeQuoteFromDb(q))
              .sort((a, b) => {
                const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                return dateB - dateA;
              });

            devLog('🔄 Setting quotes:', sortedQuotes.length, 'quotes');
            setQuotes(sortedQuotes);
            
            // Migrate existing quotes with old 'sent' status to have proper tracking
            const quotesToMigrate = sortedQuotes.filter(quote => 
              quote.status === 'sent'
            );
            
            if (quotesToMigrate.length > 0) {
              devLog(`Found ${quotesToMigrate.length} quotes with old 'sent' status to migrate`);
              
              quotesToMigrate.forEach(async (quote) => {
                try {
                  if (quote.quote_sent || quote.invoice_sent) {
                    let newStatus = 'sent_quote';
                    if (quote.invoice_sent && quote.quote_sent) {
                      newStatus = 'sent_both';
                    } else if (quote.invoice_sent && !quote.quote_sent) {
                      newStatus = 'sent_invoice';
                    }
                    
                    await runAuthedWrite('dashboard:migrateSentStatusDirect', async () => {
                      const { error } = await supabase
                        .from('quotes')
                        .update({
                          status: newStatus
                        })
                        .eq('id', quote.id);
                      if (error) throw error;
                    });
                    
                    setQuotes(prev => prev.map(q => 
                      q.id === quote.id 
                        ? { ...q, status: newStatus }
                        : q
                    ));
                  } else {
                    const hasEmailEvidence = quote.sentToClientAt || quote.emailSentAt || quote.lastEmailSentAt;
                    
                    if (hasEmailEvidence) {
                      let newStatus = 'sent_quote';
                      let migrationData = {
                        quote_sent: true,
                        quoteSentAt: quote.sentToClientAt || quote.emailSentAt || quote.lastEmailSentAt || quote.updated_at || new Date().toISOString()
                      };
                      
                      if (quote.invoice_data || quote.invoice_generated_at) {
                        newStatus = 'sent_both';
                        migrationData.invoice_sent = true;
                        migrationData.invoiceSentAt = quote.invoice_generated_at || quote.sentToClientAt || quote.emailSentAt || quote.lastEmailSentAt || quote.updated_at || new Date().toISOString();
                      }
                      
                      try {
                        await runAuthedWrite('dashboard:migrateSentStatusWithEvidence', async () => {
                          const { error: migErr } = await supabase
                            .from('quotes')
                            .update({
                              status: newStatus,
                              quote_sent: migrationData.quote_sent || false,
                              quote_sent_at: migrationData.quoteSentAt,
                              invoice_sent: migrationData.invoice_sent || false,
                              invoice_sent_at: migrationData.invoiceSentAt,
                            })
                            .eq('id', quote.id);
                          if (migErr) throw migErr;
                        });
                      } catch (migErr) {
                        devError('Quote migration update failed:', migErr);
                      }
                      
                      setQuotes(prev => prev.map(q => 
                        q.id === quote.id 
                          ? { ...q, status: newStatus, ...migrationData }
                          : q
                      ));
                    } else {
                      await runAuthedWrite('dashboard:migrateSentStatusToDraft', async () => {
                        const { error } = await supabase
                          .from('quotes')
                          .update({
                            status: 'draft'
                          })
                          .eq('id', quote.id);
                        if (error) throw error;
                      });
                      
                      setQuotes(prev => prev.map(q => 
                        q.id === quote.id 
                          ? { ...q, status: 'draft' }
                          : q
                      ));
                    }
                  }
                } catch (error) {
                  devError(`Error migrating quote ${quote.id}:`, error);
                }
              });
            }

            // Migrate existing quotes with labour entries missing 'total' field
            const quotesWithLabourToMigrate = sortedQuotes.filter(quote => 
              quote.labour?.entries?.some(entry => !entry.total || typeof entry.total !== 'number')
            );
            
            if (quotesWithLabourToMigrate.length > 0) {
              devLog(`Found ${quotesWithLabourToMigrate.length} quotes with labour entries missing 'total' field to migrate`);
              
              quotesWithLabourToMigrate.forEach(async (quote) => {
                try {
                  const updatedLabourEntries = quote.labour.entries.map(entry => {
                    const entryTotal = entry.total || (entry.hours * entry.rate) || 0;
                    return {
                      ...entry,
                      total: typeof entryTotal === 'number' && !isNaN(entryTotal) ? entryTotal : 0
                    };
                  });
                  
                  const newLabourTotal = updatedLabourEntries.reduce((sum, entry) => sum + (entry.total || 0), 0);
                  
                  await runAuthedWrite('dashboard:migrateLabourTotals', async () => {
                    const { error } = await supabase
                      .from('quotes')
                      .update({
                        labour: {
                          entries: updatedLabourEntries,
                          total: newLabourTotal
                        }
                      })
                      .eq('id', quote.id);
                    if (error) throw error;
                  });
                  
                  setQuotes(prev => prev.map(q => 
                    q.id === quote.id 
                      ? { 
                          ...q, 
                          labour: {
                            ...q.labour,
                            entries: updatedLabourEntries,
                            total: newLabourTotal
                          }
                        }
                      : q
                  ));
                } catch (error) {
                  devError(`Error migrating labour entries for quote ${quote.id}:`, error);
                }
              });
            }
          };

          processQuotes(initialQuotes);

          if (quotesChannelRef.current) {
            supabase.removeChannel(quotesChannelRef.current);
            quotesChannelRef.current = null;
          }

          const channelName = `quotes-realtime-${user.id}`;
          const quotesChannel = supabase
            .channel(channelName)
            .on(
              'postgres_changes',
              {
                event: '*',
                schema: 'public',
                table: 'quotes',
                filter: `user_id=eq.${user.id}`,
              },
              async () => {
                const { data: quotesData } = await supabase
                  .from('quotes')
                  .select('*')
                  .eq('user_id', user.id)
                  .order('created_at', { ascending: false });
                if (quotesData) processQuotes(quotesData);
              }
            )
            .subscribe();

          quotesChannelRef.current = quotesChannel;
        } catch (error) {
          devError('Error loading quotes:', error);
        }
        // Billing & entitlements (plan_tier free | pro; access_source manual | stripe)
        try {
          const { data: planData, error: planError } = await supabase.from('profiles').select('*').eq('id', user.id).single();

          if (planError) {
            devError('Dashboard: billing profile query error:', planError);
          }

          if (!planData) {
            if (!planError || planError.code === 'PGRST116') {
              navigate('/account', { replace: true });
              return;
            }
            setLoading(false);
            return;
          }

          const revoked = planData.accessRevoked === true || planData.access_revoked === true;
          if (revoked) {
            navigate('/account', { replace: true });
            return;
          }

          if (planData.plan === 'expired') {
            try {
              await runAuthedWrite('dashboard:normalizeExpiredPlan', async () => {
                const { error } = await supabase
                  .from('profiles')
                  .update({
                    plan: 'free',
                    plan_tier: 'free',
                    billing_status: 'none',
                    stripe_billing_status: 'canceled',
                  })
                  .eq('id', user.id);
                if (error) throw error;
              });
              planData.plan = 'free';
              planData.plan_tier = 'free';
            } catch (writeErr) {
              devError('Dashboard: normalizeExpiredPlan write failed (non-fatal):', writeErr);
            }
          }

          setUserData({ ...planData, email: user.email });
          setUserPlan(effectivePlanTier(planData));
          setIsTrial(!!(planData.is_trial ?? planData.isTrial));
          setTrialEndDate(
            planData.trial_end_date || planData.trialEndDate
              ? new Date(planData.trial_end_date || planData.trialEndDate)
              : null
          );
        } catch (error) {
          devError('Dashboard: Error loading billing profile (non-fatal):', error);
        } finally {
          setLoading(false);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        if (initialSessionHandled) return;
        initialSessionHandled = true;
      }
      void applySession(event, session);
    });

    // Explicit initial bootstrap: some sleep/wake restores can miss or delay INITIAL_SESSION.
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (cancelled) return;
        if (error) {
          devWarn('Dashboard: initial getSession failed', error);
          setLoading(false);
          return;
        }
        if (!initialSessionHandled) {
          initialSessionHandled = true;
          void applySession('INITIAL_SESSION', session);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        devWarn('Dashboard: initial getSession threw', e);
        setLoading(false);
      });

    // Hard stop: never strand dashboard on spinner forever.
    safetyReleaseId = window.setTimeout(() => {
      if (!cancelled) {
        devWarn('Dashboard: releasing stuck loading state after safety timeout');
        setLoading(false);
      }
    }, SAFETY_RELEASE_MS);

    return () => {
      cancelled = true;
      if (safetyReleaseId) window.clearTimeout(safetyReleaseId);
      subscription.unsubscribe();
    };
  }, [navigate, runAuthedWrite]);

  useEffect(() => {
    return () => {
      if (quotesChannelRef.current) {
        supabase.removeChannel(quotesChannelRef.current);
        quotesChannelRef.current = null;
      }
      if (projectsChannelRef.current) {
        supabase.removeChannel(projectsChannelRef.current);
        projectsChannelRef.current = null;
      }
    };
  }, []);

  // ── Realtime: sync project changes made by team members ──────────
  useEffect(() => {
    if (!user?.id) return;
    if (projectsChannelRef.current) {
      supabase.removeChannel(projectsChannelRef.current);
      projectsChannelRef.current = null;
    }
    const ch = supabase
      .channel(`projects-realtime-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
        async () => {
          const { data } = await supabase
            .from('profiles')
            .select('projects')
            .eq('id', user.id)
            .single();
          if (data?.projects) {
            setProjects((prev) => {
              const serverMap = new Map(data.projects.map((p) => [String(p.id), p]));
              return prev.map((local) => {
                const server = serverMap.get(String(local.id));
                return server ? { ...server } : local;
              });
            });
          }
        }
      )
      .subscribe();
    projectsChannelRef.current = ch;
    return () => {
      supabase.removeChannel(ch);
      projectsChannelRef.current = null;
    };
  }, [user?.id]);

  const loadPaymentStatus = useCallback(async (opts = {}) => {
    if (DISABLE_AUTO_BILLING_SUMMARY_FETCH && !opts?.force) {
      devClickDebug('loadPaymentStatus: skipped (DISABLE_AUTO_BILLING_SUMMARY_FETCH)', {});
      return;
    }
    if (!userData?.email) return;
    if (accessSource(userData) === 'manual') {
      setPaymentStatus(null);
      return;
    }
    try {
      devClickDebug('loadPaymentStatus: calling fetchBillingSummary', { force: !!opts?.force });
      const data = await fetchBillingSummary();
      setPaymentStatus(data);
      const sub = data.subscriptionStatus;
      const bill = data.billingStatus;
      const bad = sub === 'past_due' || sub === 'unpaid' || bill === 'past_due' || bill === 'unpaid';
      if (!bad) return;
      const trial = !!(userData.is_trial ?? userData.isTrial);
      if (trial) {
        navigate('/select-plan', {
          state: {
            trialExpired: true,
            message: 'Your free trial has ended. Please select a plan to continue.',
          },
        });
      } else {
        navigate('/payment-warning', {
          state: {
            status: sub || bill,
            subscriptionId: null,
            email: userData.email,
          },
        });
      }
    } catch (error) {
      devError('Error loading billing summary:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional granular deps (not whole `userData`)
  }, [
    userData?.id,
    userData?.email,
    userData?.access_source,
    userData?.accessSource,
    userData?.billing_status,
    userData?.billingStatus,
    userData?.is_trial,
    userData?.isTrial,
    navigate,
  ]);

  useEffect(() => {
    if (section !== 'settings' && section !== 'billing') return;
    const q = new URLSearchParams(routerLocation.search);
    const sid = q.get('session_id');
    if (!sid) return;
    let cancelled = false;
    (async () => {
      try {
        await syncCheckoutSession(sid);
        clearSignupPlanIntent();
        await refreshProfilePlan();
        await loadPaymentStatus({ force: true });
        if (!cancelled) {
          openPlanBillingModal(true);
          setSaveMessage('Payment confirmed. Your subscription has been synced.');
        }
      } catch (error) {
        if (!cancelled) {
          setSaveMessage(`Error syncing payment: ${error?.message || 'Please refresh and try again.'}`);
        }
      } finally {
        if (cancelled) return;
        q.delete('session_id');
        q.delete('checkout');
        q.set('plans', '1');
        const next = q.toString();
        navigate({ pathname: '/dashboard/settings', search: next ? `?${next}` : '' }, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [section, routerLocation.search, routerLocation.pathname, navigate, refreshProfilePlan, loadPaymentStatus, openPlanBillingModal]);

  useEffect(() => {
    if (!userData?.email) return;
    let cancelled = false;
    fetch(`${DEVELLO_API_ORIGIN}/api/health`).catch(() => {});
    (async () => {
      if (!cancelled) await loadPaymentStatus();
    })();
    return () => {
      cancelled = true;
    };
  }, [userData?.email, userData?.access_source, userData?.billing_status, loadPaymentStatus]);

  // Save user settings to Supabase
  const saveUserSettings = async () => {
    devLog('saveUserSettings called!');
    if (!user) return;

    setSaving(true);
    setSaveMessage('');
    
    try {
      let companyLogo = settings.companyLogo || null;
      if (companyLogo && typeof companyLogo === 'string' && companyLogo.startsWith('data:')) {
        try {
          const res = await fetch(companyLogo);
          const blob = await res.blob();
          const { error: uploadErr } = await supabase.storage
            .from('company-logos')
            .upload(`${user.id}/company-logo`, blob, {
              upsert: true,
              contentType: blob.type || 'image/jpeg',
              cacheControl: '3600',
            });
          if (uploadErr) throw uploadErr;
          const { data: { publicUrl } } = supabase.storage.from('company-logos').getPublicUrl(`${user.id}/company-logo`);
          // Same storage path returns the same URL — bust cache so replaced logos show immediately.
          companyLogo = `${publicUrl}?v=${Date.now()}`;
          setSettings((prev) => ({ ...prev, companyLogo }));
        } catch (uploadErr) {
          devError('Logo upload failed:', uploadErr);
          setSaveMessage(
            `Could not upload the company logo: ${uploadErr?.message || 'Unknown error'}.`
          );
          setSaving(false);
          return;
        }
      }

      const cleanSettings = {
        name: settings.name,
        emailNotifications: settings.emailNotifications,
        companyName: settings.companyName,
        streetAddress: settings.streetAddress,
        country: settings.country === 'US' ? 'US' : 'CA',
        city: settings.city,
        province: settings.province,
        postalCode: settings.postalCode,
        companyEmail: settings.companyEmail,
        companyPhone: settings.companyPhone,
        hstNumber: settings.hstNumber,
        companyLogo,
      };
      
      devLog('Clean settings data:', { ...cleanSettings, companyLogo: companyLogo ? '(set)' : null });
      
      await runAuthedWrite('dashboard:saveSettings', async () => {
        const { error } = await supabase
          .from('profiles')
          .update({
            settings: cleanSettings,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.id);
        if (error) throw error;
      });
      
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
      setHasUnsavedChanges(false);
    } catch (error) {
      devError('Error saving settings:', error);
      devError('Error message:', error.message);
      setSaveMessage(`Error saving settings: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Handle settings input changes
  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handleSettingsLogoUpload = (event) => {
    const input = event.target;
    const file = input.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|jpg|webp)$/i.test(file.type)) {
      setSaveMessage('Please upload a PNG or JPEG image.');
      setTimeout(() => setSaveMessage(''), 4000);
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => {
      setSaveMessage('Could not read that image.');
      setTimeout(() => setSaveMessage(''), 4000);
      input.value = '';
    };
    reader.onload = (e) => {
      const originalLogo = e.target.result;
      const finish = (dataUrl) => {
        setSettings((prev) => ({ ...prev, companyLogo: dataUrl }));
        setHasUnsavedChanges(true);
        input.value = '';
      };
      if (typeof originalLogo !== 'string') {
        input.value = '';
        return;
      }
      if (originalLogo.length > 500000) {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxWidth = 400;
          const maxHeight = 400;
          let { width, height } = img;
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          let quality = 0.82;
          let out = canvas.toDataURL('image/jpeg', quality);
          while (out.length > 700000 && quality > 0.45) {
            quality -= 0.07;
            out = canvas.toDataURL('image/jpeg', quality);
          }
          finish(out);
        };
        img.onerror = () => {
          setSaveMessage('Could not read that image.');
          setTimeout(() => setSaveMessage(''), 4000);
          input.value = '';
        };
        img.src = originalLogo;
        return;
      }
      finish(originalLogo);
    };
    reader.readAsDataURL(file);
  };

  // Format phone number as (555) 555-5555
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Handle phone number input specifically
  const handlePhoneChange = (value) => {
    const formatted = formatPhoneNumber(value);
    setSettings(prev => ({
      ...prev,
      companyPhone: formatted
    }));
    setHasUnsavedChanges(true);
  };

  const saveClient = async (clientData) => {
    try {
      const newClient = {
        id: clientData.id || Date.now().toString(),
        ...clientData,
        createdAt: clientData.createdAt || new Date(),
        updatedAt: new Date()
      };

      const updatedClients = clientData.id 
        ? clients.map(c => c.id === clientData.id ? newClient : c)
        : [newClient, ...clients];

      let clientsForDb;
      try {
        clientsForDb = JSON.parse(JSON.stringify(updatedClients));
      } catch {
        throw new Error('Client data could not be serialized for save.');
      }

      /** PostgREST PATCH with stored JWT — avoids supabase-js hanging on `getSession()` during auth churn. */
      await patchProfileClientsColumn(user.id, clientsForDb);

      setClients(updatedClients);
      setShowAddClient(false);
      setClientForm({
        name: '', email: '', phone: '', street: '', city: '', province: '', postalCode: '', status: 'active'
      });
    } catch (error) {
      devError('Error saving client:', error);
      alert(toUserMessage(error, 'Could not save client. Please try again.'));
    }
  };

  const deleteClient = async (clientId) => {
    try {
      const updatedClients = clients.filter(c => c.id !== clientId);
      const clientsForDb = JSON.parse(JSON.stringify(updatedClients));
      await patchProfileClientsColumn(user.id, clientsForDb);
      setClients(updatedClients);
    } catch (error) {
      devError('Error deleting client:', error);
      alert(toUserMessage(error, 'Could not delete client. Please try again.'));
    }
  };

  const filteredClients = clients.filter(client => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleClientFormChange = (field, value) => {
    setClientForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Teams management functions
  const saveTeamMember = async (teamMemberData) => {
    devClickDebug('saveTeamMember:handler start', { hasId: !!teamMemberData?.id });
    try {
      const newTeamMember = {
        id: teamMemberData.id || Date.now().toString(),
        name: teamMemberData.name,
        role: teamMemberData.role,
        phone: teamMemberData.phone || '',
        email: teamMemberData.email || '',
        hourlyRate: parseFloat(teamMemberData.hourlyRate) || 0,
        createdAt: teamMemberData.createdAt || new Date(),
        updatedAt: new Date()
      };

      const updatedTeams = teamMemberData.id 
        ? teams.map(t => t.id === teamMemberData.id ? newTeamMember : t)
        : [...teams, newTeamMember];

      const teamsForDb = JSON.parse(JSON.stringify(updatedTeams));
      await patchProfileColumns(user.id, { teams: teamsForDb });

      setTeams(updatedTeams);
      setShowAddTeamMember(false);
      setEditingTeamMember(null);
      setTeamMemberForm({
        name: '', role: '', phone: '', email: '', hourlyRate: ''
      });
    } catch (error) {
      devError('Error saving team member:', error);
      alert(toUserMessage(error, 'Could not save team member. Please try again.'));
    }
  };

  const deleteTeamMember = async (teamMemberId) => {
    try {
      const updatedTeams = teams.filter(t => t.id !== teamMemberId);
      const teamsForDb = JSON.parse(JSON.stringify(updatedTeams));
      await patchProfileColumns(user.id, { teams: teamsForDb });
      setTeams(updatedTeams);
    } catch (error) {
      devError('Error deleting team member:', error);
      alert(toUserMessage(error, 'Could not delete team member. Please try again.'));
    }
  };

  const saveSubcontractor = async (data) => {
    try {
      if (!data.name || !data.name.trim()) {
        alert('Please enter a name');
        return;
      }
      const prevRow = data.id ? subcontractors.find((s) => s.id === data.id) : null;
      const row = {
        id: data.id || Date.now().toString(),
        name: data.name.trim(),
        company: data.company || '',
        trade: data.trade || '',
        phone: data.phone || '',
        email: data.email || '',
        defaultFlatRate:
          prevRow != null && prevRow.defaultFlatRate != null
            ? Number(prevRow.defaultFlatRate) || 0
            : 0,
        notes: data.notes || '',
        createdAt: data.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const updated = data.id
        ? subcontractors.map((s) => (s.id === data.id ? row : s))
        : [...subcontractors, row];

      const subsForDb = JSON.parse(JSON.stringify(updated));
      await patchProfileColumns(user.id, { subcontractors: subsForDb });

      setSubcontractors(updated);
      setShowAddSubcontractor(false);
      setEditingSubcontractor(null);
      setSubcontractorForm({
        name: '',
        company: '',
        trade: '',
        phone: '',
        email: '',
        notes: '',
      });
    } catch (error) {
      devError('Error saving subcontractor:', error);
    }
  };

  const deleteSubcontractor = async (subId) => {
    try {
      const updated = subcontractors.filter((s) => s.id !== subId);
      const subsForDb = JSON.parse(JSON.stringify(updated));
      await patchProfileColumns(user.id, { subcontractors: subsForDb });
      setSubcontractors(updated);
    } catch (error) {
      devError('Error deleting subcontractor:', error);
      alert(toUserMessage(error, 'Could not delete subcontractor. Please try again.'));
    }
  };

  const editSubcontractor = (s) => {
    setEditingSubcontractor(s);
    setSubcontractorForm({
      id: s.id,
      name: s.name,
      company: s.company || '',
      trade: s.trade || '',
      phone: s.phone || '',
      email: s.email || '',
      notes: s.notes || '',
      createdAt: s.createdAt,
    });
    setShowAddSubcontractor(true);
  };

  const handleSubcontractorFormChange = (field, value) => {
    if (field === 'phone') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = '';
      if (cleaned.length > 0) {
        formatted = '(' + cleaned.substring(0, 3);
        if (cleaned.length > 3) {
          formatted += ') ' + cleaned.substring(3, 6);
          if (cleaned.length > 6) {
            formatted += '-' + cleaned.substring(6, 10);
          }
        }
      }
      setSubcontractorForm((prev) => ({ ...prev, [field]: formatted }));
    } else {
      setSubcontractorForm((prev) => ({ ...prev, [field]: value }));
    }
  };

  const editTeamMember = (teamMember) => {
    setEditingTeamMember(teamMember);
    setTeamMemberForm({
      id: teamMember.id,
      name: teamMember.name,
      role: teamMember.role,
              phone: teamMember.phone || '',
        email: teamMember.email || '',
        hourlyRate: teamMember.hourlyRate ? teamMember.hourlyRate.toString() : ''
    });
    setShowAddTeamMember(true);
  };

  const handleTeamMemberFormChange = (field, value) => {
    if (field === 'phone') {
      // Format phone number as (555) 555-5555
      const cleaned = value.replace(/\D/g, '');
      let formatted = '';
      if (cleaned.length > 0) {
        formatted = '(' + cleaned.substring(0, 3);
        if (cleaned.length > 3) {
          formatted += ') ' + cleaned.substring(3, 6);
          if (cleaned.length > 6) {
            formatted += '-' + cleaned.substring(6, 10);
          }
        }
      }
      setTeamMemberForm(prev => ({
        ...prev,
        [field]: formatted
      }));
    } else {
      setTeamMemberForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Expenses management functions
  const saveExpense = async (expenseData, options = {}) => {
    const { skipUiReset = false } = options;
    try {
      devLog('Saving expense with data:', expenseData);
      
      // Clean the expense data
      const categoryNorm =
        expenseData.category === 'labor' || expenseData.category === 'labour' ? 'labour' : expenseData.category;

      const rawReceipts = expenseData.receipts || [];
      const normalizedReceipts = rawReceipts
        .map((r) => {
          if (typeof r === 'string') return r;
          if (r && typeof r === 'object' && r.url && r.storagePath) {
            return {
              url: String(r.url).slice(0, 2048),
              storagePath: String(r.storagePath).slice(0, 512),
              kind: 'storage',
              uploadedAt: r.uploadedAt || new Date().toISOString(),
            };
          }
          return null;
        })
        .filter(Boolean);

      const cleanExpenseData = {
        id: expenseData.id || Date.now().toString(),
        title: expenseData.title,
        amount: parseFloat(expenseData.amount) || 0,
        category: categoryNorm,
        date: expenseData.date,
        description: expenseData.description || '',
        projectId: expenseData.projectId || '',
        receipts: normalizedReceipts,
        createdAt: expenseData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      if (expenseData.vendor != null && String(expenseData.vendor).trim() !== '') {
        cleanExpenseData.vendor = String(expenseData.vendor).trim().slice(0, 200);
      }

      if (categoryNorm === 'labour') {
        cleanExpenseData.laborOrigin =
          expenseData.laborOrigin === 'subcontractor' ? 'subcontractor' : 'internal';
      }

      if (expenseData.addedByName) {
        cleanExpenseData.addedByName = String(expenseData.addedByName).slice(0, 200);
      }

      if (expenseData.editHistory) {
        cleanExpenseData.editHistory = expenseData.editHistory;
      }

      // If this was originally an automatic labor expense and is being edited, make it manual
      if (expenseData.isAutomatic && expenseData.id) {
        cleanExpenseData.isAutomatic = false; // Convert to manual expense
        devLog('Converting automatic labor expense to manual expense');
      }

      // Check if any receipt data is too large (base64 strings only)
      const largeReceipts = cleanExpenseData.receipts.filter(
        (receipt) => typeof receipt === 'string' && receipt.length > 500000
      );
      if (largeReceipts.length > 0) {
        devLog('Some receipts are too large, filtering them out');
        cleanExpenseData.receipts = cleanExpenseData.receipts.filter(receipt => receipt.length <= 500000);
        alert(`${largeReceipts.length} receipt(s) were too large and were removed. The expense will be saved with the remaining receipts.`);
      }

      // Remove any undefined values
      Object.keys(cleanExpenseData).forEach(key => {
        if (cleanExpenseData[key] === undefined) {
          delete cleanExpenseData[key];
        }
      });

      devLog('Cleaned expense data:', cleanExpenseData);

      let expensesToPersist = expenseData.id
        ? expenses.map((e) => (e.id === expenseData.id ? cleanExpenseData : e))
        : [cleanExpenseData, ...expenses];

      try {
        await runAuthedWrite('dashboard:saveExpense', async () => {
          const { error } = await supabase.from('profiles').update({ expenses: expensesToPersist }).eq('id', user.id);
          if (error) throw error;
        });
      } catch (saveError) {
        devLog('First save attempt failed, trying without receipt:', saveError.message);

        if (cleanExpenseData.receipts && cleanExpenseData.receipts.length > 0) {
          const expenseWithoutReceipts = { ...cleanExpenseData };
          delete expenseWithoutReceipts.receipts;

          expensesToPersist = expenseData.id
            ? expenses.map((e) => (e.id === expenseData.id ? expenseWithoutReceipts : e))
            : [expenseWithoutReceipts, ...expenses];

          await runAuthedWrite('dashboard:saveExpenseWithoutReceipts', async () => {
            const { error } = await supabase.from('profiles').update({ expenses: expensesToPersist }).eq('id', user.id);
            if (error) throw error;
          });
          alert('Expense saved successfully, but the receipts were too large and were removed.');
        } else {
          throw saveError;
        }
      }

      setExpenses(expensesToPersist);
      if (!skipUiReset) {
        setShowAddExpense(false);
        setEditingExpense(null);
        setExpenseForm({
          title: '',
          amount: '',
          category: '',
          date: new Date().toISOString().split('T')[0],
          description: '',
          projectId: '',
          receipts: [],
          laborOrigin: 'internal',
        });
      }

      devLog('Expense saved successfully');
      return true;
    } catch (error) {
      devError('Error saving expense:', error);
      devError('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      alert(toUserMessage(error, 'Could not save expense. Please try again.'));
      return false;
    }
  };

  const deleteExpense = async (expenseId) => {
    try {
      const updatedExpenses = expenses.filter(e => e.id !== expenseId);
      await runAuthedWrite('dashboard:deleteExpense', async () => {
        const { error } = await supabase.from('profiles').update({
          expenses: updatedExpenses
        }).eq('id', user.id);
        if (error) throw error;
      });
      setExpenses(updatedExpenses);
    } catch (error) {
      devError('Error deleting expense:', error);
      alert(toUserMessage(error, 'Could not delete expense. Please try again.'));
    }
  };

  const editExpense = (expense) => {
    setEditingExpense(expense);
    setExpenseForm({
      title: expense.title,
      amount: expense.amount.toString(),
      category: expense.category === 'labor' ? 'labour' : expense.category,
      date: expense.date,
      description: expense.description || '',
      projectId: expense.projectId || '', // Include projectId when editing
      receipts: expense.receipts || (expense.receipt ? [expense.receipt] : []), // Handle both old and new format
      isAutomatic: expense.isAutomatic || false, // Track if this was originally automatic
      laborOrigin: expense.laborOrigin || 'internal'
    });
    setShowAddExpense(true);
  };

  const handleExpenseFormChange = (field, value) => {
    if (field === 'category') {
      const cat = value === 'labor' ? 'labour' : value;
      setExpenseForm((prev) => ({
        ...prev,
        category: cat,
        laborOrigin: cat === 'labour' ? prev.laborOrigin || 'internal' : 'internal'
      }));
      return;
    }
    setExpenseForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleReceiptUpload = (event) => {
    devLog('Receipt upload triggered');
    const files = Array.from(event.target.files);
    
    if (files.length === 0) {
      devLog('No files selected');
      return;
    }

    // Ensure expenseForm.receipts exists, default to empty array if undefined
    const currentReceipts = expenseForm.receipts || [];
    
    // Check if adding these files would exceed the limit (max 5 receipts per expense)
    const currentReceiptCount = currentReceipts.length;
    if (currentReceiptCount + files.length > 5) {
      setReceiptLimitMessage(`You can only upload up to 5 receipts per expense. You currently have ${currentReceiptCount} receipt${currentReceiptCount === 1 ? '' : 's'}. Please remove some existing receipts before adding more.`);
      setShowReceiptLimitModal(true);
      return;
    }

    files.forEach((file, index) => {
      devLog('File selected:', file.name, file.type, file.size);
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setReceiptLimitMessage(`File "${file.name}" is not an image. Please select image files only.`);
        setShowReceiptLimitModal(true);
        return;
      }
      
      // Validate file size (increased limit since we'll compress)
      if (file.size > 10 * 1024 * 1024) { // 10MB limit for mobile photos
        setReceiptLimitMessage(`File "${file.name}" is too large (${Math.round(file.size / (1024 * 1024))}MB). File size must be less than 10MB. Please choose a smaller image.`);
        setShowReceiptLimitModal(true);
        return;
      }
      
      // Enhanced compression for mobile photos
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (balanced resizing for good quality)
        let maxSize = 1200; // Increased for better quality
        let { width, height } = img;
        
        // For very large images, resize more but keep good quality
        if (width > 3000 || height > 3000) {
          maxSize = 800; // Still good size for very large images
        } else if (width > 2000 || height > 2000) {
          maxSize = 1000; // Good size for large images
        }
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Progressive compression - start with good quality and reduce if needed
        let quality = 0.8; // Start with good quality
        let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        
        // If still too large, reduce quality gradually
        while (compressedDataUrl.length > 800000 && quality > 0.5) { // ~800KB limit, minimum 0.5 quality
          quality -= 0.1;
          compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        
        devLog('Image compressed, new size:', compressedDataUrl.length, 'quality:', quality);
        
        // Check if compressed size is still too large
        if (compressedDataUrl.length > 800000) { // ~800KB limit
          setReceiptLimitMessage(`Image "${file.name}" is still too large after compression (${Math.round(compressedDataUrl.length / 1024)}KB). Please choose a smaller image or take a new photo with lower resolution.`);
          setShowReceiptLimitModal(true);
          return;
        }
        
        setExpenseForm(prev => ({
          ...prev,
          receipts: [...(prev.receipts || []), compressedDataUrl]
        }));
      };
      
      img.onerror = () => {
        setReceiptLimitMessage(`Error processing image "${file.name}". Please try again.`);
        setShowReceiptLimitModal(true);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const removeReceipt = (index) => {
    setExpenseForm(prev => ({
      ...prev,
      receipts: (prev.receipts || []).filter((_, i) => i !== index)
    }));
  };

  const filteredExpenses = expenses.filter((expense) => {
    const matchesSearch =
      expense.title.toLowerCase().includes(expenseSearchTerm.toLowerCase()) ||
      (expense.description && expense.description.toLowerCase().includes(expenseSearchTerm.toLowerCase()));
    const catNorm = expense.category === 'labor' ? 'labour' : expense.category;
    const matchesCategory = expenseCategoryFilter === 'all' || catNorm === expenseCategoryFilter;
    const matchesProject = !currentProjectFilter || expense.projectId === currentProjectFilter;
    return matchesSearch && matchesCategory && matchesProject;
  });

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

  const handleLabourEntryFormChange = (field, value) => {
    setLabourEntryForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Labour management functions
  const addLabourEntry = (teamMemberId, hours) => {
    const teamMember = teams.find(t => t.id === teamMemberId);
    if (!teamMember) return;

    const newEntry = {
      id: Date.now().toString(),
      teamMemberId: teamMemberId,
      name: teamMember.name,
      hours: parseFloat(hours),
      rate: teamMember.hourlyRate,
      total: parseFloat(hours) * teamMember.hourlyRate
    };

    const updatedEntries = [...quoteForm.labour.entries, newEntry];
    const total = updatedEntries.reduce((sum, entry) => {
      const entryTotal = entry.total || (entry.hours * entry.rate) || 0;
      return sum + entryTotal;
    }, 0);

    setQuoteForm(prev => ({
      ...prev,
      labour: {
        entries: updatedEntries,
        total: total
      }
    }));
  };

  const removeLabourEntry = (entryId) => {
    const updatedEntries = quoteForm.labour.entries.filter(entry => entry.id !== entryId);
    const total = updatedEntries.reduce((sum, entry) => {
      const entryTotal = entry.total || (entry.hours * entry.rate) || 0;
      return sum + entryTotal;
    }, 0);

    setQuoteForm(prev => ({
      ...prev,
      labour: {
        entries: updatedEntries,
        total: total
      }
    }));
  };

  // Helper function to calculate quote totals with labour


  const handleQuoteFormChange = (field, value) => {
    setQuoteForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Helper function to reset quote form to initial state
  const resetQuoteForm = (resetNotesAndTerms = true) => {
    setQuoteForm({
      clientId: '',
      materials: [],
      markupType: 'percentage',
      overheadType: 'amount',
      taxEnabled: false,
      finalTaxEnabled: false,
      taxRate: '',
      labour: {
        entries: [],
        total: 0
      },
      description: '',
      editedOn: new Date().toISOString(),
      quoteNumber: '',
      customQuoteData: {}
    });
    setMaterialSearch('');
    setMaterialResults([]);
    setMaterialQty(1);
    setMaterialPrice('');
    setShowMaterialSearchModal(false);
    setShowMaterialDropdown(false);
    // Only reset notes if explicitly requested (for new quotes)
    if (resetNotesAndTerms) {
      setNotes([{ id: '1', text: '' }]);
    }
    // Reset client search
    setClientSearch('');
    setShowClientDropdown(false);
  };

  const closeCreateInvoiceModal = () => {
    setShowMaterialSearchModal(false);
    setShowCustomMaterialModal(false);
    setShowCustomMaterialsList(false);
    setShowEditMaterialModal(false);
    setEditingMaterial(null);
    resetQuoteForm();
    setIsEditingQuote(false);
    setShowCreateQuote(false);
  };

  const closeEditInvoiceModal = () => {
    setShowMaterialSearchModal(false);
    setShowCustomMaterialModal(false);
    setShowCustomMaterialsList(false);
    setShowEditMaterialModal(false);
    setEditingMaterial(null);
    resetQuoteForm();
    setIsEditingQuote(false);
    setShowEditQuote(false);
  };

  // Seed modal draft once when it opens — snapshot quick-add fields only at open, not on every keystroke.
  useEffect(() => {
    if (!showCustomMaterialModal) return;
    setCustomMaterialModalDraft({
      name: materialSearch || '',
      qty: materialQty >= 1 ? materialQty : 1,
      price: materialPrice !== '' && materialPrice != null ? String(materialPrice) : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustomMaterialModal]);

  // Save quote to Supabase (timeout + retry + session refresh — survives cold tab / stale JWT)
  const saveQuote = async (quoteData) => {
    devReliabilityLog('saveQuote:start', { hasId: !!quoteData?.id, isEditingQuote });
    devClickDebug('saveQuote:handler start', { hasId: !!quoteData?.id, isEditingQuote });

    if (!user) {
      devClickDebug('saveQuote:early return — no user', {});
      alert('Please sign in to save your invoice.');
      return false;
    }
    if (!supabase) {
      devClickDebug('saveQuote:early return — no supabase', {});
      alert('App configuration error. Please refresh the page.');
      return false;
    }
    if (saveQuoteInFlightRef.current) {
      devReliabilityLog('saveQuote:skipped-in-flight');
      devClickDebug('saveQuote:early return — saveQuoteInFlightRef already true', {});
      return false;
    }

    saveQuoteInFlightRef.current = true;
    devClickDebug('saveQuote:set in-flight ref true, entering try', {});
    try {
      const client = clients.find((c) => c.id === quoteData.clientId);
      const clientEmail = client?.email || '';
      const existingQuote = quotes.find((q) => q.id === quoteData.id);

      const rawCleanData = Object.fromEntries(
        Object.entries(quoteData).filter(([_, value]) => value !== undefined)
      );
      const {
        clientId,
        clientEmail: _ce,
        customQuoteData: _cqd2,
        quoteNumber: _qn,
        editedOn: _eo,
        markupType,
        overheadType,
        taxRate,
        taxEnabled,
        finalTaxEnabled,
        ...cleanQuoteData
      } = rawCleanData;
      cleanQuoteData.client_id = clientId || null;
      if (markupType !== undefined && markupType !== null) cleanQuoteData.markup_type = markupType;
      if (taxRate !== undefined) cleanQuoteData.tax_rate = taxRate;

      const materialsToSave = isEditingQuote
        ? quoteData.materials || []
        : existingQuote?.materials || quoteData.materials || [];

      const notesPlainText = Array.isArray(notes)
        ? notes.map((n) => (n && n.text ? String(n.text) : '')).filter(Boolean).join('\n')
        : typeof notes === 'string'
          ? notes
          : '';

      const totalsForDb = calculateQuoteTotals({
        ...quoteData,
        materials: materialsToSave,
      });

      const newQuote = {
        ...cleanQuoteData,
        materials: materialsToSave,
        custom_quote_data: {
          ...(quoteData.customQuoteData || quoteData.custom_quote_data || customQuoteData || {}),
          materials: materialsToSave,
          notes,
          overheadType: overheadType || quoteData.overheadType || 'amount',
          ...(taxEnabled !== undefined ? { taxEnabled } : {}),
          ...(finalTaxEnabled !== undefined ? { finalTaxEnabled: finalTaxEnabled === true } : {}),
        },
        notes: notesPlainText,
        quote_number:
          quoteData.quoteNumber || quoteData.quote_number || `Quote #${Math.floor(Math.random() * 10000)}`,
        client_email: clientEmail,
        user_id: user.id,
        total: totalsForDb.total,
        created_at: quoteData.created_at || quoteData.createdAt || new Date().toISOString(),
        updated_at: new Date().toISOString(),
        edited_on: new Date().toISOString(),
        status: quoteData.status || 'draft',
        ...(existingQuote && {
          ...((existingQuote.invoice_data || existingQuote.invoiceData) && {
            invoice_data: existingQuote.invoice_data || existingQuote.invoiceData,
          }),
          ...((existingQuote.invoice_generated_at || existingQuote.invoiceGeneratedAt) && {
            invoice_generated_at:
              existingQuote.invoice_generated_at || existingQuote.invoiceGeneratedAt,
          }),
        }),
        ...(quoteData.id && {
          pdf_data: null,
          pdf_generated_at: null,
          pdf_reference: null,
        }),
      };

      const dbRow = normalizeQuoteNumericFields(pickQuoteDbColumns(newQuote));

      const persist = async () => {
        devClickDebug('saveQuote:persist() start', { isUpdate: !!quoteData.id });
        if (quoteData.id) {
          devClickDebug('saveQuote:before quotes.update', { quoteId: quoteData.id });
          const { error: updateErr } = await supabase.from('quotes').update(dbRow).eq('id', quoteData.id);
          if (updateErr) throw updateErr;
          devClickDebug('saveQuote:after quotes.update', { quoteId: quoteData.id });
          return { mode: 'update', quoteId: quoteData.id };
        }
        const insertPayload = { ...dbRow };
        delete insertPayload.id;
        devClickDebug('saveQuote:before quotes.insert', {});
        const { data: insertedQuote, error: insertErr } = await supabase
          .from('quotes')
          .insert(insertPayload)
          .select()
          .single();
        if (insertErr) throw insertErr;
        if (!insertedQuote?.id) throw new Error('Quote insert did not return an id');
        devClickDebug('saveQuote:after quotes.insert', { newId: insertedQuote?.id });
        return { mode: 'insert', insertedQuote };
      };

      devClickDebug('saveQuote:before runSupabaseAuthedOperation', {});
      const result = await runSupabaseAuthedOperation({
        name: 'saveQuote',
        supabaseClient: supabase,
        timeoutMs: 55000,
        retries: 1,
        retryDelayMs: 800,
        onBeforeRetry: () => refreshSupabaseSessionOnce(supabase),
        fn: persist,
      });
      devClickDebug('saveQuote:after runSupabaseAuthedOperation', { ok: result.ok });

      if (!result.ok) {
        devReliabilityLog('saveQuote:failed', { userMessage: result.userMessage });
        devClickDebug('saveQuote:result !ok after operation', { userMessage: result.userMessage });
        alert(result.userMessage || toUserMessage(result.error, 'Could not save your invoice. Please try again.'));
        return false;
      }

      const payload = result.data;
      if (payload.mode === 'update') {
        const merged = normalizeQuoteFromDb({
          ...existingQuote,
          ...newQuote,
          id: quoteData.id,
        });
        setQuotes((prev) => prev.map((q) => (q.id === quoteData.id ? merged : q)));
      } else {
        const quoteWithId = normalizeQuoteFromDb(payload.insertedQuote);
        setQuotes((prev) => [quoteWithId, ...prev]);
      }

      setShowCreateQuote(false);
      setShowEditQuote(false);
      setShowReviewQuote(false);
      setIsEditingQuote(false);
      setIsInReviewMode(false);
      resetQuoteForm(false);
      devReliabilityLog('saveQuote:done', { ms: result.durationMs });
      devClickDebug('saveQuote:success return true', { ms: result.durationMs });
      return true;
    } catch (error) {
      devReliabilityLog('saveQuote:catch', { err: String(error) });
      devClickDebug('saveQuote:catch', { err: String(error) });
      alert(toUserMessage(error, 'Could not save your invoice. Please try again.'));
      return false;
    } finally {
      clearQuoteSaveLoadingState();
      devClickDebug('saveQuote:finally (in-flight ref cleared)', {});
    }
  };

  // Delete quote
  const deleteQuote = async (quoteId) => {
    if (!user) return;
    
    try {
      await runAuthedWrite('dashboard:deleteQuote', async () => {
        const { error } = await supabase.from('quotes').delete().eq('id', quoteId);
        if (error) throw error;
      });
      
      // Update local state
      const updatedQuotes = quotes.filter(q => q.id !== quoteId);
      setQuotes(updatedQuotes);
      
      // Close the client quotes modal after deleting
      setShowClientQuotes(false);
    } catch (error) {
      devError('Error deleting quote:', error);
      alert(toUserMessage(error, 'Could not delete quote. Please try again.'));
    }
  };

  // Edit quote
  const editQuote = (quote) => {
    const q = normalizeQuoteFromDb(quote);
    devLog('🔍 [editQuote] Editing quote:', q.id);
    devLog('🔍 [editQuote] Quote notes:', q.notes);
    devLog('🔍 [editQuote] Quote customQuoteData:', q.customQuoteData);
    devLog('🔍 [editQuote] Quote customQuoteData.notes:', q.customQuoteData?.notes);

    setSelectedQuote(q);
    setIsEditingQuote(true);

    // CRITICAL FIX: Load ALL customQuoteData fields from mobile app
    const customQuoteData = q.customQuoteData || {};

    setQuoteForm({
      id: q.id,
      clientId: q.clientId,
      materials: q.materials || [],
      labour: q.labour || { entries: [], total: 0 },
      markup: q.markup ?? '',
      markupType: q.markupType || 'percentage',
      overheadType: q.overheadType || q.customQuoteData?.overheadType || 'amount',
      overhead: q.overhead ?? '',
      taxEnabled: q.taxEnabled !== false,
      finalTaxEnabled: q.finalTaxEnabled === true || customQuoteData.finalTaxEnabled === true,
      taxRate:
        customQuoteData.taxRate != null && customQuoteData.taxRate !== ''
          ? customQuoteData.taxRate
          : q.taxRate != null && q.taxRate !== ''
            ? q.taxRate
            : '',
      description: q.description || '',
      editedOn: new Date().toISOString(),
      quoteNumber: customQuoteData.quoteNumber || q.quoteNumber || '',
      // CRITICAL FIX: Load ALL customQuoteData fields
      customQuoteData: {
        ...customQuoteData,
        // Ensure these fields are loaded from mobile app
        companyName: customQuoteData.companyName || '',
        companyPhone: customQuoteData.companyPhone || '',
        streetAddress: customQuoteData.streetAddress || '',
        city: customQuoteData.city || '',
        province: customQuoteData.province || '',
        postalCode: customQuoteData.postalCode || '',
        companyEmail: customQuoteData.companyEmail || '',
        clientName: customQuoteData.clientName || q.clientName || '',
        quoteDate: customQuoteData.quoteDate || new Date().toISOString().split('T')[0],
        descriptionHeading: customQuoteData.descriptionHeading || 'To complete the following:',
        scopeOfWork: customQuoteData.scopeOfWork || [],
        notesBulletPoints: customQuoteData.notesBulletPoints || [],
        notesHeaderTitle: customQuoteData.notesHeaderTitle || '',
        showNotesSection: customQuoteData.showNotesSection || false,
        showLogo: customQuoteData.showLogo !== undefined ? customQuoteData.showLogo : true,
        showSignature: customQuoteData.showSignature !== undefined ? customQuoteData.showSignature : false,
        paymentTerms: customQuoteData.paymentTerms || []
      }
    });
    
    // Restore notes and payment terms from the quote's custom data or separate fields
    const existingCustomData = q.customQuoteData || {};

    // Check for notes in customQuoteData first, then in separate notes field
    let savedNotes = null;
    devLog('🔍 [editQuote] Checking for notes...');
    devLog('🔍 [editQuote] existingCustomData.notes:', existingCustomData.notes);
    devLog('🔍 [editQuote] existingCustomData.notes is array:', Array.isArray(existingCustomData.notes));
    devLog('🔍 [editQuote] quote.notes:', q.notes);
    devLog('🔍 [editQuote] quote.notes is array:', Array.isArray(q.notes));

    if (existingCustomData.notes && Array.isArray(existingCustomData.notes)) {
      savedNotes = existingCustomData.notes;
      devLog('✅ [editQuote] Found notes in customQuoteData:', savedNotes);
    } else if (q.notes && Array.isArray(q.notes)) {
      savedNotes = q.notes;
      devLog('✅ [editQuote] Found notes in quote.notes:', savedNotes);
    } else if (typeof q.notes === 'string' && q.notes.trim()) {
      savedNotes = q.notes.split('\n').map((text, i) => ({ id: String(i + 1), text }));
      devLog('✅ [editQuote] Parsed notes from quote.notes string:', savedNotes);
    } else {
      devLog('⚠️ [editQuote] No notes found in either location');
    }
    
    if (savedNotes) {
      setNotes(savedNotes);
      devLog('✅ [editQuote] Setting notes to:', savedNotes);
    } else {
      setNotes([{ id: '1', text: '' }]);
      devLog('⚠️ [editQuote] No saved notes found, using default');
    }
    
    // Check for payment terms in customQuoteData first, then in separate paymentTerms field
    let savedPaymentTerms = null;
    if (existingCustomData.paymentTerms && Array.isArray(existingCustomData.paymentTerms)) {
      savedPaymentTerms = existingCustomData.paymentTerms;
      devLog('✅ [editQuote] Found payment terms in customQuoteData:', savedPaymentTerms);
    } else if (q.paymentTerms && Array.isArray(q.paymentTerms)) {
      savedPaymentTerms = q.paymentTerms;
      devLog('✅ [editQuote] Found payment terms in quote.paymentTerms:', savedPaymentTerms);
    } else {
      devLog('⚠️ [editQuote] No payment terms found in either location');
    }
    
    if (savedPaymentTerms) {
      setPaymentTerms(savedPaymentTerms);
      devLog('✅ [editQuote] Setting payment terms to:', savedPaymentTerms);
    } else {
      setPaymentTerms([{ id: '1', text: '' }]);
      devLog('⚠️ [editQuote] No saved payment terms found, using default');
    }
    
    setShowEditQuote(true);
    setShowClientQuotes(false);
    setTimeout(() => {
      devLog('[editQuote] After set, quoteForm:', quoteForm, 'isEditingQuote:', isEditingQuote);
    }, 100);
  };

  // Initialize custom quote data when opening PDF template modal
  const initializeCustomQuoteData = (quote) => {
    const client = clients.find(c => c.id === quote.clientId);
    
    // Preserve existing custom quote data if it exists, otherwise use defaults
    const existingCustomData = quote.customQuoteData || {};
    
    // Restore logo from stored field or existing custom data
    const storedLogo = quote.storedCompanyLogo || existingCustomData.companyLogo || settings.companyLogo || null;
    
    setCustomQuoteData({
      // Company Information - preserve existing or use settings defaults
      companyName: existingCustomData.companyName || settings.companyName || '',
              streetAddress: existingCustomData.streetAddress || settings.streetAddress || '',
        city: existingCustomData.city || settings.city || '',
        province: existingCustomData.province || settings.province || '',
        postalCode: existingCustomData.postalCode || settings.postalCode || '',
      companyPhone: existingCustomData.companyPhone || settings.companyPhone || '',
      companyEmail: existingCustomData.companyEmail || settings.companyEmail || '',
      companyWebsite: existingCustomData.companyWebsite || settings.websiteUrl || '',
      companyLogo: storedLogo, // Use stored logo
      logoSize: existingCustomData.logoSize || settings.logoSize || 60, // default size in px
      
      // Quote Details - preserve existing or use defaults
      quoteNumber: existingCustomData.quoteNumber || quote.quoteNumber || `Quote #${Math.floor(Math.random() * 10000)}`,
      quoteDate: existingCustomData.quoteDate || new Date().toLocaleDateString(),
      clientName: existingCustomData.clientName || client?.name || '',
      
      // CRITICAL FIX: Also populate descriptionHeading field for PDF generation compatibility
      descriptionHeading: existingCustomData.descriptionHeading || quote.descriptionHeading || 'To complete the following:',
      
      // Design & Styling - preserve existing or use defaults
      templateStyle: existingCustomData.templateStyle || 'modern',
      primaryColor: existingCustomData.primaryColor || '#0891b2',
      secondaryColor: existingCustomData.secondaryColor || '#0e7490',
      fontFamily: existingCustomData.fontFamily || 'Arial',
      showLogo: existingCustomData.showLogo !== undefined ? existingCustomData.showLogo : true,
      showBorder: existingCustomData.showBorder !== undefined ? existingCustomData.showBorder : true,
      showShadows: existingCustomData.showShadows !== undefined ? existingCustomData.showShadows : true,
      
      // Content - preserve existing scope of work or use default
      scopeOfWork: existingCustomData.scopeOfWork && existingCustomData.scopeOfWork.length > 0 
        ? existingCustomData.scopeOfWork.map(section => ({
            ...section,
            // Ensure both heading and title fields exist for mobile app compatibility
            heading: section.heading || section.title || 'To complete the following:',
            title: section.title || section.heading || 'To complete the following:'
          }))
        : [
            {
              id: Date.now(),
              heading: 'To complete the following:',
              title: 'To complete the following:', // Add title field for mobile app compatibility
              points: [
                { id: Date.now() + 1, text: '' }
              ]
            }
          ],
      
      // CRITICAL FIX: Also populate descriptionPoints field for PDF generation compatibility
      descriptionPoints: (() => {
        // PRIORITY 1: Use existing descriptionPoints if available
        if (existingCustomData.descriptionPoints && Array.isArray(existingCustomData.descriptionPoints) && existingCustomData.descriptionPoints.length > 0) {
          devLog('🔍 [initializeCustomQuoteData] Using existing descriptionPoints data:', existingCustomData.descriptionPoints.length, 'points');
          return existingCustomData.descriptionPoints;
        }
        
        // PRIORITY 2: Convert from scopeOfWork if available
        if (existingCustomData.scopeOfWork && existingCustomData.scopeOfWork.length > 0 && 
            existingCustomData.scopeOfWork[0].points && existingCustomData.scopeOfWork[0].points.length > 0) {
          devLog('🔍 [initializeCustomQuoteData] Converting scopeOfWork to descriptionPoints:', existingCustomData.scopeOfWork[0].points.length, 'points');
          return existingCustomData.scopeOfWork[0].points.map(point => point.text || '');
        }
        
        // PRIORITY 3: Convert from root level scopeOfWork if available
        if (quote.scopeOfWork && quote.scopeOfWork.length > 0 && 
            quote.scopeOfWork[0].points && quote.scopeOfWork[0].points.length > 0) {
          devLog('🔍 [initializeCustomQuoteData] Converting root level scopeOfWork to descriptionPoints:', quote.scopeOfWork[0].points.length, 'points');
          return quote.scopeOfWork[0].points.map(point => point.text || '');
        }
        
        // PRIORITY 4: Use root level descriptionPoints if available
        if (quote.descriptionPoints && Array.isArray(quote.descriptionPoints) && quote.descriptionPoints.length > 0) {
          devLog('🔍 [initializeCustomQuoteData] Using root level descriptionPoints data:', quote.descriptionPoints.length, 'points');
          return quote.descriptionPoints;
        }
        
        // PRIORITY 5: Default empty description points
        devLog('🔍 [initializeCustomQuoteData] No descriptionPoints found, using default');
        return [''];
      })(),
      
      notes: existingCustomData.notes || null, // Will be handled by separate state
      
      // Notes Section with Header and Bullet Points (like invoice)
      showNotesSection: existingCustomData.showNotesSection || false,
      notesHeaderTitle: existingCustomData.notesHeaderTitle || '',
      notesBulletPoints: existingCustomData.notesBulletPoints && existingCustomData.notesBulletPoints.length > 0 
        ? existingCustomData.notesBulletPoints.map(point => ({ id: Date.now() + Math.random(), text: point.text }))
        : [{ id: Date.now(), text: '' }],
      
      // Additional Fields - preserve existing or use defaults
      validUntil: existingCustomData.validUntil || '',
      termsAndConditions: existingCustomData.termsAndConditions || '',
      signature: existingCustomData.signature || '',
      showMaterialsBreakdown: existingCustomData.showMaterialsBreakdown !== undefined ? existingCustomData.showMaterialsBreakdown : true,
      showLabourBreakdown: existingCustomData.showLabourBreakdown !== undefined ? existingCustomData.showLabourBreakdown : true,
      showTaxBreakdown: existingCustomData.showTaxBreakdown !== undefined ? existingCustomData.showTaxBreakdown : true,
              showSignature: existingCustomData.showSignature || false,
      currency: existingCustomData.currency || 'CAD',
      taxRate:
        existingCustomData.taxRate != null && existingCustomData.taxRate !== ''
          ? existingCustomData.taxRate
          : '',
      taxLabel: existingCustomData.taxLabel || 'Tax'
    });
    
    // Notes and payment terms are handled separately in handleOpenCustomQuoteModal
    // to avoid conflicts and ensure proper loading logic
  };

  // Custom Quote Template Functions
  const handleCustomQuoteChange = (field, value) => {
    setCustomQuoteData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // CRITICAL FIX: Sync descriptionPoints with scopeOfWork data
  const syncDescriptionPoints = useCallback(() => {
    setCustomQuoteData(prev => {
      const newDescriptionPoints = prev.scopeOfWork && prev.scopeOfWork.length > 0 && prev.scopeOfWork[0].points
        ? prev.scopeOfWork[0].points.map(point => point.text || '')
        : [''];
      
      devLog('🔍 [syncDescriptionPoints] Syncing descriptionPoints:', newDescriptionPoints);
      
      return {
        ...prev,
        descriptionPoints: newDescriptionPoints
      };
    });
  }, []);

  // CRITICAL FIX: Update scopeOfWork and sync descriptionPoints
  const updateScopeSection = useCallback((sectionId, field, value) => {
    devLog('🔍 [updateScopeSection] Called with:', { sectionId, field, value });
    
    setCustomQuoteData(prev => {
      const updated = {
        ...prev,
        scopeOfWork: prev.scopeOfWork.map(section => 
          section.id === sectionId 
            ? { 
                ...section, 
                [field]: value,
                // When heading is updated, also update title for mobile app compatibility
                ...(field === 'heading' && { title: value }),
                // When title is updated, also update heading for website compatibility
                ...(field === 'title' && { heading: value })
              }
            : section
        )
      };
      
      // Sync descriptionPoints after updating scopeOfWork
      setTimeout(() => syncDescriptionPoints(), 0);
      
      return updated;
    });
  }, [syncDescriptionPoints]);

  const addScopeSection = useCallback(() => {
    const newSection = {
      id: Date.now(),
      heading: '',
      title: '', // Add title field for mobile app compatibility
      points: [{ id: Date.now() + 1, text: '' }]
    };
    setCustomQuoteData(prev => {
      const updated = {
        ...prev,
        scopeOfWork: [...prev.scopeOfWork, newSection]
      };
      
      // Sync descriptionPoints after adding scope section
      setTimeout(() => syncDescriptionPoints(), 0);
      
      return updated;
    });
  }, [syncDescriptionPoints]);

  const removeScopeSection = useCallback((sectionId) => {
    setCustomQuoteData(prev => {
      const updated = {
        ...prev,
        scopeOfWork: prev.scopeOfWork.filter(section => section.id !== sectionId)
      };
      
      // Sync descriptionPoints after removing scope section
      setTimeout(() => syncDescriptionPoints(), 0);
      
      return updated;
    });
  }, [syncDescriptionPoints]);



  const addScopePoint = useCallback((sectionId) => {
    setCustomQuoteData(prev => {
      const updated = {
        ...prev,
        scopeOfWork: prev.scopeOfWork.map(section => 
          section.id === sectionId 
            ? { ...section, points: [...section.points, { id: Date.now(), text: '' }] }
            : section
        )
      };
      
      // Sync descriptionPoints after adding scope point
      setTimeout(() => syncDescriptionPoints(), 0);
      
      return updated;
    });
  }, [syncDescriptionPoints]);

  const removeScopePoint = useCallback((sectionId, pointId) => {
    setCustomQuoteData(prev => {
      const updated = {
        ...prev,
        scopeOfWork: prev.scopeOfWork.map(section => 
          section.id === sectionId 
            ? { ...section, points: section.points.filter(point => point.id !== pointId) }
            : section
        )
      };
      
      // Sync descriptionPoints after removing scope point
      setTimeout(() => syncDescriptionPoints(), 0);
      
      return updated;
    });
  }, [syncDescriptionPoints]);

  const updateScopePoint = useCallback((sectionId, pointId, text) => {
    setCustomQuoteData(prev => {
      const updated = {
        ...prev,
        scopeOfWork: prev.scopeOfWork.map(section => 
          section.id === sectionId 
            ? { 
                ...section, 
                points: section.points.map(point => 
                  point.id === pointId 
                    ? { ...point, text }
                    : point
                )
              }
            : section
        )
      };
      
      // Sync descriptionPoints after updating scope point
      setTimeout(() => syncDescriptionPoints(), 0);
      
      return updated;
    });
  }, [syncDescriptionPoints]);

  // SEPARATE STATE FOR NOTES AND PAYMENT TERMS
  const [notes, setNotes] = useState([{ id: '1', text: '' }]);
  const [, setPaymentTerms] = useState([{ id: '1', text: '' }]);


  // eslint-disable-next-line no-unused-vars
  const handleOpenCustomQuoteModal = (quote) => {
    devLog('🔍 [handleOpenCustomQuoteModal] Opening modal for quote:', quote.id);
    devLog('🔍 [handleOpenCustomQuoteModal] Quote notes:', quote.notes);
    devLog('🔍 [handleOpenCustomQuoteModal] Quote customQuoteData:', quote.customQuoteData);
    
    // Initialize the rest of the custom quote data FIRST
    initializeCustomQuoteData(quote);
    
    // CRITICAL FIX: Sync descriptionPoints after initialization
    setTimeout(() => syncDescriptionPoints(), 0);
    
    // CLEAN APPROACH: Simple and bulletproof
    const existingCustomData = quote.customQuoteData || {};
    
    // SIMPLE NOTES LOADING
    let savedNotes = null;
    let notesFound = false;
    
    // Check ALL possible locations for notes
    if (existingCustomData.notes && Array.isArray(existingCustomData.notes)) {
      savedNotes = existingCustomData.notes;
      notesFound = true;
    } else if (quote.notes && Array.isArray(quote.notes)) {
      savedNotes = quote.notes;
      notesFound = true;
    } else if (existingCustomData.notesText) {
      savedNotes = [{ id: '1', text: existingCustomData.notesText }];
      notesFound = true;
    } else if (quote.notesText) {
      savedNotes = [{ id: '1', text: quote.notesText }];
      notesFound = true;
    }
    
    // Set notes
    if (notesFound && savedNotes) {
      devLog('✅ [handleOpenCustomQuoteModal] Setting notes to:', savedNotes);
      setNotes(savedNotes);
    } else {
      devLog('⚠️ [handleOpenCustomQuoteModal] No notes found, using default');
      setNotes([{ id: '1', text: '' }]);
    }
    

    
    // Load notes section data (header and bullet points) if it exists
    if (existingCustomData.showNotesSection !== undefined) {
      devLog('✅ [handleOpenCustomQuoteModal] Loading notes section data');
      setCustomQuoteData(prev => ({
        ...prev,
        showNotesSection: existingCustomData.showNotesSection,
        notesHeaderTitle: existingCustomData.notesHeaderTitle || '',
        notesBulletPoints: existingCustomData.notesBulletPoints && existingCustomData.notesBulletPoints.length > 0 
          ? existingCustomData.notesBulletPoints.map(point => ({ id: Date.now() + Math.random(), text: point.text }))
          : [{ id: Date.now(), text: '' }]
      }));
    } else {
      devLog('⚠️ [handleOpenCustomQuoteModal] No notes section data found, using defaults');
      setCustomQuoteData(prev => ({
        ...prev,
        showNotesSection: false,
        notesHeaderTitle: '',
        notesBulletPoints: [{ id: Date.now(), text: '' }]
      }));
    }
    
    setSelectedQuoteForCustomTemplate({
      ...quote,
      markup: quote.markup || '',
      markupType: quote.markupType || 'percentage',
      overheadType: quote.overheadType || quote.customQuoteData?.overheadType || 'amount',
      overhead: quote.overhead || '',
      labour: quote.labour || { entries: [], total: 0 }
    });
    
    setShowCustomQuoteModal(true);
    
    // ALL DEBUGGING AT THE BOTTOM - Easy to read

  };

  const handleGenerateCustomPDF = async () => {
    if (!selectedQuoteForCustomTemplate) {
      alert('No quote selected for PDF generation.');
      return;
    }
    if (customPdfGenInFlightRef.current) return;
    customPdfGenInFlightRef.current = true;

    devReliabilityLog('customPdf:start', { quoteId: selectedQuoteForCustomTemplate.id });

    const templateSnapshot = selectedQuoteForCustomTemplate;

    try {
      devLog('🚨 [handleGenerateCustomPDF] AGGRESSIVE DEBUGGING START 🚨');
      devLog('[handleGenerateCustomPDF] Current notes state:', notes);
      devLog('[handleGenerateCustomPDF] Notes length:', notes?.length);
      devLog('[handleGenerateCustomPDF] Notes with content:', notes?.filter(note => note.text && note.text.trim()));
      
      // Use current notes state
      const notesText = notes.filter(note => note.text && note.text.trim()).map(note => note.text).join('\n');
      
      devLog('[handleGenerateCustomPDF] Processed notesText:', notesText);
      devLog('🔍 [handleGenerateCustomPDF] Current customQuoteData.descriptionPoints:', customQuoteData.descriptionPoints);
      devLog('🔍 [handleGenerateCustomPDF] Current customQuoteData.scopeOfWork:', customQuoteData.scopeOfWork);
      
      // Prepare customQuoteData for PDF (keep companyLogo for PDF generation)
      const sanitizedCustomQuoteData = { ...customQuoteData };
      
      devLog('🔍 [handleGenerateCustomPDF] sanitizedCustomQuoteData.descriptionPoints:', sanitizedCustomQuoteData.descriptionPoints);
      devLog('🔍 [handleGenerateCustomPDF] sanitizedCustomQuoteData.scopeOfWork:', sanitizedCustomQuoteData.scopeOfWork);
      
      // CRITICAL FIX: Preserve original materials from the quote
      devLog('🔧 CRITICAL FIX: Preserving original materials from selectedQuoteForCustomTemplate');
      devLog('🔧 Original materials:', templateSnapshot.materials);
      devLog('🔧 Original customQuoteData.materials:', templateSnapshot.customQuoteData?.materials);
      
      // FIXED: Save data in the EXACT format that mobile app expects
      const updatedQuote = {
        ...templateSnapshot,
        markup: templateSnapshot.markup || '',
        markupType: templateSnapshot.markupType || 'percentage',
        overheadType:
          templateSnapshot.overheadType || templateSnapshot.customQuoteData?.overheadType || 'amount',
        overhead: templateSnapshot.overhead || '',
        labour: templateSnapshot.labour || { entries: [], total: 0 },
        // CRITICAL FIX: Preserve materials in both locations
        materials: templateSnapshot.materials || [],
        // FIXED: Save in customQuoteData structure EXACTLY like mobile app expects
        customQuoteData: {
          // Company information (EXACTLY like mobile app expects)
          companyName: sanitizedCustomQuoteData.companyName || 'Devello',
          companyPhone: sanitizedCustomQuoteData.companyPhone || '',
          streetAddress: sanitizedCustomQuoteData.streetAddress || '',
          city: sanitizedCustomQuoteData.city || '',
          province: sanitizedCustomQuoteData.province || '',
          postalCode: sanitizedCustomQuoteData.postalCode || '',
          companyEmail: sanitizedCustomQuoteData.companyEmail || '',
          
          // Client information (EXACTLY like mobile app expects)
          clientName: sanitizedCustomQuoteData.clientName || templateSnapshot.clientName || '',
          clientAddress: sanitizedCustomQuoteData.clientAddress || '',
          clientCity: sanitizedCustomQuoteData.clientCity || '',
          clientProvince: sanitizedCustomQuoteData.clientProvince || '',
          clientPostalCode: sanitizedCustomQuoteData.clientPostalCode || '',
          
          // Quote details (EXACTLY like mobile app expects)
          quoteNumber: sanitizedCustomQuoteData.quoteNumber || templateSnapshot.quoteNumber,
          quoteDate: sanitizedCustomQuoteData.quoteDate || new Date().toISOString().split('T')[0],
          descriptionHeading: sanitizedCustomQuoteData.descriptionHeading || 'To complete the following:',
          taxRate:
            sanitizedCustomQuoteData.taxRate != null && sanitizedCustomQuoteData.taxRate !== ''
              ? sanitizedCustomQuoteData.taxRate
              : templateSnapshot.taxRate != null &&
                  templateSnapshot.taxRate !== ''
                ? templateSnapshot.taxRate
                : '',
          
          // CRITICAL FIX: Preserve materials in customQuoteData (mobile app looks here)
          materials: templateSnapshot.materials || templateSnapshot.customQuoteData?.materials || [],
          
          // CRITICAL FIX: Also save descriptionPoints field for PDF generation compatibility
          descriptionPoints: sanitizedCustomQuoteData.descriptionPoints || [],
          
          // FIXED: Save scope of work in the format mobile app expects
          scopeOfWork: [{
            heading: customQuoteData.scopeOfWork[0]?.heading || sanitizedCustomQuoteData.descriptionHeading || 'To complete the following:',
            title: customQuoteData.scopeOfWork[0]?.heading || sanitizedCustomQuoteData.descriptionHeading || 'To complete the following:',
            points: sanitizedCustomQuoteData.descriptionPoints ? sanitizedCustomQuoteData.descriptionPoints.map(point => ({
              text: point || ''
            })) : []
          }],
          
          // FIXED: Save notes section data (EXACTLY like mobile app expects)
          notesBulletPoints: sanitizedCustomQuoteData.notesBulletPoints || [],
          notesHeaderTitle: sanitizedCustomQuoteData.notesHeaderTitle || '',
          showNotesSection: sanitizedCustomQuoteData.showNotesSection || false,
          
          // Payment terms (EXACTLY like mobile app expects)
          paymentTerms: sanitizedCustomQuoteData.paymentTerms || [],
          
          // Design options (EXACTLY like mobile app expects)
          showLogo: sanitizedCustomQuoteData.showLogo !== undefined ? sanitizedCustomQuoteData.showLogo : true,
          showQtyUnit: sanitizedCustomQuoteData.showQtyUnit !== undefined ? sanitizedCustomQuoteData.showQtyUnit : true,
          showSignature: sanitizedCustomQuoteData.showSignature !== undefined ? sanitizedCustomQuoteData.showSignature : false,
          
          // Notes (EXACTLY like mobile app expects)
          notes: notes
        },
        
        // FIXED: Also save at root level for backward compatibility AND mobile app compatibility
        // The mobile app looks at BOTH customQuoteData AND root level
        quoteNumber: sanitizedCustomQuoteData.quoteNumber || templateSnapshot.quoteNumber,
        description: templateSnapshot.description || '',
        taxRate:
          sanitizedCustomQuoteData.taxRate != null && sanitizedCustomQuoteData.taxRate !== ''
            ? sanitizedCustomQuoteData.taxRate
            : templateSnapshot.taxRate != null &&
                templateSnapshot.taxRate !== ''
              ? templateSnapshot.taxRate
              : '',
        descriptionHeading: sanitizedCustomQuoteData.descriptionHeading || 'To complete the following:',
        
        // CRITICAL FIX: Also save descriptionPoints field at root level for PDF generation compatibility
        descriptionPoints: sanitizedCustomQuoteData.descriptionPoints || [],
        
        // FIXED: Save scope of work at root level too (mobile app looks here first)
        scopeOfWork: [{
          heading: customQuoteData.scopeOfWork[0]?.heading || sanitizedCustomQuoteData.descriptionHeading || 'To complete the following:',
          title: customQuoteData.scopeOfWork[0]?.heading || sanitizedCustomQuoteData.descriptionHeading || 'To complete the following:',
          points: sanitizedCustomQuoteData.descriptionPoints ? sanitizedCustomQuoteData.descriptionPoints.map(point => ({
            text: point || ''
          })) : []
        }],
        
        // FIXED: Save notes section data at root level too (mobile app looks here first)
        notesBulletPoints: sanitizedCustomQuoteData.notesBulletPoints || [],
        notesHeaderTitle: sanitizedCustomQuoteData.notesHeaderTitle || '',
        showNotesSection: sanitizedCustomQuoteData.showNotesSection || false,
        
        // Notes (EXACTLY like mobile app expects)
        notes: notes,
        
        // Mark as ready for PDF generation
        pdfDataReady: true,
        
        // Update timestamp
        updatedAt: new Date().toISOString()
      };
      
      devLog('🔍 [handleGenerateCustomPDF] Saving scopeOfWork with points:', sanitizedCustomQuoteData.descriptionPoints ? sanitizedCustomQuoteData.descriptionPoints.map(point => ({ text: point || '' })) : []);
      devLog('🔍 [handleGenerateCustomPDF] Final updatedQuote.customQuoteData.scopeOfWork:', updatedQuote.customQuoteData.scopeOfWork);
      
      const supabaseCustomQuoteData = { ...updatedQuote.customQuoteData };
      const logoToStore = supabaseCustomQuoteData.companyLogo;
      
      if ('companyLogo' in supabaseCustomQuoteData) {
        delete supabaseCustomQuoteData.companyLogo;
      }

      const { customQuoteData: _cqd, ...updatedQuoteClean } = updatedQuote;
      const updateData = {
        ...updatedQuoteClean,
        custom_quote_data: supabaseCustomQuoteData,
        materials: updatedQuote.materials || [],
        scopeOfWork: updatedQuote.scopeOfWork || [],
        notesBulletPoints: updatedQuote.notesBulletPoints || [],
        notesHeaderTitle: updatedQuote.notesHeaderTitle || '',
        showNotesSection: updatedQuote.showNotesSection || false,
        notes: updatedQuote.notes || [],
        // Add timestamp to ensure the save is recent
        lastSaved: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      devLog('🔧 [handleGenerateCustomPDF] Before save - notes:', notes);
      devLog('🔧 [handleGenerateCustomPDF] Before save - updateData.custom_quote_data.notes:', updateData.custom_quote_data.notes);
      
      devLog('[handleGenerateCustomPDF] Saving updateData:', updateData);
      devLog('[handleGenerateCustomPDF] Saving notes:', updateData.notes);
      devLog('[handleGenerateCustomPDF] Saving custom_quote_data.notes:', updateData.custom_quote_data.notes);
      devLog('[handleGenerateCustomPDF] Notes type:', typeof updateData.notes);
      devLog('[handleGenerateCustomPDF] Notes is array:', Array.isArray(updateData.notes));
      
      // Add logo if it exists and is not too large (should be compressed already)
      if (logoToStore && logoToStore.length < 1000000) { // Under 1MB
        updateData.storedCompanyLogo = logoToStore;
      } else if (logoToStore) {
        devWarn('Logo too large, not storing. Size:', logoToStore.length, 'bytes');
      }
      
      const saveRes = await runSupabaseAuthedOperation({
        name: 'customPdf:saveQuote',
        supabaseClient: supabase,
        timeoutMs: 55000,
        retries: 1,
        fn: async () => {
          const { error } = await supabase.from('quotes').update(updateData).eq('id', templateSnapshot.id);
          if (error) throw error;
        },
      });
      if (!saveRes.ok) {
        alert(saveRes.userMessage);
        return;
      }

      const updatedQuoteWithLogo = {
        ...updatedQuote,
        storedCompanyLogo: logoToStore && logoToStore.length < 1000000 ? logoToStore : null
      };
      
      const updatedQuotes = quotes.map(q =>
        q.id === templateSnapshot.id ? updatedQuoteWithLogo : q
      );
      setQuotes(updatedQuotes);
      
      devReliabilityLog('customPdf:phase:generatePdf', { quoteId: templateSnapshot.id });
      await generatePDF(updatedQuote, '', notesText, customQuoteData.showBreakdown);
      
      try {
        const { data: savedData } = await supabase.from('quotes').select('*').eq('id', templateSnapshot.id).single();
        if (savedData) {
          devLog('✅ [handleGenerateCustomPDF] VERIFICATION - Saved notes:', savedData.notes);
          devLog('✅ [handleGenerateCustomPDF] VERIFICATION - Saved custom_quote_data.notes:', savedData.custom_quote_data?.notes);
          devLog('✅ [handleGenerateCustomPDF] VERIFICATION - Saved custom_quote_data.materials:', savedData.custom_quote_data?.materials);
          devLog('✅ [handleGenerateCustomPDF] VERIFICATION - Saved root materials:', savedData.materials);
        }
      } catch (verificationError) {
        devError('❌ [handleGenerateCustomPDF] Verification failed:', verificationError);
      }
      
      // AGGRESSIVE VERIFICATION - Check what was actually saved
      devLog('🚨 [handleGenerateCustomPDF] AGGRESSIVE VERIFICATION AFTER SAVE 🚨');
      devLog('[handleGenerateCustomPDF] Updated quote with logo:', updatedQuoteWithLogo);
      devLog('[handleGenerateCustomPDF] Updated quote notes:', updatedQuoteWithLogo.notes);
      devLog('[handleGenerateCustomPDF] Updated quote customQuoteData:', updatedQuoteWithLogo.customQuoteData);
      devLog('[handleGenerateCustomPDF] Updated quote customQuoteData.notes:', updatedQuoteWithLogo.customQuoteData?.notes);
      
      // Notify user of successful logo enhancement
      devLog('✅ Quote logo enhancement completed - Logo should now be properly stored and displayed in PDFs');
      devLog('✅ Logo compression enabled - Large logos will be automatically compressed');
      devLog('✅ FIXED: Data saved in format that mobile app expects - no more reverting issues!');
      devLog('🚨 [handleGenerateCustomPDF] AGGRESSIVE DEBUGGING END 🚨');

      setShowCustomQuoteModal(false);
      setSelectedQuoteForCustomTemplate(null);
      devReliabilityLog('customPdf:done', { quoteId: templateSnapshot.id });
    } catch (error) {
      devReliabilityLog('customPdf:catch', { err: String(error) });
      alert(toUserMessage(error, 'Could not generate PDF. Please try again.'));
    } finally {
      customPdfGenInFlightRef.current = false;
    }
  };

  // File Upload Handler with automatic compression
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const originalLogo = e.target.result;
        
        // Compress logo if it's too large
        if (originalLogo.length > 500000) { // If larger than ~500KB
          devLog('Logo is large, compressing...');
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Calculate new dimensions (reduce size)
            const maxWidth = 200;
            const maxHeight = 200;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Draw and compress
            ctx.drawImage(img, 0, 0, width, height);
            const compressedLogo = canvas.toDataURL('image/jpeg', 0.7); // 70% quality
            
            devLog('Logo compressed from', originalLogo.length, 'to', compressedLogo.length, 'bytes');
            
            setCustomQuoteData(prev => ({
              ...prev,
              companyLogo: compressedLogo
            }));
          };
          img.src = originalLogo;
        } else {
          // Logo is small enough, use as is
          setCustomQuoteData(prev => ({
            ...prev,
            companyLogo: originalLogo
          }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Generate PDF (bounded phases: fetch → DOM/html → canvas → jsPDF → optional Supabase store)
  const generatePDF = async (quote, customTerms = '', customNotes = '', showBreakdown = false) => {
    let pdfContent = null;
    try {
      devReliabilityLog('quotePdf:phase:fetch', { quoteId: quote.id });

      let latestQuoteData = null;
      const fetchRes = await runReliableAsync({
        name: 'quotePdf:loadRow',
        timeoutMs: 35000,
        retries: 1,
        onBeforeRetry: () => refreshSupabaseSessionOnce(supabase),
        fn: async () => {
          const { data: quoteDoc, error } = await supabase.from('quotes').select('*').eq('id', quote.id).single();
          if (error) throw error;
          return quoteDoc;
        },
      });
      if (fetchRes.ok && fetchRes.data) {
        latestQuoteData = fetchRes.data;
      } else {
        devReliabilityLog('quotePdf:fetchFallback', { useInlineQuote: true });
        latestQuoteData = quote;
      }

      const quoteToUse = normalizeQuoteFromDb(latestQuoteData || quote);
      devLog('🔍 [generatePDF] Using quote data:', {
        id: quoteToUse.id,
        hasCustomQuoteData: !!quoteToUse.customQuoteData,
        customQuoteDataKeys: quoteToUse.customQuoteData ? Object.keys(quoteToUse.customQuoteData) : [],
        scopeOfWork: quoteToUse.scopeOfWork,
        materials: quoteToUse.materials?.length || 0
      });
      
      const client = clients.find(c => c.id === quoteToUse.clientId);
      const totals = calculateQuoteTotals(quoteToUse);
      const showQuoteTax =
        quoteToUse.finalTaxEnabled === true || quoteToUse.customQuoteData?.finalTaxEnabled === true;

      // Use the quote's stored customQuoteData if available, otherwise fall back to global customQuoteData
      const quoteCustomData = quote.customQuoteData || customQuoteData;
      devLog('Generating custom quote template with data:', quoteCustomData);
      devLog('Logo present:', !!quoteCustomData.companyLogo);
      devLog('Show logo:', quoteCustomData.showLogo);
      devLog('Logo size:', quoteCustomData.logoSize);
        
      // Use settings data for company information if custom quote data is not available
      const companyName = quoteCustomData.companyName || settings.companyName || 'Devello Tech';
      const companyStreetAddress = quoteCustomData.streetAddress || settings.streetAddress || '';
    const companyCity = quoteCustomData.city || settings.city || '';
    const companyProvince = quoteCustomData.province || settings.province || '';
    const companyPostalCode = quoteCustomData.postalCode || settings.postalCode || '';
      const companyPhone = quoteCustomData.companyPhone || settings.companyPhone || '';
      const clientName = quoteCustomData.clientName || client?.name || 'Unknown Client';
      const clientAddress = quoteCustomData.clientAddress || client?.address || '';
      const quoteNumber = quoteCustomData.quoteNumber || quote.quoteNumber || 'N/A';
      
      // Use custom terms and notes if provided, otherwise use custom quote data or separate fields
      let notesText = '';
      if (customNotes) {
        notesText = customNotes;
      } else if (Array.isArray(quoteCustomData.notes)) {
        notesText = quoteCustomData.notes.filter(note => note.text && note.text.trim()).map(note => note.text).join('\n');
      } else if (Array.isArray(quote.notes)) {
        notesText = quote.notes.filter(note => note.text && note.text.trim()).map(note => note.text).join('\n');
      }
      


      // Create PDF content HTML
        pdfContent = document.createElement('div');
        pdfContent.style.width = '600px';
        pdfContent.style.padding = '40px';
        pdfContent.style.margin = '0 auto';
        pdfContent.style.backgroundColor = 'white';
        pdfContent.style.fontFamily = 'Arial, sans-serif';
        pdfContent.style.color = '#111';
        pdfContent.style.lineHeight = '1.6';
        pdfContent.style.padding = '20px';
        pdfContent.style.margin = '0';
        pdfContent.style.boxSizing = 'border-box';
        
        // Enhanced logo handling for quotes
        const logoHtml = quoteCustomData.companyLogo && quoteCustomData.showLogo ? 
          `<div style="margin-bottom: -15px;">
            <img src="${quoteCustomData.companyLogo}" alt="Company Logo" style="width: ${quoteCustomData.logoSize || 60}px; height: ${quoteCustomData.logoSize || 60}px; object-fit: contain; margin-bottom: 0;" />
          </div>` : '';
        
        devLog('Quote logo HTML generated:', logoHtml ? 'Yes' : 'No');
        
        pdfContent.innerHTML = `
          <div style="margin-bottom: 20px;">
            <div style="display: flex; flex-direction: column; align-items: flex-start;">
              ${logoHtml}
              <div style="margin-top: 0;">
                <h1 style="color: #000; margin: 0 0 3px 0; font-size: 18px; font-weight: bold;">${companyName}</h1>
                ${companyStreetAddress ? `<p style="color: #000; margin: 1px 0; font-size: 10px;">${companyStreetAddress}</p>` : ''}
                ${companyCity && companyProvince && companyPostalCode ? `<p style="color: #000; margin: 1px 0; font-size: 10px;">${companyCity}, ${companyProvince} ${companyPostalCode}</p>` : ''}
                ${companyPhone ? `<p style="color: #000; margin: 1px 0; font-size: 10px;">${companyPhone}</p>` : ''}
                ${quoteCustomData.companyEmail ? `<p style="color: #000; margin: 1px 0; font-size: 10px;">${quoteCustomData.companyEmail}</p>` : ''}
              </div>
            </div>
            
            <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-top: 15px;">
              <div>
                <p style="color: #000; margin: 2px 0; font-size: 10px;">${quoteCustomData.quoteDate || new Date().toLocaleDateString()}</p>
                <p style="color: #000; margin: 2px 0; font-size: 10px;">ISSUED TO: ${clientName}</p>
                ${clientAddress ? `<p style="color: #000; margin: 2px 0; font-size: 10px;">${clientAddress}</p>` : ''}
            </div>
            <div style="text-align: right;">
                <p style="color: #000; margin: 2px 0; font-size: 12px; font-weight: bold;">${quoteNumber}</p>
            </div>
          </div>
          
            <div style="border-bottom: 1px solid #000; margin: 15px 0;"></div>
          </div>
          
                        <div style="margin-bottom: 5px; border: 1px solid #000; padding: 6px;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div style="flex: 1;">
                    <p style="color: #000; margin: 0; font-size: 12px; font-weight: bold; margin-top: -10px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">DESCRIPTION</p>
                  </div>
                  <div style="flex: 0 0 auto; text-align: right;">
                    <p style="color: #000; margin: 0; font-size: 12px; font-weight: bold; margin-top: -10px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">TOTAL</p>
                  </div>
                </div>
              </div>
              
              <div style="margin-bottom: 5px;">
            ${(() => {
              // CRITICAL FIX: Check BOTH customQuoteData.scopeOfWork AND root level scopeOfWork
              // This ensures we get the mobile app data that was just saved
              devLog('🔍 [generatePDF] Checking scope of work data...');
              devLog('  quoteCustomData.scopeOfWork:', quoteCustomData.scopeOfWork);
              devLog('  quoteToUse.scopeOfWork:', quoteToUse.scopeOfWork);
              
              // PRIORITY 1: Check customQuoteData.scopeOfWork first (website format)
              if (quoteCustomData.scopeOfWork && quoteCustomData.scopeOfWork.length > 0 && 
                  quoteCustomData.scopeOfWork[0].points && quoteCustomData.scopeOfWork[0].points.length > 0) {
                devLog('🔍 [generatePDF] Using customQuoteData.scopeOfWork:', quoteCustomData.scopeOfWork.length, 'sections');
                return quoteCustomData.scopeOfWork.map(section => `
                  <div style="margin-bottom: 10px;">
                    ${section.heading ? `<p style="color: #000; margin: 0 0 5px 0; font-weight: bold; font-size: 12px;">${section.heading}</p>` : ''}
                    ${section.points.map(point => `<div style="display: grid; grid-template-columns: 16px 1fr; gap: 8px; font-size: 12px; color: #000; margin-bottom: 4px; align-items: start;"><div style="font-size: 14px; line-height: 1; text-align: center; margin-top: 1px;">&#8226;</div><div style="line-height: 1.6;">${point.text}</div></div>`).join('')}
                  </div>
                `).join('');
              }
              
              // PRIORITY 2: Check root level scopeOfWork (mobile app format)
              if (quoteToUse.scopeOfWork && quoteToUse.scopeOfWork.length > 0 && 
                  quoteToUse.scopeOfWork[0].points && quoteToUse.scopeOfWork[0].points.length > 0) {
                devLog('🔍 [generatePDF] Using root level scopeOfWork:', quoteToUse.scopeOfWork.length, 'sections');
                return quoteToUse.scopeOfWork.map(section => `
                  <div style="margin-bottom: 10px;">
                    ${section.heading ? `<p style="color: #000; margin: 0 0 5px 0; font-weight: bold; font-size: 12px;">${section.heading}</p>` : ''}
                    ${section.points.map(point => `<div style="display: grid; grid-template-columns: 16px 1fr; gap: 8px; font-size: 12px; color: #000; margin-bottom: 4px; align-items: start;"><div style="font-size: 14px; line-height: 1; text-align: center; margin-top: 1px;">&#8226;</div><div style="line-height: 1.6;">${point.text}</div></div>`).join('')}
                  </div>
                `).join('');
              }
              
              // PRIORITY 3: Fallback to materials (legacy format)
              devLog('🔍 [generatePDF] No scope of work found, falling back to materials');
              return `${(quoteToUse.materials || []).map(material => `<div style="display: grid; grid-template-columns: 16px 1fr; gap: 8px; font-size: 12px; color: #000; margin-bottom: 4px; align-items: start;"><div style="font-size: 14px; line-height: 1; text-align: center;">&#8226;</div><div style="line-height: 1.6;">${material.name} - Qty: ${material.qty} - Price: $${material.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>`).join('')}`;
            })()}
          </div>
          
                        <div style="margin-bottom: 2px; color: #000;">
                <div style="display: flex; flex-direction: column; gap: 2px; margin-bottom: 0; color: #000;">
                  ${showBreakdown ? `
              <div style="display: flex; justify-content: flex-end; align-items: baseline; font-size: 11px; margin-bottom: 0;">
                <span style="font-weight: bold; color: #000;">Materials:</span>
                <span style="margin-left: 8px; color: #000;">$${(totals.materialsSubtotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            ${totals.labourTotal > 0 ? `
              <div style="display: flex; justify-content: flex-end; align-items: baseline; font-size: 11px; margin-bottom: 0;">
                <span style="font-weight: bold; color: #000;">Labour:</span>
                <span style="margin-left: 8px; color: #000;">$${(totals.labourTotal ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
                    ` : ''}
            ` : ''}
              <div style="display: flex; justify-content: flex-end; align-items: baseline; font-size: 11px; margin-bottom: 0;">
                <span style="font-weight: bold; color: #000;">Subtotal:</span>
                <span style="margin-left: 8px; color: #000;">$${(totals.subtotalAfterMarkup ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
                  ${
                    showQuoteTax
                      ? `<div style="display: flex; justify-content: flex-end; align-items: baseline; font-size: 11px; margin-bottom: 5px;">
                <span style="font-weight: bold; color: #000;">Tax:</span>
                <span style="margin-left: 8px; color: #000;">$${(totals.finalHstAmount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>`
                      : ''
                  }
                  <div style="margin-bottom: 15px; border: 1px solid #000; padding: 6px;">
                    <div style="display: flex; justify-content: space-between; align-items: center;">
                      <div style="flex: 1;">
                      </div>
                      <div style="flex: 0 0 auto; text-align: right;">
                        <p style="color: #000; margin: 0; font-size: 11px; font-weight: bold; margin-top: -10px; font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;">Total: $${(totals.total ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      </div>
                    </div>
              </div>
            </div>
          </div>
          

          
          ${(() => {
            // CRITICAL FIX: Check BOTH customQuoteData AND root level for notes section
            devLog('🔍 [generatePDF] Checking notes section data...');
            devLog('  quoteCustomData.showNotesSection:', quoteCustomData.showNotesSection);
            devLog('  quoteCustomData.notesBulletPoints:', quoteCustomData.notesBulletPoints);
            devLog('  quoteToUse.showNotesSection:', quoteToUse.showNotesSection);
            devLog('  quoteToUse.notesBulletPoints:', quoteToUse.notesBulletPoints);
            
            // PRIORITY 1: Check customQuoteData first (website format)
            if (quoteCustomData.showNotesSection && quoteCustomData.notesBulletPoints && quoteCustomData.notesBulletPoints.length > 0) {
              devLog('🔍 [generatePDF] Using customQuoteData notes section');
              return `
                <div style="margin-bottom: 15px;">
                  <p style="color: #000; margin: 0 0 5px 0; font-weight: bold; font-size: 12px;">${quoteCustomData.notesHeaderTitle || 'Notes'}</p>
                  ${quoteCustomData.notesBulletPoints.filter(point => point.text && point.text.trim()).map(point => `<div style="display: grid; grid-template-columns: 16px 1fr; gap: 8px; font-size: 11px; color: #000; margin-bottom: 4px; align-items: start;"><div style="font-size: 14px; line-height: 1; text-align: center;">&#8226;</div><div style="line-height: 1.6;">${point.text}</div></div>`).join('')}
                </div>
              `;
            }
            
            // PRIORITY 2: Check root level (mobile app format)
            if (quoteToUse.showNotesSection && quoteToUse.notesBulletPoints && quoteToUse.notesBulletPoints.length > 0) {
              devLog('🔍 [generatePDF] Using root level notes section');
              return `
                <div style="margin-bottom: 15px;">
                  <p style="color: #000; margin: 0 0 5px 0; font-weight: bold; font-size: 12px;">${quoteToUse.notesHeaderTitle || 'Notes'}</p>
                  ${quoteToUse.notesBulletPoints.filter(point => point.text && point.text.trim()).map(point => `<div style="display: grid; grid-template-columns: 16px 1fr; gap: 8px; font-size: 11px; color: #000; margin-bottom: 4px; align-items: start;"><div style="font-size: 14px; line-height: 1; text-align: center;">&#8226;</div><div style="line-height: 1.6;">${point.text}</div></div>`).join('')}
                </div>
              `;
            }
            
            // PRIORITY 3: Fallback to notesText
            if (notesText) {
              devLog('🔍 [generatePDF] Using notesText fallback');
              return `
                <div style="margin-bottom: 15px;">
                  <p style="color: #000; margin: 0 0 5px 0; font-weight: bold; font-size: 12px;">Notes:</p>
                  ${notesText.split(/\r?\n/).filter(line => line.trim()).map(line => `<div style="display: grid; grid-template-columns: 16px 1fr; gap: 8px; font-size: 11px; color: #000; margin-bottom: 4px; align-items: start;"><div style="font-size: 14px; line-height: 1; text-align: center;">&#8226;</div><div style="line-height: 1.6;">${line}</div></div>`).join('')}
                </div>
              `;
            }
            
            // No notes section to show
            devLog('🔍 [generatePDF] No notes section to show');
            return '';
          })()}
          

          
          ${quoteCustomData.showSignature ? `
          <div style="margin-top: 30px; border-top: 1px solid #000; padding-top: 20px;">
            <div style="display: flex; justify-content: space-between; align-items: flex-end;">
              <div style="width: 45%;">
                <div style="border-bottom: 1px solid #000; height: 40px; margin-bottom: 5px;"></div>
                <p style="color: #000; margin: 0; font-size: 10px; text-align: center;">Client Signature</p>
              </div>
              <div style="width: 45%;">
                <div style="border-bottom: 1px solid #000; height: 40px; margin-bottom: 5px;"></div>
                <p style="color: #000; margin: 0; font-size: 10px; text-align: center;">Date</p>
              </div>
            </div>
          </div>
          ` : ''}
        `;
        
        document.body.appendChild(pdfContent);
        devReliabilityLog('quotePdf:phase:html2canvas', { quoteId: quote.id });

        let canvas;
        try {
          canvas = await withTimeout(
            html2canvas(pdfContent, {
              scale: 4,
              useCORS: true,
              allowTaint: true,
              imageTimeout: 0,
              removeContainer: true,
              backgroundColor: '#ffffff',
              width: pdfContent.scrollWidth,
              height: pdfContent.scrollHeight,
              scrollX: 0,
              scrollY: 0,
            }),
            120000,
            'quotePdf:html2canvas'
          );
        } catch (error) {
          devReliabilityLog('quotePdf:canvasFail', { err: String(error) });
          alert(toUserMessage(error, 'Could not render PDF. Please try again.'));
          return;
        }
        
        // Convert to JPEG with best quality
        const imgData = canvas.toDataURL('image/jpeg', 0.98); // 0.98 = very high quality
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 190; // Reduced from 210 to add margins
        const pageHeight = 277; // Reduced from 295 to add margins
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 10; // Start with 10mm margin from top
        
        pdf.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight); // 10mm margin from left
        heightLeft -= pageHeight;
        
        while (heightLeft >= 0) {
          position = heightLeft - imgHeight + 10; // Add 10mm margin from top
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight); // 10mm margin from left
          heightLeft -= pageHeight;
        }
        
        devReliabilityLog('quotePdf:phase:blob', { quoteId: quote.id });
        const pdfBlob = pdf.output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfViewerUrl(pdfUrl);
        setShowPDFViewer(true);
        
        try {
          // Convert PDF blob to base64 for storage
          const arrayBuffer = await pdfBlob.arrayBuffer();
          const uint8Array = new Uint8Array(arrayBuffer);
          
          // Convert to base64 in chunks to avoid call stack overflow
          const chunkSize = 8192;
          let binary = '';
          for (let i = 0; i < uint8Array.length; i += chunkSize) {
            const chunk = uint8Array.slice(i, i + chunkSize);
            binary += String.fromCharCode.apply(null, chunk);
          }
          const pdfBase64 = btoa(binary);
          
          const updateData = {
            custom_quote_data: {
              ...customQuoteData,
              // CRITICAL FIX: NEVER overwrite materials - preserve original quote materials ONLY
              materials: quote.materials || [],
              // Ensure scopeOfWork has both heading and title fields for mobile app compatibility
              scopeOfWork: (customQuoteData.scopeOfWork || []).map(section => ({
                ...section,
                heading: section.heading || section.title || 'To complete the following:',
                title: section.title || section.heading || 'To complete the following:'
              }))
            },
            
            pdf_data: pdfBase64,
            
            quote_number: customQuoteData.quoteNumber || '',
            description: customQuoteData.descriptionHeading || '',
            tax_rate:
              quote.tax_rate != null && quote.tax_rate !== '' ? quote.tax_rate : (quote.taxRate != null && quote.taxRate !== '' ? quote.taxRate : (quote.custom_quote_data?.taxRate ?? '')),
            
            // AGGRESSIVE: Save materials at root level (mobile app format)
            // CRITICAL FIX: NEVER overwrite materials - preserve original quote materials ONLY
            materials: quote.materials || [],
            
            // AGGRESSIVE: Save scope of work at root level (mobile app format)
            scopeOfWork: (customQuoteData.scopeOfWork || []).map(section => ({
              ...section,
              // Ensure both heading and title fields exist for mobile app compatibility
              heading: section.heading || section.title || 'To complete the following:',
              title: section.title || section.heading || 'To complete the following:'
            })),
            
            // AGGRESSIVE: Save notes at root level (mobile app format)
            notes: customQuoteData.notes || [],
            
            // AGGRESSIVE: Save notes section data at root level (mobile app format)
            notesBulletPoints: customQuoteData.notesBulletPoints || [],
            notesHeaderTitle: customQuoteData.notesHeaderTitle || '',
            showNotesSection: customQuoteData.showNotesSection || false,
            
            // AGGRESSIVE: Save description heading at root level (mobile app format)
            descriptionHeading: customQuoteData.descriptionHeading || 'To complete the following:',
            
            // AGGRESSIVE: Save company info at root level (mobile app format)
            companyName: customQuoteData.companyName || '',
            companyPhone: customQuoteData.companyPhone || '',
            address: customQuoteData.streetAddress || '',
            city: customQuoteData.city || '',
            province: customQuoteData.province || '',
            postalCode: customQuoteData.postalCode || '',
            email: customQuoteData.companyEmail || '',
            
            // AGGRESSIVE: Save client info at root level (mobile app format)
            clientName: customQuoteData.clientName || '',
            clientStreet: customQuoteData.clientAddress || '',
            
            // AGGRESSIVE: Save quote date at root level (mobile app format)
            quoteDate: customQuoteData.quoteDate || '',
            
            // AGGRESSIVE: Mark as ready for PDF generation
            pdfDataReady: true,
            
            updated_at: new Date().toISOString(),
            
            pdf_generated_at: new Date().toISOString()
          };
          const storeRes = await runSupabaseAuthedOperation({
            name: 'quotePdf:persistPdfMeta',
            supabaseClient: supabase,
            timeoutMs: 55000,
            retries: 1,
            fn: async () => {
              const { error } = await supabase.from('quotes').update(updateData).eq('id', quote.id);
              if (error) throw error;
            },
          });
          if (storeRes.ok) {
            const updatedQuotes = quotes.map((q) =>
              q.id === quote.id ? { ...q, ...updateData } : q
            );
            setQuotes(updatedQuotes);
            devReliabilityLog('quotePdf:phase:storeOk', { quoteId: quote.id });
          } else {
            devReliabilityLog('quotePdf:storeFail', { message: storeRes.userMessage });
          }
        } catch (error) {
          devReliabilityLog('quotePdf:storeCatch', { err: String(error) });
        }

        return;
    } catch (error) {
      devReliabilityLog('quotePdf:fail', { err: String(error) });
      alert(toUserMessage(error, 'Could not generate PDF. Please try again.'));
    } finally {
      safeRemoveDetachedNode(pdfContent);
    }
  };

  // Handle opening email modal
  const handleSendEmail = (quote) => {
    if (userPlan !== 'pro') return;
    setEmailQuoteData(quote); // Pass the full quote object
    setShowEmailModal(true);
    // Close the client quotes modal when opening email modal
    setShowClientQuotes(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewRequestChanges = (quote) => {
    setSelectedQuoteForChanges(quote);
    setShowRequestChangesModal(true);
  };

  const handleInvoiceSentStatusClick = async (quote) => {
    if (!user) return;
    if (quoteRowMutationBusyId) return;

    if (quote.status === 'draft') {
      if (!window.confirm('Mark this invoice as sent? Use this if you emailed or delivered it outside Devello.')) return;
      const at = new Date().toISOString();
      setQuoteRowMutationBusyId(quote.id);
      try {
        await patchQuoteColumns(quote.id, {
          status: 'sent_invoice',
          invoice_sent: true,
          invoice_sent_at: at,
          updated_at: new Date().toISOString(),
        });
        setQuotes((prev) =>
          prev.map((q) =>
            q.id === quote.id
              ? {
                  ...q,
                  status: 'sent_invoice',
                  invoice_sent: true,
                  invoice_sent_at: at,
                  invoiceSentAt: at,
                }
              : q
          )
        );
      } catch (error) {
        alert(toUserMessage(error, 'Could not update invoice status. Please try again.'));
      } finally {
        setQuoteRowMutationBusyId(null);
      }
      return;
    }

    if (quote.status === 'sent_invoice') {
      if (
        !window.confirm(
          'Mark invoice as not sent yet? Status will go back to Draft (use if the invoice was not actually sent).'
        )
      ) {
        return;
      }
      setQuoteRowMutationBusyId(quote.id);
      try {
        await patchQuoteColumns(quote.id, {
          status: 'draft',
          invoice_sent: false,
          invoice_sent_at: null,
          updated_at: new Date().toISOString(),
        });
        setQuotes((prev) =>
          prev.map((q) =>
            q.id === quote.id
              ? { ...q, status: 'draft', invoice_sent: false, invoice_sent_at: null, invoiceSentAt: null }
              : q
          )
        );
      } catch (error) {
        alert(toUserMessage(error, 'Could not update invoice status. Please try again.'));
      } finally {
        setQuoteRowMutationBusyId(null);
      }
      return;
    }

    if (quote.status === 'sent_both') {
      if (
        !window.confirm(
          'Mark invoice as not sent? The quote will show as Sent Quote only (invoice no longer marked sent).'
        )
      ) {
        return;
      }
      setQuoteRowMutationBusyId(quote.id);
      try {
        await patchQuoteColumns(quote.id, {
          status: 'sent_quote',
          invoice_sent: false,
          invoice_sent_at: null,
          updated_at: new Date().toISOString(),
        });
        setQuotes((prev) =>
          prev.map((q) =>
            q.id === quote.id
              ? { ...q, status: 'sent_quote', invoice_sent: false, invoice_sent_at: null, invoiceSentAt: null }
              : q
          )
        );
      } catch (error) {
        alert(toUserMessage(error, 'Could not update invoice status. Please try again.'));
      } finally {
        setQuoteRowMutationBusyId(null);
      }
    }
  };

  const handleInvoiceAmountUpdate = async (quoteId, value) => {
    if (!user) return;
    if (quoteRowMutationBusyId) return;
    setQuoteRowMutationBusyId(quoteId);
    try {
      const updatedAt = new Date().toISOString();
      if (value === null || value === undefined) {
        await patchQuoteColumns(quoteId, { invoice_amount: null, updated_at: updatedAt });
        setQuotes((prev) =>
          prev.map((q) =>
            q.id === quoteId ? { ...q, invoice_amount: null, invoiceAmount: undefined } : q
          )
        );
      } else {
        await patchQuoteColumns(quoteId, { invoice_amount: value, updated_at: updatedAt });
        setQuotes((prev) =>
          prev.map((q) =>
            q.id === quoteId ? { ...q, invoice_amount: value, invoiceAmount: value } : q
          )
        );
      }
    } catch (error) {
      alert(toUserMessage(error, 'Could not update invoice amount. Please try again.'));
    } finally {
      setQuoteRowMutationBusyId(null);
    }
  };

  const handleToggleInvoicePayment = async (quote) => {
    if (!user || !quote.invoiceData) return;
    if (quoteRowMutationBusyId) return;
    const next = quote.invoicePaymentStatus === 'paid' ? 'awaiting' : 'paid';
    setQuoteRowMutationBusyId(quote.id);
    try {
      await patchQuoteInvoicePaymentStatus(quote.id, next);
      setQuotes((prev) =>
        prev.map((q) =>
          q.id === quote.id ? { ...q, invoice_payment_status: next, invoicePaymentStatus: next } : q
        )
      );
    } catch (error) {
      devError('Invoice payment toggle:', error);
      alert(toUserMessage(error, 'Could not update payment status. Please try again.'));
    } finally {
      setQuoteRowMutationBusyId(null);
    }
  };

  // Handle go to quote from client modal
  const handleGoToQuote = (quote) => {
    setHighlightedQuoteId(quote.id);
    setShowClientQuotes(false);
    setSelectedClientForQuotes(null);
    navigate('/dashboard/invoices');
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      
      // Force navigation with replace to prevent back button issues
      navigate('/', { replace: true });
      
      // Force page reload to ensure clean state
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    } catch (error) {
      devError('Error signing out:', error);
      // Even if there's an error, force navigation
      window.location.href = '/';
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setIsChangingPassword(true);

    try {
      // Validate form
      if (!passwordForm.newPassword || !passwordForm.confirmPassword) {
        setPasswordError('New password and confirmation are required');
        return;
      }

      if (passwordForm.newPassword !== passwordForm.confirmPassword) {
        setPasswordError('New passwords do not match');
        return;
      }

      if (
        passwordForm.newPassword.length < 8 ||
        !/[!@#$%^&*(),.?":{}|<>]/.test(passwordForm.newPassword)
      ) {
        setPasswordError('Password must be at least 8 characters and include at least one symbol.');
        return;
      }

      if (!user) {
        setPasswordError('No user found. Please sign in again.');
        return;
      }

      const { error: pwError } = await supabase.auth.updateUser({ password: passwordForm.newPassword });
      if (pwError) throw pwError;

      // Reset form and close modal
      setPasswordForm({
        newPassword: '',
        confirmPassword: ''
      });
      setShowChangePasswordModal(false);
      setPasswordError('');
      
      // Show success message
      alert('Password changed successfully!');
    } catch (error) {
      devError('Error changing password:', error);
      if (error.code === 'auth/weak-password') {
        setPasswordError('New password is too weak');
      } else {
        setPasswordError('Failed to change password. Please try again.');
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handlePasswordFormChange = (field, value) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: value
    }));
    setPasswordError(''); // Clear error when user starts typing
  };

  const navigateToSection = (sectionName, quoteId = null) => {
    navigate(`/dashboard/${sectionName}`);
    if (quoteId) {
      // Set highlighted quote ID for invoices section
      setHighlightedQuoteId(quoteId);
    }
  };

  const projectsVisible = canViewProjects(userData);
  const projectsWritable = canMutateProjects(userData);
  const downgradedReadOnly = isDowngradedReadOnlyProfile(userData);

  // Section rendering based on URL param
  const proSections = ['overview', 'invoices', 'clients', 'teams', 'settings', 'analytics', 'scheduling', 'projects'];
  const freeSections = ['invoices', 'clients', 'teams', 'settings'];
  const downgradedSections = [...freeSections, 'projects'];
  const sectionsForPlan = userPlan === 'pro' ? proSections : downgradedReadOnly ? downgradedSections : freeSections;
  const defaultSection = userPlan === 'pro' ? 'overview' : 'invoices';
  const effectiveSection =
    section === 'subcontractors' ? 'teams' : section === 'billing' ? 'settings' : section;
  const currentSection =
    effectiveSection && sectionsForPlan.includes(effectiveSection) ? effectiveSection : defaultSection;

  /**
   * Client-side navigation for sidebar sections so each click does not reload
   * the document. Full reloads could serve a cached index.html that still
   * references an old hashed main.*.js (missing on CDN) → HTML returned for
   * the .js URL → "Unexpected token '<'" and a blank app.
   */
  const handleSidebarSectionNavigation = useCallback(
    (event) => {
      const linkEl = event.target?.closest?.('a[data-section]');
      if (!linkEl) return;
      const targetSection = linkEl.getAttribute('data-section');
      const href = linkEl.getAttribute('href');
      if (!targetSection || !href) return;
      if (targetSection === currentSection) return;
      event.preventDefault();
      try {
        const url = new URL(href, window.location.href);
        navigate(`${url.pathname}${url.search}${url.hash}`);
      } catch {
        navigate(href);
      }
    },
    [currentSection, navigate]
  );

  // Filter quotes based on search term and status
  const filteredQuotes = quotes.filter(quote => {
    const client = clients.find(c => c.id === quote.clientId);
    const clientName = client?.name || '';
    const searchMatch = !quoteSearchTerm || 
      clientName.toLowerCase().includes(quoteSearchTerm.toLowerCase()) ||
      quote.id.toLowerCase().includes(quoteSearchTerm.toLowerCase());
    
    let statusMatch = true;
    if (quoteStatusFilter === 'all') {
      statusMatch = true;
    } else if (quoteStatusFilter === 'payment_awaiting') {
      statusMatch =
        !!quote.invoiceData &&
        quote.invoicePaymentStatus !== 'paid';
    } else if (quoteStatusFilter === 'payment_paid') {
      statusMatch = quote.invoicePaymentStatus === 'paid';
    } else {
      statusMatch = quote.status === quoteStatusFilter;
    }
    
    return searchMatch && statusMatch;
  });

  const renderContent = () => {
    if (effectiveSection && !sectionsForPlan.includes(effectiveSection)) {
      return <Navigate to={`/dashboard/${defaultSection}`} replace />;
    }

    if (currentSection === 'overview' && userPlan !== 'pro') {
      return <Navigate to="/dashboard/invoices" replace />;
    }

    if (currentSection === 'analytics' && userPlan !== 'pro') {
      return <Navigate to="/dashboard/invoices" replace />;
    }
    if (currentSection === 'scheduling' && userPlan !== 'pro') {
      return <Navigate to="/dashboard/invoices" replace />;
    }
    if (effectiveSection === 'projects' && !projectsVisible) {
      return <Navigate to="/dashboard/invoices" replace />;
    }

    switch (currentSection) {
      case 'overview':
        return (
    <OverviewSection
      quotes={quotes}
      clients={clients}
      projects={projects}
      appointments={appointments}
      teams={teams}
      dayAssignments={allDayAssignments}
      editQuote={editQuote}
      navigateToSection={navigateToSection}
    />
  );
      
      case 'invoices':
        return (
      <QuotesSection
        quotes={quotes}
        setQuotes={setQuotes}
        clients={clients}
        filteredQuotes={filteredQuotes}
        quoteSearchTerm={quoteSearchTerm}
        setQuoteSearchTerm={setQuoteSearchTerm}
        quoteStatusFilter={quoteStatusFilter}
        setQuoteStatusFilter={setQuoteStatusFilter}
        showCreateQuote={showCreateQuote}
        setShowCreateQuote={setShowCreateQuote}
        showEditQuote={showEditQuote}
        setShowEditQuote={setShowEditQuote}
        quoteForm={quoteForm}
        setQuoteForm={setQuoteForm}
        saveQuote={saveQuote}
        deleteQuote={deleteQuote}
        editQuote={editQuote}
        calculateQuoteTotals={calculateQuoteTotals}
        dropdownOpenId={dropdownOpenId}
        setDropdownOpenId={setDropdownOpenId}
        handleSendEmail={handleSendEmail}
        onInvoiceSentStatusClick={handleInvoiceSentStatusClick}
        onInvoiceAmountUpdate={handleInvoiceAmountUpdate}
        onToggleInvoicePayment={handleToggleInvoicePayment}
        quoteRowMutationBusyId={quoteRowMutationBusyId}
        settings={settings}
        setIsEditingQuote={setIsEditingQuote}
        setShowReviewQuote={setShowReviewQuote}
        setIsInReviewMode={setIsInReviewMode}
        highlightedQuoteId={highlightedQuoteId}
        setHighlightedQuoteId={setHighlightedQuoteId}
        plan={userPlan}
      />
        );

      case 'teams':
        return (
          <TeamsSection
            teams={teams}
            showAddTeamMember={showAddTeamMember}
            setShowAddTeamMember={setShowAddTeamMember}
            teamMemberForm={teamMemberForm}
            setTeamMemberForm={setTeamMemberForm}
            saveTeamMember={saveTeamMember}
            deleteTeamMember={deleteTeamMember}
            editTeamMember={editTeamMember}
            handleTeamMemberFormChange={handleTeamMemberFormChange}
            editingTeamMember={editingTeamMember}
            setEditingTeamMember={setEditingTeamMember}
            subcontractors={subcontractors}
            showAddSubcontractor={showAddSubcontractor}
            setShowAddSubcontractor={setShowAddSubcontractor}
            subcontractorForm={subcontractorForm}
            setSubcontractorForm={setSubcontractorForm}
            saveSubcontractor={saveSubcontractor}
            deleteSubcontractor={deleteSubcontractor}
            editSubcontractor={editSubcontractor}
            handleSubcontractorFormChange={handleSubcontractorFormChange}
            editingSubcontractor={editingSubcontractor}
            setEditingSubcontractor={setEditingSubcontractor}
            userPlan={userPlan}
          />
        );

      case 'clients':
        return (
            <ClientsSection
              clients={clients}
              filteredClients={filteredClients}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              showAddClient={showAddClient}
              setShowAddClient={setShowAddClient}
              clientForm={clientForm}
              setClientForm={setClientForm}
              saveClient={saveClient}
              deleteClient={deleteClient}
              editClient={setClientForm}
              handleClientFormChange={handleClientFormChange}
              formatPhoneNumber={formatPhoneNumber}
              quotes={quotes}
              setSelectedClientForQuotes={setSelectedClientForQuotes}
              setShowClientQuotes={setShowClientQuotes}
              // Add any additional props needed for modals, etc.
            />
        );

      case 'analytics':
        return (
          <AnalyticsSection
            projects={projects}
            expenses={expenses}
            quotes={quotes}
          />
        );

      case 'settings':
        return (
          <div className="max-w-7xl mx-auto space-y-8 pb-10">
            <DashboardPageHeader
              eyebrow="Dashboard"
              title="Settings"
              description="Account details, company branding for invoices, and your subscription."
              action={
                <div className="flex items-center gap-3">
                  {saveMessage && (
                    <span className={`text-sm font-medium ${saveMessage.includes('Error') ? 'text-red-600' : 'text-emerald-700'}`}>
                      {saveMessage}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={saveUserSettings}
                    disabled={saving || !hasUnsavedChanges}
                    className={
                      saving
                        ? 'inline-flex cursor-not-allowed items-center gap-2 rounded-2xl bg-slate-400 px-6 py-3 text-sm font-semibold text-white'
                        : !hasUnsavedChanges
                          ? `${dashboardBtnPrimary} pointer-events-none cursor-not-allowed opacity-40 shadow-none`
                          : dashboardBtnPrimary
                    }
                  >
                    {saving ? 'Saving…' : !hasUnsavedChanges ? 'Saved ✓' : 'Save changes'}
                  </button>
                </div>
              }
            />

            {/* Save Message (error only fallback) */}
            {saveMessage && saveMessage.includes('Error') && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
                {saveMessage}
              </div>
            )}

            {/* Account Settings */}
            <div className={dashboardCardClass}>
              <div className={dashboardCardHeaderClass}>
                <h3 className="text-base font-semibold text-slate-900">Account Settings</h3>
              </div>
              <div className="space-y-4 p-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Name</label>
                  <input
                    type="text"
                    placeholder="Your Full Name"
                    value={settings.name}
                    onChange={(e) => handleSettingChange('name', e.target.value)}
                    className={dashboardInputClass}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Email address</label>
                  <EmailChangeField currentEmail={user?.email} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <button 
                    onClick={() => setShowChangePasswordModal(true)}
                    className="text-primary hover:text-primary-dark font-medium text-sm"
                  >
                    Change Password
                  </button>
                </div>

              </div>
            </div>
            {/* Appearance */}
            <div className={dashboardCardClass}>
              <div className={dashboardCardHeaderClass}>
                <h3 className="text-base font-semibold text-slate-900">Appearance</h3>
                <p className="mt-0.5 text-xs text-slate-500">Optional dark theme for your workspace</p>
              </div>
              <div className="p-6">
                <ThemeToggle />
              </div>
            </div>
            {/* Company Info */}
            <div className={dashboardCardClass}>
              <div className={dashboardCardHeaderClass}>
                <h3 className="text-base font-semibold text-slate-900">Company Info</h3>
              </div>
              <div className="space-y-4 p-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Company name</label>
                  <input
                    type="text"
                    placeholder="Your Company Name"
                    value={settings.companyName}
                    onChange={(e) => handleSettingChange('companyName', e.target.value)}
                    className={dashboardInputClass}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Company logo</label>
                  <p className="mb-2 text-xs text-slate-500">
                    PNG or JPEG. Shown on generated invoice PDFs; upload here or override per invoice in the invoice editor.
                  </p>
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50">
                      {settings.companyLogo ? (
                        <img key={settings.companyLogo} src={settings.companyLogo} alt="" className="max-w-full max-h-full object-contain" />
                      ) : (
                        <span className="text-xs text-gray-400 text-center px-2">No logo</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 min-w-0">
                      <input
                        ref={settingsLogoInputRef}
                        id="settings-company-logo-input"
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        className="hidden"
                        onChange={handleSettingsLogoUpload}
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (settingsLogoInputRef.current) {
                              settingsLogoInputRef.current.value = '';
                              settingsLogoInputRef.current.click();
                            }
                          }}
                          className="inline-flex cursor-pointer items-center rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:border-primary/30 hover:bg-slate-50"
                        >
                          Choose image
                        </button>
                        {settings.companyLogo && (
                          <button
                            type="button"
                            onClick={() => {
                              setSettings((prev) => ({ ...prev, companyLogo: null }));
                              setHasUnsavedChanges(true);
                              if (settingsLogoInputRef.current) settingsLogoInputRef.current.value = '';
                            }}
                            className="rounded-2xl px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            Remove logo
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Street address</label>
                  <input
                    type="text"
                    placeholder="123 Main Street"
                    value={settings.streetAddress || ''}
                    onChange={(e) => handleSettingChange('streetAddress', e.target.value)}
                    className={dashboardInputClass}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Country</label>
                  <select
                    value={settings.country === 'US' ? 'US' : 'CA'}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSettings((prev) => ({
                        ...prev,
                        country: next,
                        province: '',
                      }));
                      setHasUnsavedChanges(true);
                    }}
                    className={dashboardInputClass}
                  >
                    {COUNTRY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">City</label>
                    <input
                      type="text"
                      placeholder={settings.country === 'US' ? 'New York' : 'Toronto'}
                      value={settings.city || ''}
                      onChange={(e) => handleSettingChange('city', e.target.value)}
                      className={dashboardInputClass}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      {settings.country === 'US' ? 'State' : 'Province'}
                    </label>
                    <select
                      value={settings.province || ''}
                      onChange={(e) => handleSettingChange('province', e.target.value)}
                      className={dashboardInputClass}
                    >
                      {(settings.country === 'US' ? US_STATE_OPTIONS : CA_PROVINCE_OPTIONS).map((opt, idx) => (
                        <option key={`${opt.value}-${idx}`} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      {settings.country === 'US' ? 'Zip code' : 'Postal code'}
                    </label>
                    <input
                      type="text"
                      placeholder={settings.country === 'US' ? '12345 or 12345-6789' : 'M5V 3A8'}
                      value={settings.postalCode || ''}
                      onChange={(e) => handleSettingChange('postalCode', e.target.value)}
                      className={dashboardInputClass}
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Company Email</label>
                  <input
                    type="email"
                    placeholder="info@yourcompany.com"
                    value={settings.companyEmail}
                    onChange={(e) => handleSettingChange('companyEmail', e.target.value)}
                    className={dashboardInputClass}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Company Phone</label>
                  <input
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={settings.companyPhone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    className={dashboardInputClass}
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">HST No.</label>
                  <input
                    type="text"
                    placeholder="123456789 RT0001"
                    value={settings.hstNumber || ''}
                    onChange={(e) => handleSettingChange('hstNumber', e.target.value)}
                    className={dashboardInputClass}
                  />
                </div>
              </div>
            </div>
            {/* Account: plan & billing lives only in settings */}
            <div className={dashboardCardClass}>
              <div className={dashboardCardHeaderClass}>
                <h3 className="text-base font-semibold text-slate-900">Plan & billing</h3>
                <p className="mt-1 text-xs text-slate-500">
                  Subscription and card management are handled securely by Stripe. Manage everything from this settings
                  area.
                </p>
              </div>
              <div className="space-y-5 p-6">
                <div className="flex flex-col gap-4 rounded-2xl border border-slate-100 bg-slate-50/60 p-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-lg font-bold text-slate-900">{getPlanDetails(userPlan).name}</span>
                      {userPlan === 'pro' && accessSource(userData) === 'manual' && (
                        <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-[11px] font-semibold text-violet-900">
                          Complimentary
                        </span>
                      )}
                      {userPlan === 'pro' && accessSource(userData) === 'stripe' && (
                        <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-900">
                          Paid
                        </span>
                      )}
                      {userPlan === 'pro' && accessSource(userData) === 'apple' && (
                        <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-[11px] font-semibold text-sky-900">
                          App Store
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                      {userData?.isTrial || userData?.is_trial
                        ? 'Trial access — confirm billing before trial ends.'
                        : userPlan === 'pro'
                          ? accessSource(userData) === 'stripe'
                            ? 'Active subscription through Stripe.'
                            : accessSource(userData) === 'apple'
                              ? 'Active subscription through the App Store.'
                              : 'Pro access without a paid subscription on file. You can start paid billing anytime.'
                          : `Free workspace. Pro is $${PRO_MONTHLY_USD}/month when you subscribe.`}
                    </p>
                    <p className="mt-2 text-xs text-slate-500">
                      {accessSource(userData) === 'stripe'
                        ? 'Billing is linked to your Stripe customer record.'
                        : accessSource(userData) === 'apple'
                          ? 'Manage or cancel in iPhone Settings → your name → Subscriptions.'
                          : 'Plan changes from self-serve checkout update automatically after payment.'}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col gap-2 sm:w-52">
                    <button
                      type="button"
                      onClick={() => openPlanBillingModal(false)}
                      className="rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white shadow-md shadow-primary/20 hover:bg-primary-dark"
                    >
                      Manage plan & billing
                    </button>
                  </div>
                </div>
                {(paymentStatus?.subscriptionStatus === 'past_due' ||
                  paymentStatus?.billingStatus === 'past_due') && (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    Payment failed. Open Manage plan & billing to update your card in Stripe.
                  </div>
                )}
              </div>
            </div>
            
            {/* Save Button */}

          </div>
        );

      case 'scheduling':
        return <Scheduling quotes={quotes} />;

      case 'projects':
        return (
          <Projects
            authUser={user}
            projectsAccessMode={projectsWritable ? 'full' : 'read_only'}
            projects={projects}
            setProjects={setProjects}
            expenses={expenses}
            linkedTeamMembers={teams}
            linkedSubcontractors={subcontractors}
            saveExpense={(data) => saveExpense(data, { skipUiReset: true })}
            deleteExpense={deleteExpense}
            addedByName={settings.name || settings.companyName || null}
          />
        );

      
      default:
        return <Navigate to={`/dashboard/${defaultSection}`} replace />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center dark:from-slate-950 dark:to-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (section === 'quotes') {
    return <Navigate to="/dashboard/invoices" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-100 dashboard-container">
      {/* Material Save Success Message */}
      {materialSaveMessage && (
        <div className="fixed top-4 right-4 z-[80] p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg">
          <div className="flex items-center gap-2 text-green-700 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {materialSaveMessage}
          </div>
        </div>
      )}
      
      {/* Sidebar */}
      {user && currentSection !== 'scheduling' && (
        <aside
          className={`fixed inset-y-0 left-0 z-40 flex flex-col border-r border-slate-800/90 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 shadow-[4px_0_24px_-4px_rgba(0,0,0,0.35)] transition-[width] duration-200 ease-out ${
            sidebarCollapsed ? 'w-[4.5rem]' : 'w-[17rem]'
          }`}
        >
          {/* Wave-style collapse handle: half-pill on the right edge, protrudes into main content */}
          <button
            type="button"
            onClick={toggleSidebarCollapsed}
            aria-expanded={!sidebarCollapsed}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute right-0 top-1/2 z-[45] flex h-[3.25rem] w-[1.125rem] -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-r-full border border-slate-600/90 border-l-0 bg-slate-800 pl-0.5 shadow-[2px_0_10px_rgba(0,0,0,0.25)] transition-colors hover:border-slate-500 hover:bg-slate-700"
          >
            {sidebarCollapsed ? (
              <svg className="h-3.5 w-3.5 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M9 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="h-3.5 w-3.5 text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M15 19l-7-7 7-7" />
              </svg>
            )}
          </button>

          <div
            className={`flex shrink-0 items-center border-b border-white/10 ${
              sidebarCollapsed ? 'justify-center px-2 py-4' : 'px-4 py-4'
            }`}
          >
            {!sidebarCollapsed ? (
              <div className="min-w-0 pr-1">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500">Devello</p>
                <p className="mt-0.5 bg-gradient-to-r from-cyan-300 via-white to-cyan-200/90 bg-clip-text text-lg font-bold tracking-tight text-transparent">
                  Workspace
                </p>
              </div>
            ) : (
              <div
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/30 to-slate-800 text-sm font-bold text-cyan-200 ring-1 ring-white/10"
                aria-hidden
              >
                D
              </div>
            )}
          </div>

          <nav
            className="dashboard-tabs min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-3"
            onClickCapture={handleSidebarSectionNavigation}
          >
            <ul className={`space-y-1 ${sidebarCollapsed ? 'px-1.5' : 'px-3'}`}>
              {userPlan === 'pro' && !downgradedReadOnly && (
                <li>
                  <Link
                    to="/dashboard/overview"
                    data-section="overview"
                    title="Overview"
                    className={`flex items-center rounded-xl text-sm font-medium transition-all ${
                      sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                    } ${
                      currentSection === 'overview'
                        ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-1 ring-white/10'
                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <svg className="h-5 w-5 shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    <span className={sidebarCollapsed ? 'sr-only' : ''}>Overview</span>
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/dashboard/invoices"
                  data-section="invoices"
                  title="Invoices"
                  className={`flex items-center rounded-xl text-sm font-medium transition-all ${
                    sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                  } ${
                    currentSection === 'invoices'
                      ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-1 ring-white/10'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <svg className="h-5 w-5 shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className={sidebarCollapsed ? 'sr-only' : ''}>Invoices</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/dashboard/clients"
                  data-section="clients"
                  title="Clients"
                  className={`flex items-center rounded-xl text-sm font-medium transition-all ${
                    sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                  } ${
                    currentSection === 'clients'
                      ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-1 ring-white/10'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <svg className="h-5 w-5 shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className={sidebarCollapsed ? 'sr-only' : ''}>Clients</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/dashboard/teams"
                  data-section="teams"
                  title="Teams"
                  className={`flex items-center rounded-xl text-sm font-medium transition-all ${
                    sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                  } ${
                    currentSection === 'teams'
                      ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-1 ring-white/10'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <svg className="h-5 w-5 shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <span className={sidebarCollapsed ? 'sr-only' : ''}>Teams</span>
                </Link>
              </li>
              
              {projectsVisible && (
                <li>
                  <Link
                    to="/dashboard/projects"
                    data-section="projects"
                    title="Projects"
                    className={`flex items-center rounded-xl text-sm font-medium transition-all ${
                      sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                    } ${
                      currentSection === 'projects'
                        ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-1 ring-white/10'
                        : 'text-slate-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <svg className="h-5 w-5 shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    <span className={sidebarCollapsed ? 'sr-only' : ''}>Projects</span>
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/dashboard/settings"
                  data-section="settings"
                  title="Settings"
                  className={`flex items-center rounded-xl text-sm font-medium transition-all ${
                    sidebarCollapsed ? 'justify-center px-0 py-2.5' : 'gap-3 px-3 py-2.5'
                  } ${
                    currentSection === 'settings'
                      ? 'bg-primary text-white shadow-lg shadow-primary/30 ring-1 ring-white/10'
                      : 'text-slate-400 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  <svg className="h-5 w-5 shrink-0 opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className={sidebarCollapsed ? 'sr-only' : ''}>Settings</span>
                </Link>
              </li>
            </ul>
          </nav>

          <div className={`shrink-0 border-t border-white/10 ${sidebarCollapsed ? 'p-2' : 'p-3'}`}>
            <div
              className={`rounded-2xl border border-slate-700/60 bg-slate-800/35 backdrop-blur-sm ${
                sidebarCollapsed ? 'flex flex-col items-center gap-2 p-2' : 'p-3'
              }`}
            >
              {!sidebarCollapsed ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-dark text-sm font-semibold text-white shadow-inner ring-1 ring-white/10">
                      {user && user.email.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-slate-300">{user && user.email}</p>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">Signed in</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="mt-3 w-full rounded-xl border border-slate-600/80 py-2 text-center text-sm font-medium text-slate-300 transition-colors hover:border-red-500/40 hover:bg-red-950/30 hover:text-red-200"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-dark text-sm font-semibold text-white shadow-inner ring-1 ring-white/10"
                    title={user?.email || ''}
                  >
                    {user && user.email.charAt(0).toUpperCase()}
                  </div>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    title="Sign out"
                    aria-label="Sign out"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-600/80 text-slate-300 transition-colors hover:border-red-500/40 hover:bg-red-950/30 hover:text-red-200"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.75}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Main Content */}
      <div
        className={
          currentSection === 'scheduling'
            ? ''
            : `transition-[margin] duration-200 ease-out ${sidebarCollapsed ? 'ml-[4.5rem]' : 'ml-[17rem]'}`
        }
      >
        <div className="app-main-surface min-h-screen bg-gradient-to-br from-slate-50 via-white to-cyan-50/20 px-4 py-6 sm:px-6 sm:py-8 lg:px-10 lg:py-10">
          {/* Trial Countdown */}
          <TrialCountdown user={user} userData={userData} />

          {renderContent()}
        </div>
      </div>

      {/* Create invoice — materials & tax */}
      {showCreateQuote && (
        <>
          <div className={dashboardModalBackdrop} aria-hidden />
          <div className={dashboardModalWrap}>
            <div
              className={invoiceBuilderModalShell}
              role="dialog"
              aria-modal="true"
              aria-labelledby="quote-create-heading"
            >
              <header className={`${dashboardModalHeaderSurfaceClass} px-4 py-3 sm:px-6`}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Invoices</p>
                    <h3 id="quote-create-heading" className="mt-0.5 text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                      Create invoice
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={closeCreateInvoiceModal}
                    className="shrink-0 rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                    title="Close"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </header>

              <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row" style={{ paddingBottom: '4.5rem' }}>
              <div className={invoiceBuilderLeftPanel}>
                {/* Client Selection */}
                <div className="mb-4">
                  <label className="block text-base font-semibold text-gray-700 mb-2">Client</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => {
                        const v = e.target.value;
                        setClientSearch(v);
                        setShowClientDropdown(true);
                        if (v === '') {
                          handleQuoteFormChange('clientId', '');
                        }
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      onBlur={() => {
                        // Delay hiding dropdown to allow clicking
                        setTimeout(() => setShowClientDropdown(false), 200);
                      }}
                      placeholder="Search for a client..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {showClientDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {clients
                          .filter(client => 
                            client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                            client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                          )
                          .map(client => (
                            <div
                              key={client.id}
                              onClick={() => {
                                handleQuoteFormChange('clientId', client.id);
                                setClientSearch(client.name);
                                setShowClientDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{client.name}</div>
                              {client.email && (
                                <div className="text-xs text-gray-500">{client.email}</div>
                              )}
                            </div>
                          ))}
                        {clients.filter(client => 
                          client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                          client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            No clients found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Materials — search opens full-screen picker; quick add for manual lines */}
                <div className="flex min-h-0 flex-1 flex-col">
                  <label className="mb-2 block text-base font-semibold text-gray-700">Materials</label>
                  <div className="mb-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!canUseMaterialSearch) return;
                        setShowMaterialSearchModal(true);
                      }}
                      disabled={!canUseMaterialSearch}
                      className={`${dashboardBtnPrimary} !py-2 !text-sm`}
                    >
                      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      {canUseMaterialSearch ? 'Material search' : 'Material search (Pro)'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomMaterialsList(true)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      <svg className="h-4 w-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Custom ({materialHistory.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomMaterialModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add custom line
                    </button>
                  </div>
                  <div className="rounded-xl border border-slate-200/90 bg-slate-50/80 p-3">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Quick add line</p>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-[10rem] flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-700">Description</label>
                        <input
                          type="text"
                          value={materialSearch}
                          onChange={(e) => setMaterialSearch(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder="Item name"
                        />
                      </div>
                      <div className="w-20">
                        <label className="mb-1 block text-xs font-medium text-gray-700">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={materialQty}
                          onChange={(e) => setMaterialQty(Number(e.target.value))}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="w-28">
                        <label className="mb-1 block text-xs font-medium text-gray-700">Unit price</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={materialPrice}
                          onChange={(e) => setMaterialPrice(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!materialSearch || !materialPrice || materialQty < 1) return;
                          setQuoteForm((prev) => ({
                            ...prev,
                            materials: [...(prev.materials || []), { name: materialSearch, qty: materialQty, price: parseFloat(materialPrice) }],
                          }));
                          const existingMaterial = materialHistory.find(
                            (m) => m.name.toLowerCase() === materialSearch.toLowerCase()
                          );
                          if (!existingMaterial) {
                            saveMaterialToHistory({ name: materialSearch, price: parseFloat(materialPrice) });
                          }
                          setMaterialSearch('');
                          setMaterialPrice('');
                          setMaterialQty(1);
                          setMaterialResults([]);
                          setShowMaterialDropdown(false);
                          setHasSearched(false);
                        }}
                        className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-dark"
                      >
                        Add
                      </button>
                    </div>
                    {materialSaveMessage && (
                      <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-2 text-sm text-green-700">
                        {materialSaveMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: line items & running total */}
              <div className={invoiceBuilderRightPanel}>
                <div className="flex min-h-0 flex-1 flex-col p-5 sm:p-6">
                  <div className="mb-4 flex items-center justify-between gap-2">
                    <h4 className="text-base font-bold text-slate-900 sm:text-lg">Line items</h4>
                    <span className="rounded-full bg-slate-200/80 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                      {quoteForm.materials.length}
                    </span>
                  </div>
                  <div className="mb-4 min-h-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200/90 bg-white shadow-inner dark:border-slate-700 dark:bg-slate-900/80">
                    {quoteForm.materials && quoteForm.materials.length > 0 ? (
                      <div className="p-4 space-y-3">
                        {quoteForm.materials.map((mat, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800/60"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">{mat.name}</div>
                              <div className="text-xs text-gray-500">Qty: {mat.qty} × ${mat.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <div className="font-semibold text-sm">${(mat.qty * mat.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              </div>
                              <button
                                className="text-red-500 hover:text-red-700 text-xs font-medium"
                                onClick={() => {
                                  setQuoteForm(prev => ({
                                    ...prev,
                                    materials: prev.materials.filter((_, i) => i !== idx)
                                  }));
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center p-8 text-center text-slate-400">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <p className="font-medium text-slate-600">No materials yet</p>
                        <p className="mt-1 text-sm text-slate-500">Use Material search or quick add on the left.</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-slate-200 bg-white p-4 sm:p-5 dark:border-slate-700 dark:bg-slate-900">
                  <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Totals</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Materials:</span>
                      <span className="font-semibold">${materialsSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {quoteForm.taxEnabled !== false && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Tax (
                          {quoteForm.taxRate === '' || quoteForm.taxRate == null
                            ? '0'
                            : quoteForm.taxRate}
                          %):
                        </span>
                        <span className="font-semibold">${materialsTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <hr className="border-gray-300" />
                    <div className="flex justify-between text-base">
                      <span className="font-bold text-gray-800">Total:</span>
                      <span className="font-bold text-primary">
                        $
                        {(quoteForm.taxEnabled !== false ? materialsWithTax : materialsSubtotal).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="absolute bottom-0 left-0 right-0 z-10 flex flex-col gap-2 border-t border-slate-200 bg-white/98 px-3 py-2 shadow-[0_-6px_20px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-2"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <div className="w-full min-w-0 sm:max-w-[9.5rem]">
                  <label className={`${dashboardLabelClass} !mb-1 text-xs`}>Invoice #</label>
                  <input
                    type="text"
                    value={quoteForm.quoteNumber || ''}
                    onChange={(e) => setQuoteForm((prev) => ({ ...prev, quoteNumber: e.target.value }))}
                    className={`${dashboardInputClass} !py-2`}
                    placeholder="e.g. INV-1042"
                  />
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-700 sm:mb-0 sm:mt-5">
                  <input
                    type="checkbox"
                    checked={quoteForm.taxEnabled !== false}
                    onChange={(e) =>
                      setQuoteForm((prev) => ({
                        ...prev,
                        taxEnabled: e.target.checked,
                      }))
                    }
                    className="rounded border-slate-300 text-primary focus:ring-primary/30"
                  />
                  Apply tax
                </label>
                {quoteForm.taxEnabled !== false && (
                  <div className="w-full min-w-0 sm:w-24">
                    <label className={`${dashboardLabelClass} !mb-1 text-xs`}>Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={quoteForm.taxRate === '' || quoteForm.taxRate == null ? '' : quoteForm.taxRate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setQuoteForm((prev) => ({
                          ...prev,
                          taxRate: v === '' ? '' : parseFloat(v) || 0,
                        }));
                      }}
                      className={`${dashboardInputClass} !py-2`}
                      placeholder="13"
                    />
                  </div>
                )}
              </div>
              <div className="flex shrink-0 justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreateInvoiceModal}
                  className={`${dashboardBtnModalCancel} !py-2 !text-sm`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!quoteForm.clientId) {
                      return;
                    }
                    if (quoteForm.materials.length === 0) {
                      return;
                    }
                    setIsEditingQuote(false);
                    setIsInReviewMode(true);
                    setShowReviewQuote(true);
                    setShowCreateQuote(false);
                  }}
                  className={`${dashboardBtnPrimary} !px-4 !py-2 !text-sm disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none`}
                  disabled={!quoteForm.clientId || quoteForm.materials.length === 0}
                >
                  Review
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      {/* Review invoice */}
      {showReviewQuote && (
        <>
          <div className={dashboardModalBackdrop} aria-hidden />
          <div className={dashboardModalWrap}>
            <div
              className="relative flex h-[min(96vh,calc(100dvh-0.5rem))] w-full max-w-[min(96vw,1680px)] flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/20"
              role="dialog"
              aria-modal="true"
              aria-labelledby="quote-review-heading"
            >
              <header className={`${dashboardModalHeaderSurfaceClass} px-4 py-3 sm:px-6`}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Invoices</p>
                    <h3 id="quote-review-heading" className="mt-0.5 text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                      Review invoice
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowReviewQuote(false);
                      setIsInReviewMode(false);
                      if (isEditingQuote) {
                        setShowEditQuote(true);
                      } else {
                        setIsEditingQuote(false);
                        setShowCreateQuote(true);
                      }
                    }}
                    className="shrink-0 rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                    title="Close"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </header>

            <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/60 p-5 sm:p-6">
              <section className="mb-6 rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Client</p>
                <p className="mt-2 text-lg font-bold text-slate-900">
                  {clients.find((c) => c.id === quoteForm.clientId)?.name || 'Not selected'}
                </p>
              </section>

              <div className="mb-6">
                <div className="mb-3 flex items-center justify-between gap-2">
                  <h4 className="text-base font-bold text-slate-900 sm:text-lg">Materials</h4>
                  <span className="rounded-full bg-slate-200/80 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                    {quoteForm.materials.length}
                  </span>
                </div>
                <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm">
                  <table className="w-full">
                    <thead className="bg-slate-900 text-left text-xs font-semibold uppercase tracking-wide text-white">
                      <tr>
                        <th className="px-4 py-3">Material</th>
                        <th className="px-4 py-3 text-right">Qty</th>
                        <th className="px-4 py-3 text-right">Unit</th>
                        <th className="px-4 py-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {quoteForm.materials.map((mat, idx) => (
                        <tr key={idx} className="transition-colors hover:bg-slate-50/80">
                          <td className="px-4 py-3 text-sm text-gray-900">{mat.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">{mat.qty}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">${mat.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">${(mat.qty * mat.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="mb-6">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="text-base font-bold text-slate-900 sm:text-lg">Labour</h4>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddLabourModal(true);
                      setLabourEntryForm({ teamMemberId: '', hours: '' });
                    }}
                    className={`${dashboardBtnPrimary} !py-2 !px-3 !text-xs sm:!text-sm`}
                  >
                    Add labour
                  </button>
                </div>

                {quoteForm.labour?.entries?.length > 0 ? (
                  <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm">
                    <table className="w-full">
                      <thead className="bg-slate-900 text-left text-xs font-semibold uppercase tracking-wide text-white">
                        <tr>
                          <th className="px-4 py-3">Team member</th>
                          <th className="px-4 py-3 text-right">Hours</th>
                          <th className="px-4 py-3 text-right">Rate</th>
                          <th className="px-4 py-3 text-right">Total</th>
                          <th className="px-4 py-3 text-center"> </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {quoteForm.labour.entries.map((entry) => {
                          // Calculate total if it's missing (for compatibility with mobile app data)
                          const entryTotal = entry.total || (entry.hours * entry.rate) || 0;
                          // Ensure entryTotal is a valid number
                          const safeEntryTotal = typeof entryTotal === 'number' && !isNaN(entryTotal) ? entryTotal : 0;
                          return (
                            <tr key={entry.id} className="transition-colors hover:bg-slate-50/80">
                              <td className="px-4 py-3 text-sm text-slate-900">{entry.name}</td>
                              <td className="px-4 py-3 text-right text-sm text-slate-800">{entry.hours}</td>
                              <td className="px-4 py-3 text-right text-sm text-slate-800">${entry.rate}/hr</td>
                              <td className="px-4 py-3 text-right text-sm font-semibold text-slate-900">
                                ${safeEntryTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => removeLabourEntry(entry.id)}
                                  className="text-sm font-medium text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
                    <p className="font-medium text-slate-600">No labour yet</p>
                    <p className="mt-1 text-sm text-slate-500">Add crew hours from the button above.</p>
                  </div>
                )}
              </div>

              <div className="mb-6 rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
                <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Markup &amp; overhead</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={dashboardLabelClass}>Markup type</label>
                      <select
                        value={quoteForm.markupType || 'percentage'}
                        onChange={(e) => setQuoteForm((prev) => ({ ...prev, markupType: e.target.value }))}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="amount">Fixed Amount ($)</option>
                      </select>
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>
                        {quoteForm.markupType === 'amount' ? 'Markup amount ($)' : 'Markup (%)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step={quoteForm.markupType === 'amount' ? '0.01' : '0.1'}
                        value={quoteForm.markup || ''}
                        onChange={(e) => setQuoteForm((prev) => ({ ...prev, markup: e.target.value }))}
                        className={dashboardInputClass}
                        placeholder={quoteForm.markupType === 'amount' ? '0.00' : '0'}
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Overhead type</label>
                      <select
                        value={quoteForm.overheadType || 'amount'}
                        onChange={(e) => setQuoteForm((prev) => ({ ...prev, overheadType: e.target.value }))}
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="amount">Fixed Amount ($)</option>
                      </select>
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>
                        {quoteForm.overheadType === 'amount' ? 'Overhead amount ($)' : 'Overhead (%)'}
                      </label>
                      <input
                        type="number"
                        min="0"
                        step={quoteForm.overheadType === 'amount' ? '0.01' : '0.1'}
                        value={quoteForm.overhead || ''}
                        onChange={(e) => setQuoteForm((prev) => ({ ...prev, overhead: e.target.value }))}
                        className={dashboardInputClass}
                        placeholder={quoteForm.overheadType === 'amount' ? '0.00' : '0'}
                      />
                    </div>
                </div>
                <div className="mt-4 border-t border-slate-100 pt-4">
                  <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-800">
                    <input
                      type="checkbox"
                      checked={!!quoteForm.finalTaxEnabled}
                      onChange={(e) =>
                        setQuoteForm((prev) => ({
                          ...prev,
                          finalTaxEnabled: e.target.checked,
                        }))
                      }
                      className="rounded border-slate-300 text-primary focus:ring-primary/30"
                    />
                    <span>Add tax to total</span>
                  </label>
                  <p className="mt-1 text-xs text-slate-500">
                    When enabled, tax applies to materials, labour, markup, and overhead combined.
                  </p>
                  {quoteForm.finalTaxEnabled && (
                    <div className="mt-3 w-full max-w-[8rem]">
                      <label className={dashboardLabelClass}>Tax rate (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={quoteForm.taxRate === '' || quoteForm.taxRate == null ? '' : quoteForm.taxRate}
                        onChange={(e) => {
                          const v = e.target.value;
                          setQuoteForm((prev) => ({
                            ...prev,
                            taxRate: v === '' ? '' : parseFloat(v) || 0,
                          }));
                        }}
                        className={`${dashboardInputClass} !py-2`}
                        placeholder="13"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Quote Summary */}
              <div className="mb-6 rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm">
                <h4 className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate-400">Totals</h4>
                <div className="rounded-xl bg-slate-50/80 p-4">
                  <div className="space-y-2">
                    {(() => {
                      const totals = calculateQuoteTotals(quoteForm);
                      return (
                        <>
                    <div className="flex justify-between">
                            <span className="text-gray-600">
                              {quoteForm.taxEnabled !== false ? 'Materials (including tax):' : 'Materials:'}
                            </span>
                            <span className="font-semibold">
                              $
                              {(quoteForm.taxEnabled !== false
                                ? totals.materialsWithTax
                                : totals.materialsSubtotal
                              ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                    </div>
                    <div className="flex justify-between">
                              <span className="text-gray-600">Labour:</span>
                              <span className="font-semibold">${totals.labourTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Markup ({quoteForm.markupType === 'amount' ? '$' : ''}{(quoteForm.markup && parseFloat(quoteForm.markup)) || 0}{quoteForm.markupType === 'percentage' ? '%' : ''}):
                            </span>
                            <span className="font-semibold">${totals.markupAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Overhead ({quoteForm.overheadType === 'amount' ? '$' : ''}{(quoteForm.overhead && parseFloat(quoteForm.overhead)) || 0}{quoteForm.overheadType === 'percentage' ? '%' : ''}):
                            </span>
                            <span className="font-semibold">${totals.overheadAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-200 pt-2">
                      <span className="text-gray-600 font-medium">Subtotal:</span>
                      <span className="font-semibold">${totals.subtotalAfterMarkup.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {quoteForm.finalTaxEnabled && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        Tax (
                        {quoteForm.taxRate === '' || quoteForm.taxRate == null
                          ? '0'
                          : quoteForm.taxRate}
                        %):
                      </span>
                            <span className="font-semibold">${totals.finalHstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Edited On:</span>
                      <span className="font-semibold">{new Date(quoteForm.editedOn).toLocaleString()}</span>
                    </div>
                    <hr className="border-gray-300" />
                    <div className="flex justify-between text-lg">
                      <span className="font-bold text-gray-800">Total:</span>
                            <span className="font-bold text-primary text-xl">${totals.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <footer className="flex shrink-0 flex-col gap-3 border-t border-slate-200 bg-slate-50/90 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <button
                type="button"
                onClick={() => {
                  setShowReviewQuote(false);
                  setIsInReviewMode(false);
                  if (isEditingQuote) {
                    setShowEditQuote(true);
                  } else {
                    setIsEditingQuote(false);
                    setShowCreateQuote(true);
                  }
                }}
                className={dashboardBtnModalCancel}
              >
                Back to edit
              </button>
              <div className="flex flex-wrap justify-end gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowReviewQuote(false);
                    setIsInReviewMode(false);
                    setShowEditQuote(false);
                    setShowCreateQuote(false);
                    setIsEditingQuote(false);
                    resetQuoteForm();
                  }}
                  className={dashboardBtnModalCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSavingQuote}
                  onClick={async () => {
                    devClickDebug('reviewQuote modal: Create/Save invoice button click', {
                      isSavingQuoteDisabled: isSavingQuote,
                      isEditingQuote,
                    });
                    const quoteDataToSave = {
                      ...quoteForm,
                      materials: quoteForm.materials || [],
                    };
                    devClickDebug('reviewQuote modal: setIsSavingQuote(true)', {});
                    setIsSavingQuote(true);
                    if (saveQuoteWatchdogRef.current) {
                      clearTimeout(saveQuoteWatchdogRef.current);
                    }
                    saveQuoteWatchdogRef.current = setTimeout(() => {
                      devClickDebug('reviewQuote modal: watchdog hard reset fired', {});
                      clearQuoteSaveLoadingState();
                    }, QUOTE_SAVE_HARD_RESET_MS);
                    try {
                      devClickDebug('reviewQuote modal: await saveQuote()', {
                        hasClient: !!quoteDataToSave.clientId,
                      });
                      const ok = await saveQuote(quoteDataToSave);
                      devClickDebug('reviewQuote modal: saveQuote returned', { ok });
                      if (!ok) {
                        return;
                      }
                    } finally {
                      devClickDebug('reviewQuote modal: finally setIsSavingQuote(false)', {});
                      clearQuoteSaveLoadingState();
                    }
                  }}
                  className={dashboardBtnPrimary}
                >
                  {isSavingQuote
                    ? isEditingQuote
                      ? 'Saving…'
                      : 'Creating…'
                    : isEditingQuote
                      ? 'Save changes'
                      : 'Create invoice'}
                </button>
              </div>
            </footer>
          </div>
        </div>
        </>
      )}

      {/* Edit invoice — same builder as create */}
      {showEditQuote && (
        <>
          <div className={dashboardModalBackdrop} aria-hidden />
          <div className={dashboardModalWrap}>
            <div
              className={invoiceBuilderModalShell}
              role="dialog"
              aria-modal="true"
              aria-labelledby="quote-edit-heading"
            >
              <header className={`${dashboardModalHeaderSurfaceClass} px-4 py-3 sm:px-6`}>
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="relative flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Invoices</p>
                    <h3 id="quote-edit-heading" className="mt-0.5 text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                      Edit invoice
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={closeEditInvoiceModal}
                    className="shrink-0 rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                    title="Close"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </header>

              <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row" style={{ paddingBottom: '4.5rem' }}>
              <div className={invoiceBuilderLeftPanel}>
                {/* Client Selection */}
                <div className="mb-4">
                  <label className="block text-base font-semibold text-gray-700 mb-2">Client</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={clientSearch}
                      onChange={(e) => {
                        const v = e.target.value;
                        setClientSearch(v);
                        setShowClientDropdown(true);
                        if (v === '') {
                          handleQuoteFormChange('clientId', '');
                        }
                      }}
                      onFocus={() => setShowClientDropdown(true)}
                      onBlur={() => {
                        // Delay hiding dropdown to allow clicking
                        setTimeout(() => setShowClientDropdown(false), 200);
                      }}
                      placeholder="Search for a client..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                    {showClientDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {clients
                          .filter(client => 
                            client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                            client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                          )
                          .map(client => (
                            <div
                              key={client.id}
                              onClick={() => {
                                handleQuoteFormChange('clientId', client.id);
                                setClientSearch(client.name);
                                setShowClientDropdown(false);
                              }}
                              className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{client.name}</div>
                              {client.email && (
                                <div className="text-xs text-gray-500">{client.email}</div>
                              )}
                            </div>
                          ))}
                        {clients.filter(client => 
                          client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                          client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-3 py-2 text-gray-500 text-sm">
                            No clients found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Materials — search opens full-screen picker; quick add for manual lines */}
                <div className="flex min-h-0 flex-1 flex-col">
                  <label className="mb-2 block text-base font-semibold text-gray-700">Materials</label>
                  <div className="mb-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!canUseMaterialSearch) return;
                        setShowMaterialSearchModal(true);
                      }}
                      disabled={!canUseMaterialSearch}
                      className={`${dashboardBtnPrimary} !py-2 !text-sm`}
                    >
                      <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      {canUseMaterialSearch ? 'Material search' : 'Material search (Pro)'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomMaterialsList(true)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      <svg className="h-4 w-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Custom ({materialHistory.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomMaterialModal(true)}
                      className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      <svg className="h-4 w-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add custom line
                    </button>
                  </div>
                  <div className="rounded-xl border border-slate-200/90 bg-slate-50/80 p-3">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Quick add line</p>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-[10rem] flex-1">
                        <label className="mb-1 block text-xs font-medium text-gray-700">Description</label>
                        <input
                          type="text"
                          value={materialSearch}
                          onChange={(e) => setMaterialSearch(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
                          placeholder="Item name"
                        />
                      </div>
                      <div className="w-20">
                        <label className="mb-1 block text-xs font-medium text-gray-700">Qty</label>
                        <input
                          type="number"
                          min="1"
                          value={materialQty}
                          onChange={(e) => setMaterialQty(Number(e.target.value))}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div className="w-28">
                        <label className="mb-1 block text-xs font-medium text-gray-700">Unit price</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={materialPrice}
                          onChange={(e) => setMaterialPrice(e.target.value)}
                          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!materialSearch || !materialPrice || materialQty < 1) return;
                          setQuoteForm((prev) => ({
                            ...prev,
                            materials: [...(prev.materials || []), { name: materialSearch, qty: materialQty, price: parseFloat(materialPrice) }],
                          }));
                          const existingMaterial = materialHistory.find(
                            (m) => m.name.toLowerCase() === materialSearch.toLowerCase()
                          );
                          if (!existingMaterial) {
                            saveMaterialToHistory({ name: materialSearch, price: parseFloat(materialPrice) });
                          }
                          setMaterialSearch('');
                          setMaterialPrice('');
                          setMaterialQty(1);
                          setMaterialResults([]);
                          setShowMaterialDropdown(false);
                          setHasSearched(false);
                        }}
                        className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary-dark"
                      >
                        Add
                      </button>
                    </div>
                    {materialSaveMessage && (
                      <div className="mt-2 rounded-lg border border-green-200 bg-green-50 p-2 text-sm text-green-700">
                        {materialSaveMessage}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: line items & running total */}
              <div className={invoiceBuilderRightPanel}>
                <div className="flex min-h-0 flex-1 flex-col p-5 sm:p-6">
                  <div className="mb-4 flex items-center justify-between gap-2">
                    <h4 className="text-base font-bold text-slate-900 sm:text-lg">Line items</h4>
                    <span className="rounded-full bg-slate-200/80 px-2.5 py-0.5 text-xs font-semibold text-slate-700">
                      {quoteForm.materials.length}
                    </span>
                  </div>
                  <div className="mb-4 min-h-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200/90 bg-white shadow-inner dark:border-slate-700 dark:bg-slate-900/80">
                    {quoteForm.materials && quoteForm.materials.length > 0 ? (
                      <div className="p-4 space-y-3">
                        {quoteForm.materials.map((mat, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-xl border border-slate-100 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800/60"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm text-gray-900 truncate">{mat.name}</div>
                              <div className="text-xs text-gray-500">Qty: {mat.qty} × ${mat.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <div className="font-semibold text-sm">${(mat.qty * mat.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                              </div>
                              <button
                                className="text-red-500 hover:text-red-700 text-xs font-medium"
                                onClick={() => {
                                  setQuoteForm(prev => ({
                                    ...prev,
                                    materials: prev.materials.filter((_, i) => i !== idx)
                                  }));
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center p-8 text-center text-slate-400">
                        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
                          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        </div>
                        <p className="font-medium text-slate-600">No materials yet</p>
                        <p className="mt-1 text-sm text-slate-500">Use Material search or quick add on the left.</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-slate-200 bg-white p-4 sm:p-5 dark:border-slate-700 dark:bg-slate-900">
                  <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Totals</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Materials:</span>
                      <span className="font-semibold">${materialsSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                    {quoteForm.taxEnabled !== false && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Tax (
                          {quoteForm.taxRate === '' || quoteForm.taxRate == null
                            ? '0'
                            : quoteForm.taxRate}
                          %):
                        </span>
                        <span className="font-semibold">${materialsTax.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}
                    <hr className="border-gray-300" />
                    <div className="flex justify-between text-base">
                      <span className="font-bold text-gray-800">Total:</span>
                      <span className="font-bold text-primary">
                        $
                        {(quoteForm.taxEnabled !== false ? materialsWithTax : materialsSubtotal).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="absolute bottom-0 left-0 right-0 z-10 flex flex-col gap-2 border-t border-slate-200 bg-white/98 px-3 py-2 shadow-[0_-6px_20px_rgba(15,23,42,0.06)] backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-2"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <div className="w-full min-w-0 sm:max-w-[9.5rem]">
                  <label className={`${dashboardLabelClass} !mb-1 text-xs`}>Invoice #</label>
                  <input
                    type="text"
                    value={quoteForm.quoteNumber || ''}
                    onChange={(e) => setQuoteForm((prev) => ({ ...prev, quoteNumber: e.target.value }))}
                    className={`${dashboardInputClass} !py-2`}
                    placeholder="e.g. INV-1042"
                  />
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-slate-700 sm:mb-0 sm:mt-5">
                  <input
                    type="checkbox"
                    checked={quoteForm.taxEnabled !== false}
                    onChange={(e) =>
                      setQuoteForm((prev) => ({
                        ...prev,
                        taxEnabled: e.target.checked,
                      }))
                    }
                    className="rounded border-slate-300 text-primary focus:ring-primary/30"
                  />
                  Apply tax
                </label>
                {quoteForm.taxEnabled !== false && (
                  <div className="w-full min-w-0 sm:w-24">
                    <label className={`${dashboardLabelClass} !mb-1 text-xs`}>Rate (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={quoteForm.taxRate === '' || quoteForm.taxRate == null ? '' : quoteForm.taxRate}
                      onChange={(e) => {
                        const v = e.target.value;
                        setQuoteForm((prev) => ({
                          ...prev,
                          taxRate: v === '' ? '' : parseFloat(v) || 0,
                        }));
                      }}
                      className={`${dashboardInputClass} !py-2`}
                      placeholder="13"
                    />
                  </div>
                )}
              </div>
              <div className="flex shrink-0 justify-end gap-2">
                <button
                  type="button"
                  onClick={closeEditInvoiceModal}
                  className={`${dashboardBtnModalCancel} !py-2 !text-sm`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!quoteForm.clientId) {
                      return;
                    }
                    if (quoteForm.materials.length === 0) {
                      return;
                    }
                    setIsEditingQuote(true);
                    setShowReviewQuote(true);
                    setShowEditQuote(false);
                  }}
                  className={`${dashboardBtnPrimary} !px-4 !py-2 !text-sm disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none`}
                  disabled={!quoteForm.clientId || quoteForm.materials.length === 0}
                >
                  Review changes
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}

      <MaterialSearchShoppingModal
        isOpen={canUseMaterialSearch && showMaterialSearchModal && (showCreateQuote || showEditQuote)}
        onClose={() => setShowMaterialSearchModal(false)}
        locationCountry={locationCountry}
        setLocationCountry={handleLocationCountryChange}
        locationRegion={locationRegion}
        setLocationRegion={handleLocationRegionChange}
        locationCity={locationCity}
        setLocationCity={handleLocationCityChange}
        locationRegions={locationRegions}
        testLocation={testLocation}
        locationValidation={locationValidation}
        locationValidationMessage={locationValidationMessage}
        materialSearch={materialSearch}
        setMaterialSearch={setMaterialSearch}
        searchMaterials={searchMaterials}
        materialSearchLoading={materialSearchLoading}
        materialResults={materialResults}
        saveMaterialToHistory={saveMaterialToHistory}
        onSelectMaterial={handleMaterialShoppingPick}
      />

      {/* Custom Material Modal — draft state is separate from quick-add row */}
      {showCustomMaterialModal && (
        <div
          className="fixed inset-0 z-[100130] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="custom-material-modal-title"
        >
          <div className={dashboardModalPanelMd}>
            <div className={`${dashboardModalHeaderSurfaceClass} px-5 py-4`}>
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-cyan-400/10 blur-2xl" />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Materials</p>
                  <h3 id="custom-material-modal-title" className="mt-0.5 text-lg font-bold text-slate-900">
                    Add custom line
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">Adds to this invoice. Quick-add fields on the builder stay as you left them if you cancel.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomMaterialModal(false);
                    setSaveToHistory(true);
                  }}
                  className={dashboardModalCloseBtn}
                  title="Close"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={dashboardModalBody}>
              <div className="space-y-4">
                <div>
                  <label className={dashboardLabelClass}>Description</label>
                  <input
                    type="text"
                    value={customMaterialModalDraft.name}
                    onChange={(e) =>
                      setCustomMaterialModalDraft((d) => ({ ...d, name: e.target.value }))
                    }
                    className={dashboardInputClass}
                    placeholder="Material or service name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={dashboardLabelClass}>Quantity</label>
                    <input
                      type="number"
                      min="1"
                      value={customMaterialModalDraft.qty}
                      onChange={(e) =>
                        setCustomMaterialModalDraft((d) => ({
                          ...d,
                          qty: Math.max(1, Number(e.target.value) || 1),
                        }))
                      }
                      className={dashboardInputClass}
                    />
                  </div>
                  <div>
                    <label className={dashboardLabelClass}>Unit price ($)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={customMaterialModalDraft.price}
                      onChange={(e) =>
                        setCustomMaterialModalDraft((d) => ({ ...d, price: e.target.value }))
                      }
                      className={dashboardInputClass}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    id="saveToHistoryModal"
                    checked={saveToHistory}
                    onChange={(e) => setSaveToHistory(e.target.checked)}
                    className="rounded border-slate-300 text-primary focus:ring-primary/30"
                  />
                  <span>Save to my materials library</span>
                </label>
              </div>
            </div>

            <div className={dashboardModalFooter}>
              <button
                type="button"
                onClick={() => {
                  setShowCustomMaterialModal(false);
                  setSaveToHistory(true);
                }}
                className={dashboardBtnModalCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const { name, qty, price } = customMaterialModalDraft;
                  if (!name?.trim() || !price || qty < 1) return;
                  const unit = parseFloat(price);
                  if (Number.isNaN(unit)) return;
                  setQuoteForm((prev) => ({
                    ...prev,
                    materials: [...(prev.materials || []), { name: name.trim(), qty, price: unit }],
                  }));
                  if (saveToHistory) {
                    saveMaterialToHistory({ name: name.trim(), price: unit });
                  }
                  setMaterialSearch('');
                  setMaterialPrice('');
                  setMaterialQty(1);
                  setShowCustomMaterialModal(false);
                  setSaveToHistory(true);
                }}
                className={dashboardBtnPrimary}
              >
                {saveToHistory ? 'Add & save to library' : 'Add to invoice only'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Materials List Modal */}
      {showCustomMaterialsList && (
        <div
          className="fixed inset-0 z-[100120] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="custom-materials-list-title"
        >
          <div className={`${dashboardModalPanel} max-h-[min(88vh,800px)]`}>
            <div className={`${dashboardModalHeaderSurfaceClass} px-5 py-4`}>
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-cyan-400/10 blur-2xl" />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Materials</p>
                  <h3 id="custom-materials-list-title" className="mt-0.5 text-lg font-bold text-slate-900">
                    Saved library
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">Re-use priced items on this invoice.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomMaterialsList(false);
                    setCustomMaterialSearch('');
                  }}
                  className={dashboardModalCloseBtn}
                  title="Close"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="border-b border-slate-100 px-5 py-3">
                <label className={dashboardLabelClass}>Search</label>
                <div className="relative mt-1">
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    value={customMaterialSearch}
                    onChange={(e) => setCustomMaterialSearch(e.target.value)}
                    placeholder="Filter by name…"
                    className={`${dashboardInputClass} !py-2 !pl-9`}
                  />
                </div>
                {customMaterialSearch ? (
                  <p className="mt-2 text-xs text-slate-500">
                    {materialHistory.filter((mat) =>
                      mat.name.toLowerCase().includes(customMaterialSearch.toLowerCase())
                    ).length}{' '}
                    of {materialHistory.length} shown
                  </p>
                ) : null}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
                {materialHistory.length > 0 ? (
                  <div className="space-y-3">
                    {materialHistory
                      .filter((mat) =>
                        mat.name.toLowerCase().includes(customMaterialSearch.toLowerCase())
                      )
                      .map((mat) => (
                        <div
                          key={mat.id}
                          className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm transition-all hover:border-slate-300 hover:shadow-md"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 flex-1 items-start gap-3">
                              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                  />
                                </svg>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate font-semibold text-slate-900">{mat.name}</p>
                                <p className="mt-0.5 text-xs text-slate-500">
                                  {mat.addedAt
                                    ? `Added ${new Date(mat.addedAt).toLocaleDateString()}`
                                    : 'Saved material'}
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                              <div className="mr-auto text-left sm:mr-0 sm:text-right">
                                <p className="text-base font-bold text-slate-900">
                                  $
                                  {mat.price.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </p>
                                <p className="text-[11px] text-slate-500">per unit</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingMaterial(mat);
                                  setShowEditMaterialModal(true);
                                }}
                                className={dashboardBtnRowEdit}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedHistory = materialHistory.filter((m) => m.id !== mat.id);
                                  setMaterialHistory(updatedHistory);
                                  if (user) {
                                    supabase
                                      .from('profiles')
                                      .update({ material_history: updatedHistory })
                                      .eq('id', user.id);
                                  }
                                }}
                                className={dashboardBtnRowDelete}
                              >
                                Delete
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setMaterialSearch(mat.name);
                                  setMaterialPrice(String(mat.price));
                                  setMaterialQty(1);
                                  setShowCustomMaterialsList(false);
                                  setCustomMaterialSearch('');
                                }}
                                className={`${dashboardBtnPrimary} !px-3 !py-2 !text-xs sm:!text-sm`}
                              >
                                Use in quick add
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    {materialHistory.filter((mat) =>
                      mat.name.toLowerCase().includes(customMaterialSearch.toLowerCase())
                    ).length === 0 &&
                      customMaterialSearch && (
                        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 py-10 text-center text-sm text-slate-600">
                          No matches. Try another search.
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-12 text-center">
                    <p className="font-medium text-slate-700">No saved materials yet</p>
                    <p className="mt-1 text-sm text-slate-500">Add a custom line to build your library.</p>
                  </div>
                )}
              </div>
            </div>

            <div className={dashboardModalFooter}>
              <button
                type="button"
                onClick={() => {
                  setShowCustomMaterialsList(false);
                  setCustomMaterialSearch('');
                  setShowCustomMaterialModal(true);
                }}
                className={dashboardBtnModalCancel}
              >
                New custom line
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCustomMaterialsList(false);
                  setCustomMaterialSearch('');
                }}
                className={dashboardBtnPrimary}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Material Modal */}
      {showEditMaterialModal && editingMaterial && (
        <div
          className="fixed inset-0 z-[100120] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-material-modal-title"
        >
          <div className={dashboardModalPanelMd}>
            <div className={`${dashboardModalHeaderSurfaceClass} px-5 py-4`}>
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-cyan-400/10 blur-2xl" />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Library</p>
                  <h3 id="edit-material-modal-title" className="mt-0.5 text-lg font-bold text-slate-900">
                    Edit saved material
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditMaterialModal(false);
                    setEditingMaterial(null);
                  }}
                  className={dashboardModalCloseBtn}
                  title="Close"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className={dashboardModalBody}>
              <div className="space-y-4">
                <div>
                  <label className={dashboardLabelClass}>Name</label>
                  <input
                    type="text"
                    value={editingMaterial.name}
                    onChange={(e) => setEditingMaterial({ ...editingMaterial, name: e.target.value })}
                    className={dashboardInputClass}
                    placeholder="Material name"
                  />
                </div>
                <div>
                  <label className={dashboardLabelClass}>Price per unit ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editingMaterial.price}
                    onChange={(e) =>
                      setEditingMaterial({
                        ...editingMaterial,
                        price: parseFloat(e.target.value) || 0,
                      })
                    }
                    className={dashboardInputClass}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className={dashboardModalFooter}>
              <button
                type="button"
                onClick={() => {
                  setShowEditMaterialModal(false);
                  setEditingMaterial(null);
                }}
                className={dashboardBtnModalCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!editingMaterial.name?.trim() || editingMaterial.price <= 0) return;
                  const updatedHistory = materialHistory.map((m) =>
                    m.id === editingMaterial.id ? { ...editingMaterial, name: editingMaterial.name.trim() } : m
                  );
                  setMaterialHistory(updatedHistory);
                  if (user) {
                    void runAuthedWrite('dashboard:editMaterialHistory', async () => {
                      const { error } = await supabase
                        .from('profiles')
                        .update({ material_history: updatedHistory })
                        .eq('id', user.id);
                      if (error) throw error;
                    }).catch((e) => {
                      devError('Error saving edited material history:', e);
                    });
                  }
                  setShowEditMaterialModal(false);
                  setEditingMaterial(null);
                }}
                className={dashboardBtnPrimary}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}






                    




      {/* Add Labour Entry Modal */}
      {showAddLabourModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100120] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b-2 bg-gray-50 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Add Labour Entry</h3>
                <button 
                  onClick={() => {
                    setShowAddLabourModal(false);
                    setLabourEntryForm({ teamMemberId: '', hours: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Team Member *
                  </label>
                  <select
                    value={labourEntryForm.teamMemberId}
                    onChange={(e) => handleLabourEntryFormChange('teamMemberId', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  >
                    <option value="">Select a team member</option>
                    {teams.map(teamMember => (
                      <option key={teamMember.id} value={teamMember.id}>
                        {teamMember.name} - {teamMember.role} (${teamMember.hourlyRate}/hr)
                      </option>
                    ))}
                  </select>
                  {teams.length === 0 && (
                    <p className="text-sm text-red-600 mt-1">
                      No team members available. Please add team members in the Teams section first.
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hours *
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    value={labourEntryForm.hours}
                    onChange={(e) => handleLabourEntryFormChange('hours', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter hours (e.g., 8.5)"
                    required
                  />
                </div>

                {/* Preview */}
                {labourEntryForm.teamMemberId && labourEntryForm.hours && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Preview:</h4>
                    {(() => {
                      const selectedMember = teams.find(t => t.id === labourEntryForm.teamMemberId);
                      const hours = parseFloat(labourEntryForm.hours);
                      const total = selectedMember ? hours * selectedMember.hourlyRate : 0;
                      
                      return (
                        <div className="text-sm text-gray-600">
                          <div className="flex justify-between">
                            <span>Team Member:</span>
                            <span className="font-medium">{selectedMember?.name}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Hours:</span>
                            <span className="font-medium">{hours}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Rate:</span>
                            <span className="font-medium">${selectedMember?.hourlyRate}/hr</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
                            <span className="font-medium">Total:</span>
                            <span className="font-bold text-primary">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t-2 bg-gray-50 rounded-b-2xl flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAddLabourModal(false);
                  setLabourEntryForm({ teamMemberId: '', hours: '' });
                }}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!labourEntryForm.teamMemberId || !labourEntryForm.hours) return;
                  
                  addLabourEntry(labourEntryForm.teamMemberId, labourEntryForm.hours);
                  setShowAddLabourModal(false);
                  setLabourEntryForm({ teamMemberId: '', hours: '' });
                }}
                disabled={!labourEntryForm.teamMemberId || !labourEntryForm.hours}
                className={`px-6 py-2 rounded-lg transition-colors font-semibold ${
                  !labourEntryForm.teamMemberId || !labourEntryForm.hours
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                Add Labour Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPDFViewer && pdfViewerUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100120] p-4">
          <div className="bg-white rounded-2xl w-full max-w-6xl shadow-2xl" style={{ height: '90vh' }}>
            {/* Header */}
            <div className="px-6 py-4 border-b-2 bg-gray-50 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Invoice PDF Preview</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = pdfViewerUrl;
                      link.download = `Invoice-${selectedQuote?.id?.slice(-6) || 'Unknown'}-${selectedQuote?.clientId || 'Client'}.pdf`;
                      link.click();
                    }}
                    className="bg-accent text-white px-4 py-2 rounded-lg hover:bg-accent-dark transition-colors text-sm font-semibold"
                  >
                    Download PDF
                  </button>
                  
                  {/* Store PDF in Quote Document Button */}
                  {selectedQuote && (
                    <button
                      onClick={async () => {
                        try {
                          // Convert blob URL to base64
                          const response = await fetch(pdfViewerUrl);
                          const pdfBlob = await response.blob();
                          const toBase64 = (blob) => new Promise((resolve, reject) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result.split(',')[1]);
                            reader.onerror = reject;
                            reader.readAsDataURL(blob);
                          });
                          const pdfBase64 = await toBase64(pdfBlob);
                          
                          // Update the quote document with the PDF data
                          await runAuthedWrite('dashboard:storePdfForClientPortal', async () => {
                            const { error } = await supabase
                              .from('quotes')
                              .update({
                                pdf_data: pdfBase64,
                                pdf_generated_at: new Date().toISOString()
                              })
                              .eq('id', selectedQuote.id);
                            if (error) throw error;
                          });
                          
                          alert('PDF has been stored in the quote document and will be available in the client portal.');
                        } catch (error) {
                          devError('Error storing PDF:', error);
                          alert(toUserMessage(error, 'Failed to store PDF. Please try again.'));
                        }
                      }}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
                    >
                      Store for Client Portal
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      setShowPDFViewer(false);
                      setPdfViewerUrl('');
                      URL.revokeObjectURL(pdfViewerUrl);
                      setSelectedClientForQuotes(null); // Ensure client details panel does not show
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* PDF Content */}
            <div className="flex-1 p-4" style={{ height: 'calc(90vh - 80px)' }}>
              <iframe
                src={pdfViewerUrl}
                className="w-full h-full border-0 rounded-lg"
                title="Quote PDF Preview"
              />
            </div>
          </div>
        </div>
      )}

      {/* Client Invoices Modal */}
      {showClientQuotes && selectedClientForQuotes && (
        <div
          className="fixed inset-0 z-[100120] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="client-invoices-modal-title"
          onClick={() => setShowClientQuotes(false)}
        >
          <div
            className="flex max-h-[min(90vh,820px)] min-h-[min(400px,70vh)] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-2xl shadow-slate-900/20 ring-1 ring-black/5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`${dashboardModalHeaderSurfaceClass} px-5 py-4 sm:px-6`}>
              <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-cyan-400/10 blur-2xl" />
              <div className="relative flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Clients</p>
                  <h3 id="client-invoices-modal-title" className="mt-0.5 text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
                    Invoices — {selectedClientForQuotes.name}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                    Review status, amounts, and open any invoice on the dashboard.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowClientQuotes(false)}
                  className={dashboardModalCloseBtn}
                  title="Close"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
              {selectedClientForQuotes.quotes && selectedClientForQuotes.quotes.length > 0 ? (
                <div className="space-y-3">
                  {selectedClientForQuotes.quotes.map((quote) => {
                    const totals = calculateQuoteTotals(quote);
                    const quoteTotal = totals.total;
                    const invAmt = getDisplayInvoiceAmount(quote);
                    const dueBadge = getInvoiceDueCountdownBadgeInfo(quote);
                    const canClickClientSent =
                      quote.status === 'draft' || quote.status === 'sent_invoice' || quote.status === 'sent_both';
                    return (
                      <div
                        key={quote.id}
                        className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm transition-all hover:border-slate-300 hover:shadow-md sm:p-5"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                              <h4 className="text-base font-semibold text-slate-900 sm:text-lg">
                                {quote.quoteNumber ? quote.quoteNumber : `Quote #${quote.id.slice(-6)}`}
                              </h4>
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                                  quote.status === 'draft'
                                    ? 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/80'
                                    : quote.status === 'sent_quote' ||
                                        quote.status === 'sent_invoice' ||
                                        quote.status === 'sent_both'
                                      ? 'bg-primary/10 text-primary ring-1 ring-primary/20'
                                      : quote.status === 'accepted'
                                        ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/80'
                                        : quote.status === 'rejected'
                                          ? 'bg-red-50 text-red-800 ring-1 ring-red-200/80'
                                          : quote.status === 'request_changes'
                                            ? 'bg-amber-50 text-amber-900 ring-1 ring-amber-200/80'
                                            : 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/80'
                                } ${canClickClientSent ? 'cursor-pointer hover:opacity-95 hover:ring-2 hover:ring-primary/35' : ''}`}
                                onClick={canClickClientSent ? () => handleInvoiceSentStatusClick(quote) : undefined}
                                title={
                                  quote.status === 'draft'
                                    ? 'Click to mark invoice as sent'
                                    : quote.status === 'sent_invoice'
                                      ? 'Click to mark invoice as not sent (Draft)'
                                      : quote.status === 'sent_both'
                                        ? 'Click to unmark invoice sent'
                                        : undefined
                                }
                                role={canClickClientSent ? 'button' : undefined}
                              >
                                {quote.status === 'request_changes'
                                  ? 'Request Changes'
                                  : quote.status === 'sent_quote'
                                    ? 'Sent Quote'
                                    : quote.status === 'sent_invoice'
                                      ? 'Sent Invoice'
                                      : quote.status === 'sent_both'
                                        ? 'Sent Quote & Invoice'
                                        : quote.status}
                              </span>
                              {quote.invoiceData && (
                                <>
                                  <span className="inline-flex items-center rounded-full border border-cyan-200/90 bg-cyan-50 px-2.5 py-0.5 text-xs font-semibold text-cyan-950">
                                    Invoice: $
                                    {invAmt != null
                                      ? invAmt.toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })
                                      : '—'}
                                  </span>
                                  {dueBadge && (
                                    <button
                                      type="button"
                                      onClick={() => handleToggleInvoicePayment(quote)}
                                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold transition hover:opacity-90 ${
                                        dueBadge.variant === 'paid'
                                          ? 'bg-emerald-100 text-emerald-800'
                                          : dueBadge.variant === 'overdue'
                                            ? 'bg-red-100 text-red-900 ring-1 ring-red-200'
                                            : dueBadge.variant === 'soon'
                                              ? 'bg-orange-100 text-orange-900 ring-1 ring-orange-200'
                                              : dueBadge.variant === 'upcoming'
                                                ? 'bg-slate-100 text-slate-700 ring-1 ring-slate-200'
                                                : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200'
                                      }`}
                                      title={
                                        dueBadge.variant === 'paid'
                                          ? 'Click to mark as awaiting payment (show due date again)'
                                          : 'Click to mark as paid'
                                      }
                                    >
                                      {dueBadge.label}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                            <div className="grid grid-cols-1 gap-2 text-sm text-slate-600 sm:grid-cols-2 sm:gap-x-6">
                              <div className="flex flex-wrap items-baseline gap-x-1.5">
                                <span className="font-medium text-slate-500">Total</span>
                                <span className="font-semibold text-slate-900">
                                  $
                                  {quoteTotal.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-baseline gap-x-1.5">
                                <span className="font-medium text-slate-500">Last edited</span>
                                <span>
                                  {quote.editedOn ? new Date(quote.editedOn).toLocaleString() : '—'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="shrink-0 sm:ml-4 sm:pt-0.5">
                            <button
                              type="button"
                              onClick={() => handleGoToQuote(quote)}
                              className={`${dashboardBtnPrimary} w-full sm:w-auto`}
                            >
                              Go to invoice
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 py-14 text-center">
                  <p className="font-medium text-slate-700">No invoices for this client yet</p>
                  <p className="mt-1 text-sm text-slate-500">Create a quote or invoice from the main list to see it here.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Request Changes Modal */}
      {showRequestChangesModal && selectedQuoteForChanges && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100120] p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Request Changes - Quote #{selectedQuoteForChanges.id.slice(-6)}
              </h3>
              <button 
                onClick={() => {
                  setShowRequestChangesModal(false);
                  setSelectedQuoteForChanges(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Client Feedback:
                </label>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {selectedQuoteForChanges.clientFeedback || 'No feedback provided.'}
                  </p>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                <p><strong>Status:</strong> Request Changes</p>
                <p><strong>Requested on:</strong> {selectedQuoteForChanges.updated_at ? new Date(selectedQuoteForChanges.updated_at).toLocaleString() : (selectedQuoteForChanges.updatedAt ? new Date(selectedQuoteForChanges.updatedAt).toLocaleString() : 'N/A')}</p>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowRequestChangesModal(false);
                    setSelectedQuoteForChanges(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    // Edit the quote to make changes
                    editQuote(selectedQuoteForChanges);
                    setShowRequestChangesModal(false);
                    setSelectedQuoteForChanges(null);
                  }}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom Quote Template Modal */}
      {showCustomQuoteModal && selectedQuoteForCustomTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100120] p-4">
          <div className="bg-white rounded-2xl w-full max-w-7xl max-h-[95vh] shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b-2 bg-gradient-to-r from-primary to-primary-dark rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  {/* Removed icon */}
                  <h3 className="text-xl font-bold text-white">Custom Quote Template</h3>
                </div>
                <button 
                  onClick={() => {
                    setShowCustomQuoteModal(false);
                    setSelectedQuoteForCustomTemplate(null);
                  }}
                  className="text-white hover:text-blue-100 transition-colors"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex h-[calc(95vh-140px)]">
              {/* Left Panel - Form */}
              <div className="w-1/2 p-6 overflow-y-auto border-r border-gray-200">
                <div className="space-y-6">

                  {/* Company Information */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Company Information
                    </h4>
                    
                    {/* Logo Upload */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">Company Logo</label>
                      <div className="flex items-center gap-3">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                        />
                        <label
                          htmlFor="logo-upload"
                          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Upload Logo
                        </label>
                        {customQuoteData.companyLogo && (
                          <div className="w-12 h-12 rounded-lg border border-gray-200 overflow-hidden">
                            <img src={customQuoteData.companyLogo} alt="Company Logo" className="w-full h-full object-cover" />
                          </div>
                        )}
                      </div>
                      {/* Logo size slider */}
                      <div className="flex items-center gap-3 mt-2">
                        <label htmlFor="logo-size" className="text-sm text-gray-700">Logo Size</label>
                        <input
                          id="logo-size"
                          type="range"
                          min={30}
                          max={150}
                          value={customQuoteData.logoSize}
                          onChange={e => handleCustomQuoteChange('logoSize', Number(e.target.value))}
                          className="w-32"
                        />
                        <span className="text-xs text-gray-500">{customQuoteData.logoSize}px</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                              <input
                          type="text"
                          value={customQuoteData.companyName}
                          onChange={(e) => handleCustomQuoteChange('companyName', e.target.value)}
                          placeholder="Company Name"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        />
                      <input
                        type="text"
                        value={customQuoteData.companyPhone}
                        onChange={(e) => handleCustomQuoteChange('companyPhone', formatPhoneNumber(e.target.value))}
                        placeholder="(555) 555-5555"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={customQuoteData.streetAddress || ''}
                        onChange={(e) => handleCustomQuoteChange('streetAddress', e.target.value)}
                        placeholder="123 Main Street"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={customQuoteData.city || ''}
                          onChange={(e) => handleCustomQuoteChange('city', e.target.value)}
                          placeholder="Toronto"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={customQuoteData.province || ''}
                          onChange={(e) => handleCustomQuoteChange('province', e.target.value)}
                          placeholder="ON"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={customQuoteData.postalCode || ''}
                          onChange={(e) => handleCustomQuoteChange('postalCode', e.target.value)}
                          placeholder="M5V 3A8"
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                      <input
                        type="email"
                        value={customQuoteData.companyEmail}
                        onChange={(e) => handleCustomQuoteChange('companyEmail', e.target.value)}
                        placeholder="Company Email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                  </div>

                  {/* Quote Details */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Quote Details
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        value={customQuoteData.quoteNumber}
                        onChange={(e) => handleCustomQuoteChange('quoteNumber', e.target.value)}
                        placeholder="Enter quote number"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                      <input
                        type="text"
                        value={customQuoteData.quoteDate}
                        onChange={(e) => handleCustomQuoteChange('quoteDate', e.target.value)}
                        placeholder="Enter date (e.g., 2025-07-22)"
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                      />
                    </div>
                      <input
                        type="text"
                        value={customQuoteData.clientName}
                        onChange={(e) => handleCustomQuoteChange('clientName', e.target.value)}
                        placeholder="Client Name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>

                  {/* Description */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Description
                      </h4>
                      <button
                        onClick={addScopeSection}
                        className="flex items-center gap-1 text-primary hover:text-primary-dark text-sm font-medium bg-blue-50 px-3 py-1 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Heading
                      </button>
                    </div>
                    
                    {customQuoteData.scopeOfWork.map((section, sectionIndex) => (
                      <div key={section.id} className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={section.heading}
                            onChange={(e) => updateScopeSection(section.id, 'heading', e.target.value)}
                            placeholder="Section Heading"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                          />
                          {customQuoteData.scopeOfWork.length > 1 && (
                            <button
                              onClick={() => removeScopeSection(section.id)}
                              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          {section.points.map((point, pointIndex) => (
                            <div key={point.id} className="flex items-center gap-2">
                              <span className="text-gray-500 text-sm">•</span>
                              <input
                                type="text"
                                value={point.text}
                                onChange={(e) => updateScopePoint(section.id, point.id, e.target.value)}
                                placeholder="Work item description"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                              />
                              {section.points.length > 1 && (
                                <button
                                  onClick={() => removeScopePoint(section.id, point.id)}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            onClick={() => addScopePoint(section.id)}
                            className="flex items-center gap-1 text-primary hover:text-primary-dark text-sm bg-white px-3 py-1 rounded-lg border border-gray-200 hover:border-primary transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Add Point
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Additional Options */}
                  <div className="space-y-4">
                    <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Display Options
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <label className="flex items-center gap-2" title="Show or hide the client signature line on the quote.">
                        <input
                          type="checkbox"
                          checked={customQuoteData.showSignature}
                          onChange={(e) => handleCustomQuoteChange('showSignature', e.target.checked)}
                          className="rounded text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-700">Client Signature</span>
                      </label>
                    </div>
                  </div>

                  {/* Notes Section with Header and Bullet Points (like invoice) */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-lg font-semibold text-primary flex items-center gap-2">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Notes (Optional)
                      </h4>
                      <button
                        type="button"
                        onClick={() => setCustomQuoteData(prev => ({ ...prev, showNotesSection: !prev.showNotesSection }))}
                        className="flex items-center gap-1 text-primary hover:text-primary-dark text-sm font-medium bg-blue-50 px-3 py-1 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Add Note
                      </button>
                    </div>
                    
                    {customQuoteData.showNotesSection && (
                      <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-gray-50">
                        {/* Header Title */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Header Title</label>
                          <input
                            type="text"
                            value={customQuoteData.notesHeaderTitle}
                            onChange={(e) => setCustomQuoteData(prev => ({ ...prev, notesHeaderTitle: e.target.value }))}
                            placeholder="e.g., Additional Notes, Terms & Conditions, etc."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                          />
                        </div>
                        
                        {/* Bullet Points */}
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Bullet Points</label>
                          <div className="space-y-2">
                            {customQuoteData.notesBulletPoints.map((point, index) => (
                              <div key={point.id} className="flex items-center gap-2">
                                <span className="text-gray-500 text-sm">•</span>
                                <input
                                  type="text"
                                  value={point.text}
                                  onChange={(e) => {
                                    const newPoints = [...customQuoteData.notesBulletPoints];
                                    newPoints[index].text = e.target.value;
                                    setCustomQuoteData(prev => ({ ...prev, notesBulletPoints: newPoints }));
                                  }}
                                  placeholder="Enter note"
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                                />
                                {customQuoteData.notesBulletPoints.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setCustomQuoteData(prev => ({
                                        ...prev,
                                        notesBulletPoints: prev.notesBulletPoints.filter((_, i) => i !== index)
                                      }));
                                    }}
                                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() => {
                                setCustomQuoteData(prev => ({
                                  ...prev,
                                  notesBulletPoints: [...prev.notesBulletPoints, { id: Date.now() + Math.random(), text: '' }]
                                }));
                              }}
                              className="flex items-center gap-1 text-primary hover:text-primary-dark text-sm font-medium bg-white px-3 py-1 rounded-lg border border-gray-200 hover:border-primary transition-colors"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              Add Point
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                    

                </div>
              </div>

              {/* Right Panel - Preview */}
              <div className="w-1/2 p-6 bg-gray-50 overflow-y-auto">
                <div className="sticky top-0 bg-gray-50 pb-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Live Preview
                  </h4>
                </div>
                
                {/* Preview Content */}
                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <div className="space-y-3">
                    {/* Company Header */}
                    <div className="border-b border-gray-200 pb-3">
                      <div className="flex flex-col items-start">
                        {customQuoteData.companyLogo && customQuoteData.showLogo && (
                          <div className="mb-2">
                            <img
                              src={customQuoteData.companyLogo}
                              alt="Company Logo"
                              style={{
                                width: customQuoteData.logoSize,
                                height: customQuoteData.logoSize,
                                objectFit: 'contain'
                              }}
                            />
                          </div>
                        )}
                        <div>
                          <h1 className="text-base font-bold text-black mb-1">
                            {customQuoteData.companyName || 'Your Company Name'}
                          </h1>
                          {customQuoteData.streetAddress && (
                            <>
                              <p className="text-black text-xs mb-0.5">{customQuoteData.streetAddress}</p>
                              {customQuoteData.city && customQuoteData.province && customQuoteData.postalCode && (
                                <p className="text-black text-xs mb-0.5">{customQuoteData.city}, {customQuoteData.province} {customQuoteData.postalCode}</p>
                              )}
                            </>
                          )}
                          {customQuoteData.companyPhone && (
                            <p className="text-black text-xs mb-0.5">{customQuoteData.companyPhone}</p>
                          )}
                          {customQuoteData.companyEmail && (
                            <p className="text-black text-xs mb-0.5">{customQuoteData.companyEmail}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-start mt-3">
                        <div>
                          <p className="text-black text-xs">{customQuoteData.quoteDate || 'N/A'}</p>
                          <p className="text-black text-xs">ISSUED TO: {customQuoteData.clientName || 'Client Name'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-black text-sm font-bold">{customQuoteData.quoteNumber || 'N/A'}</p>
                        </div>
                      </div>
                      
                      <div className="border-b border-black mt-3"></div>
                    </div>

                    {/* Description/TOTAL Box */}
                    <div style={{ marginBottom: '5px', border: '1px solid #000', padding: '6px', width: '100%' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ color: '#000', margin: 0, fontSize: '12px', fontWeight: 'bold', marginTop: '-4px', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>DESCRIPTION</p>
                        </div>
                        <div style={{ flex: 0, flexBasis: 'auto', textAlign: 'right' }}>
                          <p style={{ color: '#000', margin: 0, fontSize: '12px', fontWeight: 'bold', marginTop: '-4px', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>TOTAL</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Scope of Work Content */}
                    <div style={{ marginBottom: '5px' }}>
                      {customQuoteData.scopeOfWork.map((section, index) => (
                        <div key={section.id} className="mb-2">
                          {section.heading && (
                            <p style={{ color: '#000', margin: '0 0 5px 0', fontWeight: 'bold', fontSize: '12px' }}>{section.heading}</p>
                          )}
                          <ul style={{ margin: 0, paddingLeft: 16, listStyleType: 'disc' }}>
                            {section.points.map((point) => (
                              <li key={point.id} style={{ color: '#000', marginBottom: 2, fontSize: 11, lineHeight: 2.2 }}>{point.text || 'Work item description'}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Cost Breakdown Preview */}
                    <div className="border-t border-gray-200 pt-3">
                      <div style={{ marginBottom: '2px', color: '#000' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: 0, color: '#000' }}>
                          {customQuoteData.showBreakdown && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', fontSize: '11px', marginBottom: 0 }}>
                              <span style={{ fontWeight: 'bold', color: '#000' }}>Materials:</span>
                              <span style={{ marginLeft: '8px', color: '#000' }}>$0.00</span>
                        </div>
                          )}
                          {customQuoteData.showBreakdown && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', fontSize: '11px', marginBottom: 0 }}>
                              <span style={{ fontWeight: 'bold', color: '#000' }}>Labour:</span>
                              <span style={{ marginLeft: '8px', color: '#000' }}>$0.00</span>
                        </div>
                          )}
                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', fontSize: '11px', marginBottom: 0 }}>
                            <span style={{ fontWeight: 'bold', color: '#000' }}>Subtotal:</span>
                            <span style={{ marginLeft: '8px', color: '#000' }}>$0.00</span>
                        </div>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', fontSize: '11px', marginBottom: '5px' }}>
                            <span style={{ fontWeight: 'bold', color: '#000' }}>Tax:</span>
                            <span style={{ marginLeft: '8px', color: '#000' }}>$0.00</span>
                        </div>
                          <div style={{ marginBottom: '15px', border: '1px solid #000', padding: '6px', width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ flex: 1 }}>
                              </div>
                              <div style={{ flex: 0, flexBasis: 'auto', textAlign: 'right' }}>
                                <p style={{ color: '#000', margin: 0, fontSize: '11px', fontWeight: 'bold', marginTop: '-4px', fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>Total: $0.00</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Notes & Terms Preview */}
                    {customQuoteData.showNotesSection && customQuoteData.notesBulletPoints && customQuoteData.notesBulletPoints.length > 0 && customQuoteData.notesBulletPoints.some(point => point.text && point.text.trim()) && (
                      <div className="border-t border-gray-200 pt-3">
                        <p className="font-semibold text-black text-xs mb-1">{customQuoteData.notesHeaderTitle || 'Notes'}</p>
                        <ul style={{ listStyleType: 'disc', margin: 0, paddingLeft: 16 }}>
                          {customQuoteData.notesBulletPoints.filter(point => point.text && point.text.trim()).map((point, idx) => (
                            <li key={point.id} style={{ fontSize: 11, color: '#000', lineHeight: 2.2, marginBottom: 2 }}>{point.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Fallback to old notes if new notes section is not enabled */}
                    {!customQuoteData.showNotesSection && notes.length > 0 && notes.some(note => note.text && note.text.trim()) && (
                      <div className="border-t border-gray-200 pt-3">
                        <p className="font-semibold text-black text-xs mb-1">Notes:</p>
                        <ul style={{ listStyleType: 'disc', margin: 0, paddingLeft: 16 }}>
                          {notes.filter(note => note.text && note.text.trim()).map((note, idx) => (
                            <li key={note.id} style={{ fontSize: 11, color: '#000', lineHeight: 2.2, marginBottom: 2 }}>{note.text}</li>
                          ))}
                        </ul>
                      </div>
                    )}



                    {customQuoteData.showSignature && (
                      <div className="border-t border-gray-200 pt-3 mt-6">
                        <div className="flex justify-between items-end">
                          <div className="w-5/12">
                            <div className="border-b border-black h-10 mb-1"></div>
                            <p className="text-black text-xs text-center">Client Signature</p>
                          </div>
                          <div className="w-5/12">
                            <div className="border-b border-black h-10 mb-1"></div>
                            <p className="text-black text-xs text-center">Date</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t-2 bg-gray-50 rounded-b-2xl flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowCustomQuoteModal(false);
                  setSelectedQuoteForCustomTemplate(null);
                }}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateCustomPDF}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary-dark transition-all font-semibold shadow-lg hover:shadow-xl"
              >
                Generate PDF
               </button>
            </div>
          </div>
        </div>
              )}
        {(showCreateQuote || showEditQuote) && (
          <QuoteFormModal
            quoteForm={quoteForm}
            setQuoteForm={setQuoteForm}
            showCreateQuote={showCreateQuote}
            showEditQuote={showEditQuote}
            setShowCreateQuote={setShowCreateQuote}
            setShowEditQuote={setShowEditQuote}
            setShowReviewQuote={setShowReviewQuote}
            saveQuote={saveQuote}
            clients={clients}
            materialSearch={materialSearch}
            setMaterialSearch={setMaterialSearch}
            materialResults={materialResults}
            setMaterialResults={setMaterialResults}
            materialQty={materialQty}
            setMaterialQty={setMaterialQty}
            materialPrice={materialPrice}
            setMaterialPrice={setMaterialPrice}
            materialSearchLoading={materialSearchLoading}
            materialHistory={materialHistory}
            setShowCustomMaterialsList={setShowCustomMaterialsList}
            debouncedSearch={debouncedSearch}
            saveMaterialToHistory={saveMaterialToHistory}
            setShowCustomMaterialModal={setShowCustomMaterialModal}
            materialsSubtotal={materialsSubtotal}
            markupAmount={markupAmount}
            total={total}
            handleQuoteFormChange={handleQuoteFormChange}
            resetQuoteForm={resetQuoteForm}
            searchMaterials={searchMaterials}
            locationCountry={locationCountry}
            setLocationCountry={handleLocationCountryChange}
            locationRegion={locationRegion}
            setLocationRegion={handleLocationRegionChange}
            locationCity={locationCity}
            setLocationCity={handleLocationCityChange}
            locationRegions={locationRegions}
            hasSearched={hasSearched}
            setHasSearched={setHasSearched}
            canSearchMaterials={canUseMaterialSearch}
          />
        )}

        {/* View Expenses Modal — portaled so dimmer covers full viewport above Navbar */}
      {showViewExpenses &&
        createPortal(
        <div className="fixed inset-0 z-[100000] flex min-h-[100dvh] flex-col items-stretch justify-center bg-slate-950/75 backdrop-blur-[3px] px-2 pt-[max(12px,env(safe-area-inset-top))] pb-[max(12px,env(safe-area-inset-bottom))] sm:px-4 sm:pt-4 sm:pb-4">
          <div className="flex h-full min-h-0 w-full max-w-none flex-col overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_25px_80px_-12px_rgba(15,23,42,0.4)] ring-1 ring-black/10 sm:max-h-[calc(100dvh-2rem)]">
            {/* Header */}
            <div className="shrink-0 px-4 py-4 border-b-2 bg-gray-50 sm:px-6 sm:rounded-t-2xl">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {currentProjectFilter ? `View Expenses - ${projects.find(p => p.id === currentProjectFilter)?.name || 'Unknown Project'}` : 'View All Expenses'}
                  </h3>
                  <div className="flex items-center space-x-6 mt-2">
                    <div className="bg-blue-100 px-4 py-2 rounded-lg">
                      <span className="text-sm font-medium text-blue-800">Total Expenses:</span>
                      <span className="text-lg font-bold text-blue-900 ml-2">
                        ${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="bg-green-100 px-4 py-2 rounded-lg">
                      <span className="text-sm font-medium text-green-800">Total Count:</span>
                      <span className="text-lg font-bold text-green-900 ml-2">{filteredExpenses.length}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setEditingExpense(null);
                      setExpenseForm({
                        title: '',
                        amount: '',
                        category: '',
                        date: new Date().toISOString().split('T')[0],
                        description: '',
                        projectId: currentProjectFilter || '', // Preserve the current project context
                        receipts: [],
                        laborOrigin: 'internal'
                      });
                      setShowAddExpense(true);
                      // Keep view expenses modal open
                    }}
                    className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    Add New
                  </button>
                  <button 
                    onClick={() => {
                      setShowViewExpenses(false);
                      setExpenseSearchTerm('');
                      setExpenseCategoryFilter('all');
                      setCurrentProjectFilter(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="p-6 border-b">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search Expenses</label>
                  <input
                    type="text"
                    placeholder="Search by title or description..."
                    value={expenseSearchTerm}
                    onChange={(e) => setExpenseSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
                  <select
                    value={expenseCategoryFilter}
                    onChange={(e) => setExpenseCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="all">All Categories</option>
                    <option value="materials">Materials</option>
                    <option value="labour">Labour</option>
                    <option value="equipment">Equipment</option>
                    <option value="transportation">Transportation</option>
                    <option value="utilities">Utilities</option>
                    <option value="permits">Permits</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Expenses List */}
            <div className="flex-1 overflow-y-auto">
              <div className="divide-y divide-gray-200">
                {filteredExpenses.length > 0 ? (
                  filteredExpenses.map(expense => (
                    <div key={expense.id} className="p-4 hover:bg-gray-50 transition-colors rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-base font-semibold text-gray-900 truncate">{expense.title}</h4>
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                              {expense.category === 'labor' || expense.category === 'labour' ? 'labour' : expense.category}
                            </span>
                            {expense.isAutomatic && (
                              <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 rounded-full flex-shrink-0 flex items-center space-x-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                <span>Auto</span>
                              </span>
                            )}
                            {(expense.receipts && expense.receipts.length > 0) || expense.receipt ? (
                              <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded-full flex-shrink-0 flex items-center space-x-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>
                                  {expense.receipts && expense.receipts.length > 0 
                                    ? `${expense.receipts.length} Receipt${expense.receipts.length > 1 ? 's' : ''}`
                                    : 'Receipt'
                                  }
                                </span>
                              </span>
                            ) : null}
                          </div>
                          {expense.description && (
                            <p className="text-sm text-gray-600 truncate mb-1">{expense.description}</p>
                          )}
                          <div className="flex items-center space-x-3 text-xs text-gray-500">
                            <span>{new Date(expense.date).toLocaleDateString()}</span>
                            {expense.projectId && (
                              <span>• {projects.find(p => p.id === expense.projectId)?.name || 'Unknown'}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                          <span className="text-lg font-bold text-gray-900">
                            ${expense.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          <div className="flex space-x-1">
                            {(expense.receipts && expense.receipts.length > 0) || expense.receipt ? (
                              <button
                                onClick={() => {
                                  const receipts = expense.receipts || (expense.receipt ? [expense.receipt] : []);
                                  setSelectedReceipt(receipts[0]);
                                  setSelectedReceiptIndex(0);
                                  setCurrentReceiptsArray(receipts);
                                  setShowReceiptModal(true);
                                }}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title={`View Receipt${expense.receipts && expense.receipts.length > 1 ? 's' : ''}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                              </button>
                            ) : null}
                            <button
                              onClick={() => {
                                editExpense(expense);
                                // Keep view expenses modal open
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => deleteExpense(expense.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-12 text-center">
                    <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No expenses found</h3>
                    <p className="text-gray-500 mb-4">Get started by adding your first expense</p>
                    <button
                      onClick={() => {
                        setEditingExpense(null);
                        setExpenseForm({
                          title: '',
                          amount: '',
                          category: '',
                          date: new Date().toISOString().split('T')[0],
                          description: '',
                          projectId: currentProjectFilter || '', // Preserve the current project context
                          receipts: [],
                          laborOrigin: 'internal'
                        });
                        setShowAddExpense(true);
                        // Keep view expenses modal open
                      }}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                      Add First Expense
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Add/Edit Expense Modal */}
      {showAddExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100120] p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl relative max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b-2 bg-gray-50 rounded-t-2xl flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingExpense ? 'Edit Expense' : 'Add New Expense'}
                </h3>
                <button 
                  onClick={() => {
                    setShowAddExpense(false);
                    setEditingExpense(null);
                    setExpenseForm({
                      title: '',
                      amount: '',
                      category: '',
                      date: new Date().toISOString().split('T')[0],
                      description: '',
                      projectId: '',
                      receipts: [],
                      laborOrigin: 'internal'
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    value={expenseForm.title}
                    onChange={(e) => handleExpenseFormChange('title', e.target.value)}
                    placeholder="Expense title"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(e) => handleExpenseFormChange('amount', e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
                  <select
                    value={expenseForm.category}
                    onChange={(e) => handleExpenseFormChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select a category</option>
                    <option value="materials">Materials</option>
                    <option value="labour">Labour</option>
                    <option value="equipment">Equipment</option>
                    <option value="transportation">Transportation</option>
                    <option value="utilities">Utilities</option>
                    <option value="permits">Permits</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                  <input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => handleExpenseFormChange('date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {expenseForm.category === 'labour' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Labour type</label>
                  <select
                    value={expenseForm.laborOrigin || 'internal'}
                    onChange={(e) => handleExpenseFormChange('laborOrigin', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="internal">Internal team (your crew)</option>
                    <option value="subcontractor">Subcontractor / external partner</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Used for project profit breakdowns. Subcontractors can also be listed under Teams.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={expenseForm.description}
                  onChange={(e) => handleExpenseFormChange('description', e.target.value)}
                  placeholder="Additional details about this expense..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Receipts (Optional)</label>
                <p className="text-xs text-gray-500 mb-2">Upload up to 5 image files (JPEG, PNG, GIF). Max size: 10MB each. Large images will be automatically compressed for optimal performance.</p>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleReceiptUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {(expenseForm.receipts || []).length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-600 mb-2">
                      {(expenseForm.receipts || []).length} receipt{(expenseForm.receipts || []).length > 1 ? 's' : ''} uploaded
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(expenseForm.receipts || []).map((receipt, index) => (
                        <div key={index} className="relative inline-block">
                          <img 
                            src={receipt} 
                            alt={`Receipt ${index + 1}`} 
                            className="w-24 h-24 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => {
                              setSelectedReceipt(receipt);
                              setSelectedReceiptIndex(index);
                              setCurrentReceiptsArray(expenseForm.receipts || []);
                              setShowReceiptModal(true);
                            }}
                            title={`Click to view receipt ${index + 1} full size`}
                          />
                          <button
                            onClick={() => removeReceipt(index)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                            title={`Remove receipt ${index + 1}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          <div className="absolute bottom-1 left-1 bg-black bg-opacity-50 text-white text-xs px-1 rounded">
                            {index + 1}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Click image to view full size • Click X to remove</p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer - Fixed at bottom */}
            <div className="px-6 py-4 border-t-2 bg-gray-50 rounded-b-2xl flex gap-3 justify-end flex-shrink-0">
              <button
                onClick={() => {
                  setShowAddExpense(false);
                  setEditingExpense(null);
                  setExpenseForm({
                    title: '',
                    amount: '',
                    category: '',
                    date: new Date().toISOString().split('T')[0],
                    description: '',
                    projectId: '',
                    receipts: [],
                    laborOrigin: 'internal'
                  });
                }}
                className="bg-gray-200 text-gray-800 px-6 py-2 rounded-lg hover:bg-gray-300 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  devLog('Add/Update Expense button clicked');
                  devLog('Form data:', expenseForm);
                  devLog('Required fields check:', {
                    title: !!expenseForm.title,
                    amount: !!expenseForm.amount,
                    category: !!expenseForm.category,
                    date: !!expenseForm.date
                  });
                  
                  if (!expenseForm.title || !expenseForm.amount || !expenseForm.category || !expenseForm.date) {
                    alert('Please fill in all required fields (Title, Amount, Category, Date)');
                    return;
                  }
                  
                  try {
                    saveExpense({
                      ...expenseForm,
                      id: editingExpense?.id
                    });
                  } catch (error) {
                    devError('Error saving expense:', error);
                    alert('Error saving expense. Please try again.');
                  }
                }}
                disabled={!expenseForm.title || !expenseForm.amount || !expenseForm.category || !expenseForm.date}
                className={`px-6 py-2 rounded-lg transition-all font-semibold shadow-lg ${
                  !expenseForm.title || !expenseForm.amount || !expenseForm.category || !expenseForm.date
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-dark hover:shadow-xl'
                }`}
                style={{ pointerEvents: 'auto' }}
              >
                {editingExpense ? 'Update Expense' : 'Add Expense'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setEmailQuoteData(null);
          setSelectedClientForQuotes(null); // Ensure client details panel does not show
        }}
        quoteData={emailQuoteData} // Now pass the full quote object
        userEmail={user?.email}
        clients={clients}
        settings={settings}
        userPlan={userPlan}
        onQuoteUpdate={async (updatedQuote) => {
          devLog('🔄 onQuoteUpdate called with:', updatedQuote);
          
          // Update the quote in the local state
          setQuotes(prev => {
            const newQuotes = prev.map(q => 
              q.id === updatedQuote.id ? updatedQuote : q
            );
            devLog('📝 Updated local quotes state:', newQuotes.find(q => q.id === updatedQuote.id));
            return newQuotes;
          });
          
          try {
            const updateData = {
              status: updatedQuote.status,
              quote_sent: updatedQuote.quoteSent || updatedQuote.quote_sent || false
            };
              
            if (updatedQuote.quoteSentAt) {
              updateData.quote_sent_at = updatedQuote.quoteSentAt;
            }

            if (updatedQuote.invoiceSent !== undefined || updatedQuote.invoice_sent !== undefined) {
              updateData.invoice_sent = updatedQuote.invoiceSent ?? updatedQuote.invoice_sent;
            }

            if (updatedQuote.invoiceSentAt) {
              updateData.invoice_sent_at = updatedQuote.invoiceSentAt;
            }
              
            devLog('🔧 Final update data:', updateData);

            await runAuthedWrite('dashboard:quoteRowStatusUpdate', async () => {
              const { error: quoteUpdErr } = await supabase
                .from('quotes')
                .update(updateData)
                .eq('id', updatedQuote.id);
              if (quoteUpdErr) throw quoteUpdErr;
            });

            devLog('✅ Successfully saved to Supabase');
          } catch (error) {
            devError('❌ Error updating quote status in Supabase:', error);
          }
        }}
      />

      {/* Receipt Modal */}
      {showReceiptModal && selectedReceipt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100120] p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl relative max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b-2 bg-gray-50 rounded-t-2xl flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">
                  View Receipt {selectedReceiptIndex !== undefined && currentReceiptsArray.length > 0 ? `(${selectedReceiptIndex + 1} of ${currentReceiptsArray.length})` : ''}
                </h3>
                <button 
                  onClick={() => {
                    setShowReceiptModal(false);
                    setSelectedReceipt(null);
                    setSelectedReceiptIndex(0);
                    setCurrentReceiptsArray([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <img 
                src={selectedReceipt} 
                alt="Receipt" 
                className="w-full max-h-[60vh] object-contain rounded-lg border"
              />
              
              {/* Navigation buttons for multiple receipts */}
              {currentReceiptsArray.length > 1 && (
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => {
                      const prevIndex = selectedReceiptIndex > 0 ? selectedReceiptIndex - 1 : currentReceiptsArray.length - 1;
                      setSelectedReceipt(currentReceiptsArray[prevIndex]);
                      setSelectedReceiptIndex(prevIndex);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                    <span>Previous</span>
                  </button>
                  
                  <span className="px-4 py-2 text-gray-600">
                    {selectedReceiptIndex + 1} of {currentReceiptsArray.length}
                  </span>
                  
                  <button
                    onClick={() => {
                      const nextIndex = selectedReceiptIndex < currentReceiptsArray.length - 1 ? selectedReceiptIndex + 1 : 0;
                      setSelectedReceipt(currentReceiptsArray[nextIndex]);
                      setSelectedReceiptIndex(nextIndex);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
                  >
                    <span>Next</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
              
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedReceipt;
                    link.download = 'receipt.png';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download Receipt</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Professional Receipt Limit Notification Modal */}
      {showReceiptLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative">
            {/* Header */}
            <div className="px-6 py-4 border-b-2 bg-red-50 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Upload Limit Reached</h3>
                  <p className="text-sm text-gray-600">Please review the details below</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-gray-700 leading-relaxed">
                {receiptLimitMessage}
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t-2 bg-gray-50 rounded-b-2xl flex justify-end">
              <button
                onClick={() => {
                  setShowReceiptLimitModal(false);
                  setReceiptLimitMessage('');
                }}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl relative">
            {/* Header */}
            <div className="px-6 py-4 border-b-2 bg-primary/10 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                  <p className="text-sm text-gray-600">Enter your new password twice to confirm</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4 space-y-4">
              {passwordError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm">{passwordError}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => handlePasswordFormChange('newPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter your new password"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Retype new password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => handlePasswordFormChange('confirmPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Retype new password"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t-2 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setPasswordForm({
                    newPassword: '',
                    confirmPassword: ''
                  });
                  setPasswordError('');
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors font-medium"
                disabled={isChangingPassword}
              >
                Cancel
              </button>
              <button
                onClick={handleChangePassword}
                disabled={isChangingPassword}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isChangingPassword ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Changing...</span>
                  </>
                ) : (
                  <span>Change Password</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {user && userData && (
        <BillingPlanModal
          isOpen={planBillingModalOpen}
          onClose={closePlanBillingModal}
          userData={userData}
          onRefreshProfile={refreshProfilePlan}
          highlightCheckout={billingModalHighlightCheckout}
        />
      )}

      {/* Product Tour (Hidden) */}
      <div style={{ display: 'none' }}>
        <ProductTour 
          userPlan={userPlan} 
          currentSection={currentSection}
          onNavigateToSection={navigateToSection}
          onTourComplete={() => {
            devLog('Tour completed');
          }}
        />
      </div>
    </div>
  );
};

export default Dashboard; 

